import { Component, ElementRef, Input, OnDestroy, OnInit, AfterViewInit, ViewChild, HostListener, ChangeDetectorRef, NgZone, ChangeDetectionStrategy, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { Subject, takeUntil, debounceTime, distinctUntilChanged, switchMap, combineLatest, forkJoin, of, from, filter, first } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import html2canvas from 'html2canvas';
import { TradingViewChartService } from './trading-view-chart.service';
import { MarketDataService } from './services/market-data.service';
import { StorageService } from './services/storage.service';
import { WatchlistService } from './services/watchlist.service';
import { IndicatorService } from './services/indicator.service';
import { ChartTypeService } from './services/chart-type.service';
import { ChartOptions, HorizontalLine, SystemLine, TooltipData, MarkerTooltipData, ATRTooltipData, SeriesType, RecentTicker, TickerDetails, TextAnnotation, PenDrawing, Measurement, ArrowDrawing, Watchlist, WatchlistItem, SidebarTab, Indicator, IndicatorType, CrosshairMode, ChartType, ChartIndicator, ChartSettings, ChartTimeRange, ChartInterval, ChartDrawingApi, IndicatorSettings } from './chart.interface';
import { SocketConnectionService } from './services/socket-connection.service';
import { ChartDrawingsService } from './services/chart-drawings.service';
import { SocketDataService } from './services/socket-data.service';
import moment from 'moment-timezone';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-chart',
  standalone: true,
  imports: [CommonModule, FormsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChartComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() options: ChartOptions = {
    symbol: '',
    height: 500,
    width: 800,
    showVolume: true,
    showSMA: true
  };

  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;

  // Loading state management
  public isLoading: boolean = true;
  private dataReceivedForCurrentRequest: boolean = false; // Flag to track if we've received data for the current request
  private historicalRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private historicalRetryCount = 0;
  private readonly MAX_HISTORICAL_RETRIES = 4;
  private loadingTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingSnapshotPayload: any = null;
  private applyingIndicatorsFromUrl = false;
  private lastAppliedIndicatorsParam = '';
  // Flag to ensure indicators are re-synced once per socket load, after first render
  private indicatorsSyncedForCurrentRequest: boolean = false;
  // Track which symbol/range was last requested so stale socket data can be rejected
  private pendingSymbol: string = '';
  private pendingRange: string = '';
  // Previous symbol/interval for correct unsubscribe when switching
  private previousSymbol: string = '';
  private previousInterval: string = '';
  private previousRange: string = '';
  // Track the active socket subscription target explicitly.
  private activeSocketSymbol: string = '';
  private activeSocketInterval: string = '';
  private activeSocketRange: string = '';
  // Defers initial data request until chart settings have been loaded from API
  private settingsLoaded = false;
  private settingsReadyFailsafeTimer: ReturnType<typeof setTimeout> | null = null;
  private wasEverConnected = false;
  private destroy$ = new Subject<void>();
  private resizeObserver?: ResizeObserver;
  private documentClickListener?: (event: Event) => void;
  private resizeTimeout?: any;
  private lastResizeTime = 0;
  private popupClosePoll?: number;

  // Chart drawings: per-timeframe; clear on range/interval change, load from API or socket
  private drawingsFetchPending = false;
  private drawingsReceivedFromSocket = false;
  private isLoadingDrawingsFromApi = false;
  private saveDrawingsTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly SAVE_DRAWINGS_DEBOUNCE_MS = 1500;
  private historyPageRequestInFlight = false;
  private historyPaginationExhausted = false;
  private historyRequestTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastHistoryRequestTo: number | null = null;
  private lastVisibleLogicalFrom: number | null = null;
  private nextHistoryToCursor: number | null = null;
  private lastHistoryRequestAtMs = 0;
  /** True while visible range is inside left-edge zone; next fetch only on re-entry (edge trigger). */
  private historyPrevLeftEdgeInZone = false;
  // Pagination should only start after real user interaction (scroll/pan) to avoid
  // triggering history fetches immediately on initial chart render.
  private userChartInteractedAtMs = 0;
  private userHasChartInteracted = false;
  private readonly HISTORY_PAGE_LIMIT = 500;
  // Start prefetching older candles before user hits the hard left edge.
  private readonly HISTORY_LEFT_EDGE_THRESHOLD_BARS = 100;
  // Minimal cooldown; next pagination should feel immediate once data arrives.
  private readonly HISTORY_REQUEST_COOLDOWN_MS = 0;
  // Proactive indicator warm-up (fetch history before visible window without user scroll).
  private warmupFetchInFlight = false;
  private warmupFetchExhausted = false;
  private warmupChainCount = 0;
  private readonly WARMUP_MAX_CHAIN_REQUESTS = 5;

  // UI State Properties
  currentSymbol: string = '';
  currentInterval: string = '1y'; // Default: trailing 1 year, properly zoomed

  showVolume: boolean = true;
  showMarkers: boolean = true;
  isDrawingMode: boolean = false;
  isTextMode: boolean = false;
  isPenMode: boolean = false;
  isMeasuringMode: boolean = false;
  isArrowMode: boolean = false;
  horizontalLines: HorizontalLine[] = [];
  private prevHorizontalLinesCount = 0;
  systemLines: SystemLine[] = [];
  textAnnotations: TextAnnotation[] = [];
  penDrawings: PenDrawing[] = [];
  measurements: Measurement[] = [];
  arrowDrawings: ArrowDrawing[] = [];
  measureColor: string = '#00ff00';

  // Pen drawing properties
  penSize: number = 2;
  penColor: string = '#2962FF';
  systemLineVisibility: Record<SystemLine['type'], boolean> = {
    SUPPORT: true,
    RESISTANCE: true
  };
  activeDropdown: string | null = null;
  showChartSettingsPopup: boolean = false;
  currentTheme: 'light' | 'dark' = 'light'; // Default to light theme

  // Screenshot functionality
  showScreenshotDropdown: boolean = false;
  tooltipData: TooltipData | null = null;
  markerTooltipData: MarkerTooltipData | null = null;
  atrTooltipData: ATRTooltipData | null = null;

  // Indicator system properties
  indicators: Indicator[] = [];
  // Dynamic indicator types from API
  availableIndicatorTypes: ChartIndicator[] = [];
  showIndicatorSettings = false;
  selectedIndicator: Indicator | null = null;
  private defaultIndicatorsInitialized = false;

  // Chart settings properties
  crosshairMode: CrosshairMode = 'normal';
  showGridLines = true;
  showPriceLine = true;

  // Indicator display settings
  maxVisibleIndicators = 3;
  showAllIndicators = false;

  // Embedded/advanced view controls
  isEmbeddedView = false;
  isFullscreenView = false;
  showToolbar = true;
  showSidebar = true;
  /** Header Insider Maxx mark. Hidden only when hideLogo=true (dedicated Hub chart page). */
  showBrandLogo = true;
  private dedicatedChartHidesLogo = false;
  showBottomToolbar = true;
  /** Avoid overlapping Mboum quote fetches for fullscreen extras */
  private fullscreenQuoteHydrateInFlight = false;
  showPopupButton = true;
  popupWidth = 1000;
  popupHeight = 650;

  // Active indicators overlay (TradingView-like compact chips panel)
  indicatorsPanelCollapsed = false;
  private hasUserToggledIndicatorsPanel = false;
  private readonly INDICATORS_PANEL_MOBILE_MAX_WIDTH = 768;
  private lastIndicatorsPanelViewportWasMobile: boolean | null = null;


  // Preset colors for indicators - chart-appropriate colors
  presetColors = [
    { name: 'Blue', value: '#2962FF' },      // Classic chart blue
    { name: 'Red', value: '#F44336' },       // True red
    { name: 'Green', value: '#4CAF50' },     // Classic chart green
    { name: 'Orange', value: '#FF9800' },    // Warm orange
    { name: 'Purple', value: '#9C27B0' },    // Classic purple
    { name: 'Teal', value: '#009688' },      // Professional teal
    { name: 'Yellow', value: '#FFC107' },    // Bright yellow
    { name: 'Pink', value: '#E91E63' },      // Vibrant pink
    { name: 'Cyan', value: '#00BCD4' },      // Bright cyan
    { name: 'Lime', value: '#8BC34A' },      // Fresh lime
    { name: 'Indigo', value: '#3F51B5' },    // Professional indigo
    { name: 'Brown', value: '#795548' }      // Muted brown
  ];

  // Ticker Details
  tickerDetails: TickerDetails | null = null;

  // Volume display
  currentVolume: number = 0;
  isVolumePanelExpanded: boolean = true;

  // Format volume for display (e.g., "49.65 M")
  formatVolume(volume: number): string {
    if (!volume || volume === 0) return '0';

    if (volume >= 1_000_000_000) {
      return (volume / 1_000_000_000).toFixed(2) + ' B';
    } else if (volume >= 1_000_000) {
      return (volume / 1_000_000).toFixed(2) + ' M';
    } else if (volume >= 1_000) {
      return (volume / 1_000).toFixed(2) + ' K';
    }
    return volume.toLocaleString();
  }

  // User ID from query params
  private userId: string | any;

  toggleVolumePanel(): void {
    this.isVolumePanelExpanded = !this.isVolumePanelExpanded;
  }

  // Search functionality
  searchResults: {
    symbol: string;
    name?: string;
    exchange?: string;
    assetType?: string;
    lastPrice?: number;
    change?: number;
    changePercent?: number;
    marketState?: string;
    isActivelyTrading?: boolean;
  }[] = [];
  showSearchResults: boolean = false;
  searchQuery: string = '';
  isSearching: boolean = false;
  private searchSubject = new Subject<string>();

  // Selected line and toolbar
  selectedLine: HorizontalLine | null = null;
  showLineToolbar = false;
  toolbarX = 0;
  toolbarY = 0;
  activeToolbarOption: string | null = null;
  availableColors = ['#2962FF', '#FF6B6B', '#26a69a', '#FFD700', '#9C27B0', '#FF9800', '#4CAF50', '#F44336'];

  // Selected text and toolbar
  selectedText: TextAnnotation | null = null;
  showTextToolbar = false;
  textToolbarX = 0;
  textToolbarY = 0;
  activeTextToolbarOption: string | null = null;
  availableFontSizes = [10, 12, 14, 16, 18, 20, 24, 28, 32];
  availableTextColors = ['#000000', '#333333', '#666666', '#999999', '#FFFFFF', '#2962FF', '#FF6B6B', '#26a69a', '#FFD700', '#9C27B0', '#FF9800', '#4CAF50', '#F44336'];
  availableBorderColors = ['#000000', '#333333', '#666666', '#999999', '#FFFFFF', '#2962FF', '#FF6B6B', '#26a69a', '#FFD700', '#9C27B0', '#FF9800', '#4CAF50', '#F44336'];

  // Selected pen drawing and toolbar
  selectedPenDrawing: PenDrawing | null = null;
  showPenToolbar = false;
  penToolbarX = 0;
  penToolbarY = 0;
  activePenToolbarOption: string | null = null;

  // Selected arrow drawing and toolbar
  selectedArrowDrawing: ArrowDrawing | null = null;
  showArrowToolbar = false;
  arrowToolbarX = 0;
  arrowToolbarY = 0;
  activeArrowToolbarOption: string | null = null;
  arrowColor: string = '#2962FF';
  arrowLineWidth: number = 2;

  // Event states for corporate events
  eventStates = {
    earnings: true,
    dividend: true,
    split: true,
    news: false,
    other: false
  };

  // Series type options - dynamic from API only
  seriesTypes: ChartType[] = [];

  currentSeriesType: SeriesType = 'candlestick';

  // Time range options - dynamic from API
  timeRanges: ChartTimeRange[] = [];
  private timeRangesLoaded = false;

  // Chart intervals - dynamic from API
  chartIntervals: ChartInterval[] = [];
  private intervalsLoaded = false;

  showIntervalDropdown = false;
  selectedInterval: string | null = '1d'; // Default to 1d candles, will be overridden by smart selection

  // Mapping of time ranges to valid candle intervals.
  // Short ranges allow intraday (small candles); long ranges allow 1D / 1W / 1M (large candles).
  private readonly TIME_RANGE_TO_VALID_INTERVALS: Record<string, string[]> = {
    '1d': ['5m', '15m', '30m'],
    '5d': ['5m', '15m', '30m', '1d'],
    '1m': ['15m', '30m', '1h', '1d'],
    '3m': ['1d', '1wk'],
    '6m': ['1d', '1wk'],
    'ytd': ['1d', '1wk'],
    '1y': ['1d', '1wk'],
    '2y': ['1d', '1wk', '1mo'],
    '5y': ['1d', '1wk', '1mo'],
    '10y': ['1wk', '1mo'],
    'all': ['1d'],
    'max': ['1d', '1wk', '1mo']
  };


  // Available intervals - dynamic from API, fallback to MarketDataService
  get availableIntervals() {
    if (this.chartIntervals.length > 0) {
      // Filter out any "Auto" or null intervals from API to avoid duplicates with hardcoded Auto
      return this.chartIntervals
        .filter(interval =>
          interval.value !== null &&
          interval.value?.toString().toLowerCase() !== 'auto' &&
          interval.label?.toLowerCase() !== 'auto'
        )
        .map(interval => ({
          value: interval.value,
          label: interval.label,
          description: interval.description,
          isDefault: interval.isDefault
        }));
    }
    // Fallback to MarketDataService if API data not loaded yet
    return this.marketDataService.getSupportedIntervals().map(interval => ({
      ...interval,
      isDefault: false
    }));
  }

  // Get current effective interval (always manual now, defaults to 1d)
  get currentEffectiveInterval(): string {
    return this.selectedInterval || '1d';
  }

  private userManuallySelectedInterval = false;

  /**
   * Get optimal candle interval for a time range (dynamic from API or fallback to static)
   */
  private getOptimalCandleInterval(timeRange: string): string {
    // First, try to get from dynamic API data
    if (this.timeRangesLoaded && this.timeRanges.length > 0) {
      const timeRangeData = this.timeRanges.find(r => r.value === timeRange);
      if (timeRangeData?.optimalInterval) {
        console.log(`[ChartComponent] Using dynamic optimal interval for ${timeRange}: ${timeRangeData.optimalInterval}`);
        return timeRangeData.optimalInterval;
      }
    }

    // Short ranges -> smaller candles (intraday). Long ranges -> 1D / 1W / 1M.
    const optimalMapping: Record<string, string> = {
      '1d': '5m',    // 1 Day    -> 5 min candles
      '5d': '15m',   // 5 Days   -> 15 min candles
      '1m': '30m',   // 1 Month  -> 30 min candles
      '3m': '1d',    // 3 Months -> 1 day candles
      '6m': '1d',    // 6 Months -> 1 day candles
      'ytd': '1d',   // YTD      -> 1 day candles
      '1y': '1d',    // 1 Year   -> 1 day candles (daily)
      '2y': '1d',    // 2 Years  -> 1 day candles
      '5y': '1wk',   // 5 Years  -> 1 week candles
      '10y': '1mo',  // 10 Years -> 1 month candles
      'all': '1d',   // ALL      -> 1 day candles (forced)
      'max': '1mo'
    };

    return optimalMapping[timeRange] || '1d';
  }

  /**
   * Get valid intervals for a time range (custom restrictions mapping prioritized)
   */
  private getValidIntervalsForTimeRange(timeRange: string): string[] {
    const normalizedRange = timeRange.toLowerCase();

    // 1. First, check our custom restrictions mapping (prioritize requested UI behavior)
    if (this.TIME_RANGE_TO_VALID_INTERVALS[normalizedRange]) {
      return this.TIME_RANGE_TO_VALID_INTERVALS[normalizedRange];
    }

    // 2. Try to get from dynamic API data
    if (this.timeRangesLoaded && this.timeRanges.length > 0) {
      const timeRangeData = this.timeRanges.find(r => r.value.toLowerCase() === normalizedRange);
      if (timeRangeData?.validIntervals && timeRangeData.validIntervals.length > 0) {
        return timeRangeData.validIntervals;
      }
    }

    // 3. Fallback: get from intervals that have this range in their validRanges
    if (this.timeRangesLoaded && this.chartIntervals.length > 0) {
      const validIntervals = this.chartIntervals
        .filter(interval => interval.validRanges && interval.validRanges.some(r => r.toLowerCase() === normalizedRange))
        .map(interval => interval.value);
      if (validIntervals.length > 0) {
        return validIntervals;
      }
    }

    // 4. Final fallback: use MarketDataService to find intervals valid for this range
    const allIntervals = this.availableIntervals;
    return allIntervals
      .filter(interval => this.marketDataService.isValidIntervalRange(interval.value, timeRange))
      .map(interval => interval.value);
  }

  private hasUserManuallySelectedInterval(): boolean {
    return this.userManuallySelectedInterval;
  }

  // Get current interval display text
  get currentIntervalDisplay(): string {
    // When ALL range is selected, interval is forced to 1d.
    if (this.currentInterval?.toLowerCase() === 'all') {
      const forcedConfig = this.availableIntervals.find(i => i.value?.toLowerCase() === '1d');
      return forcedConfig?.label || '1d';
    }

    if (this.selectedInterval === null) {
      return 'Auto';
    }
    const config = this.availableIntervals.find(i => i.value === this.selectedInterval);
    return config?.label || this.selectedInterval;
  }

  // Get current interval description for tooltips
  get currentIntervalDescription(): string {
    if (this.currentInterval?.toLowerCase() === 'all') {
      return '1 day candles (required for ALL range)';
    }

    if (this.selectedInterval === null) {
      return 'Automatically selected based on time range';
    }
    const config = this.availableIntervals.find(i => i.value === this.selectedInterval);
    return config?.description || `${this.selectedInterval} candles`;
  }


  // Recent Tickers functionality
  recentTickers: RecentTicker[] = [];
  isRecentTickersPaneOpen: boolean = false;
  private readonly MAX_RECENT_TICKERS = 10;
  private readonly RECENT_TICKERS_STORAGE_KEY = 'tradingview_recent_tickers';
  isLoadingRecentTickers = false;

  /** In-memory cache of last successful `chart:data` payloads (repeat symbol/range feels instant; server/Redis still authoritative on refresh). */
  private readonly chartSocketPayloadCache = new Map<string, { payload: any; storedAt: number }>();
  private readonly chartSocketCacheMaxEntries = 32;
  private readonly chartSocketCacheTtlMs = Math.min(environment.cacheTTL ?? 300000, 120000);

  // Watchlist functionality
  watchlists: Watchlist[] = [];
  apiWatchlists: any[] = []; // Watchlists from API
  apiWatchlistSymbols: Set<string> = new Set(); // Set of all symbols in API watchlists for quick lookup
  selectedWatchlistId: string | null = null;
  activeSidebarTab: SidebarTab = 'recent';
  isAddingToWatchlist: boolean = false;
  isLoadingApiWatchlists: boolean = false;
  isLoadingWatchlistSymbols: boolean = false;

  // Quick add watchlist functionality
  showWatchlistSelector: boolean = false;
  showWatchlistMenu: boolean = false;
  showAddWatchlistSymbolModal: boolean = false;
  watchlistSymbolInput: string = '';
  // Add symbol modal: search + multi-select
  watchlistSymbolSearchQuery: string = '';
  watchlistSymbolSearchResults: { symbol: string; name?: string; exchange?: string }[] = [];
  watchlistSymbolSearchLoading: boolean = false;
  watchlistSymbolsSelected: { symbol: string; name?: string }[] = [];
  addCurrentTickerToWatchlist: boolean = false;
  private watchlistSearchSubject = new Subject<string>();

  // Create watchlist modal
  showCreateWatchlist: boolean = false;
  newWatchlistName: string = '';
  newWatchlistDescription: string = '';
  addCurrentSymbolToNew: boolean = true;

  // Edit watchlist modal (name + description only)
  showEditWatchlistModal: boolean = false;
  editWatchlistName: string = '';
  editWatchlistDescription: string = '';
  editWatchlistId: string | null = null;

  isEditingSymbol = false;
  editingSymbol = '';

  // User preferences for localStorage
  private readonly USER_PREFERENCES_KEY = 'ten-charts-preferences';
  private readonly LAST_ROUTE_STATE_KEY = 'ten-charts-last-route-state';
  private hasInitialRouteSync = false; // Flag to prevent multiple initial syncs
  /** When true, saveUserPreferences only updates localStorage; update API is called only after user changes something. */
  private isInitializingChartSettings = true;

  constructor(
    private chartService: TradingViewChartService,
    private marketDataService: MarketDataService,
    private storageService: StorageService,
    private watchlistService: WatchlistService,
    private indicatorService: IndicatorService,
    private chartTypeService: ChartTypeService,
    private chartDrawingsService: ChartDrawingsService,
    private socketConnectionService: SocketConnectionService,
    private socketDataService: SocketDataService,
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone,
    private route: ActivatedRoute,
    private router: Router
  ) {
    // Constructor should only handle dependency injection
    // All initialization logic moved to ngOnInit
  }

  ngOnInit(): void {
    // Setup subscriptions FIRST (including ticker details callback)
    this.setupSubscriptions();

    // 0. Handle query parameters (including userId) BEFORE connecting
    this.handleInitialQueryParams();

    // 1. Register socket data listeners ONCE (outside connectionStatus$ to avoid duplicates on reconnect)
    this.setupSocketDataListeners();

    // 2. Start connection with userId
    this.socketConnectionService.connect(this.userId);

    // 3. When connected, init socket listeners.
    //    Initial data request is deferred to onSettingsReady().
    //    On RECONNECTION, re-request data so the chart isn't left blank.
    this.socketConnectionService.connectionStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((connected) => {
        if (!connected) return;
        this.socketDataService.init();

        if (this.wasEverConnected && this.settingsLoaded && this.currentSymbol) {
          console.log('[ChartComponent] Socket reconnected — re-requesting data for', this.currentSymbol);
          this.loadDataWithCurrentSettings();
        }
        this.wasEverConnected = true;
      });

    // 4. Listen for socket errors
    this.socketDataService.errors$
      .pipe(takeUntil(this.destroy$))
      .subscribe(error => {
        if (error) {
          console.error('[ChartComponent] Socket error received:', error);
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
        }
      });

    // 5. Listen for chart drawings from socket
    this.socketDataService.chartDrawings$
      .pipe(takeUntil(this.destroy$))
      .subscribe(payload => {
        const { chartDrawings, symbol: s, timeframe, interval: iv } = payload;
        if (!chartDrawings?.length) return;
        const symbolMatch = !s || !this.currentSymbol || (s.toUpperCase() === this.currentSymbol.toUpperCase());
        const timeframeMatch = !timeframe || !this.currentInterval ||
          this.normalizeTimeRangeValue(timeframe) === this.normalizeTimeRangeValue(this.currentInterval);
        const intervalMatch = !iv || !this.currentEffectiveInterval ||
          this.normalizeCandleIntervalValue(iv) === this.normalizeCandleIntervalValue(this.currentEffectiveInterval);
        if (!symbolMatch || !timeframeMatch || !intervalMatch) return;
        this.ngZone.run(() => {
          this.drawingsReceivedFromSocket = true;
          this.drawingsFetchPending = false;
          this.isLoadingDrawingsFromApi = true;
          this.chartService.loadDrawingsFromChartDrawingApi(chartDrawings, true);
          this.setDrawingToolStateFromDrawings(chartDrawings);
          this.updateDrawingState();
          setTimeout(() => { this.isLoadingDrawingsFromApi = false; }, 200);
          [200, 500, 1000, 2000, 3000, 5000].forEach(delay => setTimeout(() => this.chartService.redrawPenDrawings(), delay));
          [200, 500, 1000, 2000, 3000, 5000].forEach(delay => setTimeout(() => this.chartService.redrawArrowDrawings(), delay));
          this.changeDetectorRef.markForCheck();
        });
      });

    // Load data from localStorage (synchronous operations)
    this.loadRecentTickers();
    this.loadFavoritesViewPreference();
    this.loadChartTypeFavoritesViewPreference();
    this.loadUserPreferences();

    // Setup document click listener
    this.setupDocumentClickListener();

    // Initialize watchlists subscriptions
    this.initializeWatchlists();

    // Handle query parameters for embedded mode and popup state - moved to beginning of ngOnInit
    // this.handleInitialQueryParams();

    // Fetch dynamic data from API
    this.loadChartSettings();
    this.loadChartTypes();
    this.loadChartIndicators();
    this.loadChartTimeRanges();
    this.loadChartIntervals();

    // Setup route subscriptions
    this.setupRouteSubscriptions();

    // Setup other subscriptions (already called earlier to register callbacks before socket connects)
    // this.setupSubscriptions(); // Moved to top of ngOnInit
    this.setupDebouncedSearch();
    this.setupWatchlistModalSearch();
    this.setupIndicatorSubscriptions();

    // Initialize component state
    this.initializeComponentState();

    // Sync event states
    this.syncEventStates();


    // Initial route sync - ensure route reflects current toolbar state
    setTimeout(() => {
      this.syncRouteWithCurrentState();
    }, 0);

    // After initial load (route params applied, etc.), allow update-chart-settings API when user changes something
    setTimeout(() => {
      this.isInitializingChartSettings = false;
    }, 500);
  }

  ngAfterViewInit(): void {
    // View-related initialization - chart container is now available
    this.initializeChart();
    this.initializeLucideIcons();
  }

  ngOnDestroy(): void {
    this.performCleanup();
  }

  // Private initialization methods
  private handleInitialQueryParams(): void {
    const queryParams = this.route.snapshot.queryParams;
    const embedParam = queryParams['embed'];
    const embedMode = this.parseBooleanParam(embedParam, false);

    // Extract user ID from query params
    this.userId = queryParams['userId'] || queryParams['user'];

    // Exceptionally store auth token if available, regardless of other conditions
    const tokenParam = queryParams['token'] || queryParams['authToken'] || queryParams['accessToken'];
    if (tokenParam) {
      console.log('[ChartComponent] Token found in query params (ngOnInit), storing in localStorage');
      this.storageService.set('token', tokenParam);
      setTimeout(() => {
        this.loadApiWatchlists();
      }, 100);
    }

    // Check if this is a popup window and use cached state for faster loading
    const isPopup = queryParams['isPopup'] === 'true';
    if (isPopup) {
      const cachedState = sessionStorage.getItem('chart-popup-state');
      if (cachedState) {
        try {
          const state = JSON.parse(cachedState);
          // Use cached state if it's recent (within 30 seconds)
          // Use cached state if it's recent (within 30 seconds)
          if (state.timestamp && (moment().valueOf() - moment(state.timestamp).valueOf()) < 30000) {
            console.log('[Popup] Using cached chart state for faster loading');
            // Pre-apply cached settings to avoid unnecessary reloads
            const normalizedRange = this.normalizeTimeRangeValue(state.interval);
            this.currentInterval = normalizedRange || this.currentInterval;
            this.currentTheme = state.theme || this.currentTheme;
            this.currentSeriesType = state.style ? this.normalizeSeriesType(state.style) : this.currentSeriesType;
            const normalizedCandleInterval = this.normalizeCandleIntervalValue(state.candleInterval);
            if (normalizedCandleInterval !== undefined) {
              this.selectedInterval = normalizedCandleInterval;
            }
            this.crosshairMode = state.crosshair || this.crosshairMode;
            this.showVolume = state.volume !== undefined ? state.volume : this.showVolume;
            this.showGridLines = state.gridLines !== undefined ? state.gridLines : this.showGridLines;
            this.showPriceLine = state.priceLine !== undefined ? state.priceLine : this.showPriceLine;
            this.showMarkers = state.markers !== undefined ? state.markers : this.showMarkers;
          }
        } catch (e) {
          console.warn('[Popup] Failed to parse cached state:', e);
        }
      }
    }
  }

  private initializeComponentState(): void {
    // Fallback to options if no route parameter
    if (!this.route.snapshot.params['symbol']) {
      this.currentSymbol = this.options.symbol || '';
    }

    this.showVolume = this.options.showVolume ?? true;

    // Initialize theme
    this.applyTheme(this.currentTheme);

    // Update drawing state
    this.updateDrawingState();
  }

  private setupRouteSubscriptions(): void {
    // Handle route parameters and query parameters
    combineLatest([
      this.route.params,
      this.route.queryParams
    ]).pipe(takeUntil(this.destroy$)).subscribe(([params, queryParams]) => {
      const symbol = params['symbol'];
      const interval = this.normalizeTimeRangeValue(queryParams['interval']);
      const theme = queryParams['theme'];
      const style = queryParams['style'];
      const indicators = queryParams['indicators'];
      const compare = queryParams['compare'];
      const candleInterval = this.normalizeCandleIntervalValue(queryParams['candleInterval']);
      const crosshair = queryParams['crosshair'];
      const volume = queryParams['volume'];
      const gridLines = queryParams['gridLines'];
      const priceLine = queryParams['priceLine'];
      const markers = queryParams['markers'];
      this.applyViewModeFromQuery(queryParams);

      console.log('🔄 [ROUTE] Route parameters changed:', { params, queryParams });

      let routeRequiresChartReload = false;

      // Handle symbol change
      if (symbol && symbol !== this.currentSymbol) {
        // Clear chart immediately so previous ticker's candles never bleed into the new view
        this.chartService.clearChartData();
        this.pendingSymbol = symbol.toUpperCase();
        this.dataReceivedForCurrentRequest = false;
        this.isLoading = true;

        // Clear any drawings/lines/tooltips from previous ticker so nothing bleeds across symbols
        this.chartService.clearAllHorizontalLines();
        this.chartService.clearAllTextAnnotations();
        this.chartService.clearAllPenDrawings();
        this.chartService.clearAllMeasurements();
        this.chartService.clearAllArrowDrawings();

        this.horizontalLines = [];
        this.systemLines = [];
        this.textAnnotations = [];
        this.penDrawings = [];
        this.measurements = [];
        this.arrowDrawings = [];
        this.selectedLine = null;
        this.selectedText = null;
        this.selectedPenDrawing = null;
        this.showLineToolbar = false;
        this.showTextToolbar = false;
        this.showPenToolbar = false;
        this.tooltipData = null;
        this.markerTooltipData = null;
        this.atrTooltipData = null;
        // Prevent stale details panel from showing previous symbol data while new socket/API payload loads.
        this.tickerDetails = null;
        this.currentVolume = 0;

        this.currentSymbol = symbol.toUpperCase();
        this.editingSymbol = this.currentSymbol;
        this.drawingsFetchPending = true;
        routeRequiresChartReload = true;
      } else if (symbol && !this.editingSymbol) {
        this.editingSymbol = symbol.toUpperCase();
      }

      // Handle interval change (time range) – clear drawings for old timeframe; new ones load for this range
      if (interval && interval !== this.currentInterval) {
        this.chartService.clearChartData();
        this.pendingSymbol = (symbol || this.currentSymbol || '').toUpperCase();
        this.dataReceivedForCurrentRequest = false;
        this.isLoading = true;
        this.currentInterval = interval;
        this.ensureSelectedIntervalValidForCurrentRange();
        this.clearDrawingsForTimeframeChange();
        this.drawingsFetchPending = true;
        this.drawingsReceivedFromSocket = false;
        routeRequiresChartReload = true;
      }

      // Handle candle interval change – clear drawings for old interval; new ones load for this interval
      if (candleInterval !== undefined && candleInterval !== this.selectedInterval) {
        this.chartService.clearChartData();
        this.pendingSymbol = (symbol || this.currentSymbol || '').toUpperCase();
        this.dataReceivedForCurrentRequest = false;
        this.isLoading = true;
        this.selectedInterval = candleInterval === 'auto' ? null : candleInterval;
        this.ensureSelectedIntervalValidForCurrentRange();
        this.clearDrawingsForTimeframeChange();
        this.drawingsFetchPending = true;
        this.drawingsReceivedFromSocket = false;
        routeRequiresChartReload = true;
      }

      // One load per route emission — avoids duplicate socket/API work when symbol + interval + candle change together.
      if (routeRequiresChartReload) {
        this.loadSymbol();
        this.changeDetectorRef.detectChanges();
      }

      // Handle theme change
      if (theme && theme !== this.currentTheme) {
        this.currentTheme = theme as 'light' | 'dark';
        this.applyTheme(this.currentTheme);
        this.saveUserPreferences(); // Save theme preference
      }

      // Handle chart style/series type change
      // Only update if it's actually different to avoid unnecessary updates
      if (style) {
        const normalizedStyle = this.normalizeSeriesType(style);
        if (normalizedStyle !== this.currentSeriesType) {
          this.currentSeriesType = normalizedStyle;
          // Update chart service immediately
          this.chartService.changeSeriesType(this.currentSeriesType);
          this.changeDetectorRef.markForCheck();
          this.saveUserPreferences();
        }
      }

      // Handle indicators change
      if (indicators !== undefined) {
        this.parseAndApplyIndicators(indicators);
      }

      // Handle comparison symbol
      if (compare !== undefined) {
        // TODO: Implement comparison functionality
        console.log('Comparison symbol:', compare);
      }

      // Handle crosshair mode
      if (crosshair && crosshair !== this.crosshairMode) {
        this.crosshairMode = crosshair as CrosshairMode;
        this.chartService.setCrosshairMode(this.crosshairMode);
        this.saveUserPreferences();
      }

      // Handle volume visibility
      if (volume !== undefined) {
        const showVolume = volume === 'true' || volume === '1';
        if (showVolume !== this.showVolume) {
          this.showVolume = showVolume;
          this.chartService.setVolumeVisible(this.showVolume);
          this.saveUserPreferences();
        }
      }

      // Handle grid lines visibility
      if (gridLines !== undefined) {
        const showGridLines = gridLines === 'true' || gridLines === '1';
        if (showGridLines !== this.showGridLines) {
          this.showGridLines = showGridLines;
          this.chartService.setGridLinesVisible(this.showGridLines);
          this.saveUserPreferences();
        }
      }

      // Handle price line visibility
      if (priceLine !== undefined) {
        const showPriceLine = priceLine === 'true' || priceLine === '1';
        if (showPriceLine !== this.showPriceLine) {
          this.showPriceLine = showPriceLine;
          this.chartService.setPriceLineVisible(this.showPriceLine);
          this.saveUserPreferences();
        }
      }

      // Handle markers visibility
      if (markers !== undefined) {
        const showMarkers = markers === 'true' || markers === '1';
        if (showMarkers !== this.showMarkers) {
          this.showMarkers = showMarkers;
          this.saveUserPreferences();
        }
      }
    });
  }

  private setupIndicatorSubscriptions(): void {
    // Subscribe to indicator changes
    this.indicatorService.indicators$
      .pipe(takeUntil(this.destroy$))
      .subscribe(indicators => {
        const deduped = this.dedupeIndicatorsByCanonicalKey(indicators || []);
        this.indicators = deduped;

        // Apply default active-indicators panel state (only until user toggles).
        this.applyDefaultIndicatorsPanelCollapseState();

        // Initialize default indicators (50/100/200 SMA + ATR) once when there are none
        if (!this.defaultIndicatorsInitialized && (!indicators || indicators.length === 0)) {
          this.defaultIndicatorsInitialized = true;
          this.initializeDefaultIndicators();
          return; // initialization will trigger another emission
        }

        // Sync indicators with chart service for rendering immediately
        this.chartService.syncIndicators(deduped);
        this.changeDetectorRef.markForCheck();
        // Refresh icons after indicators change
        setTimeout(() => this.refreshLucideIcons(), 100);

        // Update URL with current indicators (preserves embed params).
        // Skip while hydrating from the route so addIndicator does not fan out 7 navigations.
        if (this.applyingIndicatorsFromUrl) {
          return;
        }
        const indicatorsString = deduped.map(ind => ind.name).join(',');
        const currentIndicatorsParam = this.route.snapshot.queryParams['indicators'] || '';
        if (indicatorsString === currentIndicatorsParam) {
          return;
        }
        this.navigateWithQueryParams(undefined, undefined, undefined, undefined, indicatorsString);

        // Force another change detection after URL update
        setTimeout(() => {
          this.changeDetectorRef.markForCheck();
        }, 0);
      });
  }

  private getIndicatorCanonicalKey(indicator: Indicator): string {
    const settings = indicator.settings || {};
    switch (indicator.type) {
      case 'moving_average': {
        const period = settings['period'];
        const maType = settings['movingAverageType'] || 'exponential';
        return `moving_average:${period}:${maType}`;
      }
      case 'atr': {
        return `atr:${settings['period']}`;
      }
      case 'rsi': {
        return `rsi:${settings['period']}:${settings['source'] || 'close'}:${settings['overboughtLevel'] ?? 70}:${settings['oversoldLevel'] ?? 30}`;
      }
      case 'macd': {
        return `macd:${settings['fastPeriod']}:${settings['slowPeriod']}:${settings['signalPeriod']}:${settings['source'] || 'close'}`;
      }
      case 'bollinger_bands': {
        return `bollinger_bands:${settings['period']}:${settings['standardDeviations']}:${settings['source'] || 'close'}`;
      }
      case 'stochastic': {
        return `stochastic:${settings['kPeriod']}:${settings['dPeriod']}:${settings['smooth']}`;
      }
      default:
        return `${indicator.type}:${indicator.name}`;
    }
  }

  private dedupeIndicatorsByCanonicalKey(indicators: Indicator[]): Indicator[] {
    if (!Array.isArray(indicators) || indicators.length === 0) return [];

    // Keep the last occurrence for each canonical key (so visibility/updates win).
    const seen = new Set<string>();
    const uniqueReversed: Indicator[] = [];

    for (let i = indicators.length - 1; i >= 0; i--) {
      const ind = indicators[i];
      if (!ind) continue;
      const key = this.getIndicatorCanonicalKey(ind);
      if (seen.has(key)) continue;
      seen.add(key);
      uniqueReversed.push(ind);
    }

    return uniqueReversed.reverse();
  }

  /**
   * Initialize default indicator set:
   * - 50-day EMA (green)
   * - 100-day EMA (yellow/orange)
   * - 200-day EMA (red)
   * (No default ATR; ATR can be added manually)
   */
  private initializeDefaultIndicators(): void {
    try {
      const presets = [
        { period: 50, color: '#4CAF50' },  // green
        { period: 100, color: '#FFC107' }, // yellow/orange
        { period: 200, color: '#F44336' }  // red
      ];

      const createdIds: string[] = [];

      presets.forEach(preset => {
        const id = this.indicatorService.addIndicator('moving_average', {
          period: preset.period,
          movingAverageType: 'exponential',
          source: 'close'
        });
        if (id) {
          createdIds.push(id);
          // Override color to match requested palette
          this.indicatorService.updateIndicator(id, { color: preset.color });
        }
      });

      // Sync with chart service so everything renders immediately
      const allIndicators = this.indicatorService.getIndicators();
      this.indicators = allIndicators;
      this.chartService.syncIndicators(allIndicators);
      this.changeDetectorRef.markForCheck();
    } catch (error) {
      console.error('[ChartComponent] Error initializing default indicators', error);
    }
  }

  // Helper methods for query parameter handling
  private parseAndApplyIndicators(indicatorsParam: string): void {
    try {
      const indicatorNames = indicatorsParam.split(',').map(name => name.trim()).filter(Boolean);
      const normalized = indicatorNames.join(',');
      if (normalized === this.lastAppliedIndicatorsParam) {
        return;
      }
      this.lastAppliedIndicatorsParam = normalized;
      this.applyingIndicatorsFromUrl = true;

      if (indicatorNames.length === 0) {
        this.indicatorService.clearAllIndicators();
        this.saveUserPreferences();
        return;
      }

      const maPeriodColors: Record<number, string> = {
        50: '#4CAF50',
        100: '#FFC107',
        200: '#F44336'
      };

      this.indicatorService.clearAllIndicators();
      indicatorNames.forEach(name => {
        const indicatorType = this.getIndicatorTypeByName(name);
        if (indicatorType) {
          const settings = this.parseSettingsFromIndicatorName(name, indicatorType);
          const id = this.indicatorService.addIndicator(indicatorType, settings);
          if (id && indicatorType === 'moving_average' && typeof settings['period'] === 'number') {
            const color = maPeriodColors[settings['period'] as number] || this.getRandomPresetColor();
            this.indicatorService.updateIndicator(id, { color });
          }
        }
      });

      this.saveUserPreferences();
    } catch (error) {
      console.error('Error parsing indicators from query params:', error);
    } finally {
      this.applyingIndicatorsFromUrl = false;
    }
  }

  private parseSettingsFromIndicatorName(name: string, type: IndicatorType): Record<string, unknown> {
    const base = this.getDefaultIndicatorSettings(type);
    if (type === 'moving_average') {
      const match = name.match(/^(SMA|EMA)\((\d+)\)$/i);
      if (match) {
        const period = parseInt(match[2], 10);
        return {
          ...base,
          period: isNaN(period) || period <= 0 ? base.period : period,
          movingAverageType: match[1].toUpperCase() === 'EMA' ? 'exponential' : 'simple'
        };
      }
    }
    if (type === 'atr' || type === 'rsi') {
      const match = name.match(/\((\d+)\)$/);
      if (match) {
        const period = parseInt(match[1], 10);
        if (!isNaN(period) && period > 0) {
          return { ...base, period };
        }
      }
    }
    return base;
  }

  openPopupWindow(): void {
    try {
      if (typeof window === 'undefined') {
        return;
      }

      // Store current chart state in sessionStorage for popup to use (faster loading)
      const chartState = {
        symbol: this.route.snapshot.params['symbol'] || this.currentSymbol,
        interval: this.currentInterval,
        theme: this.currentTheme,
        style: this.currentSeriesType,
        candleInterval: this.selectedInterval || 'auto',
        crosshair: this.crosshairMode,
        volume: this.showVolume,
        gridLines: this.showGridLines,
        priceLine: this.showPriceLine,
        markers: this.showMarkers,
        indicators: this.indicators.map(ind => ind.name).join(','),
        timestamp: moment().tz(environment.defaultTimezone).format('YYYY-MM-DD HH:mm:ss')
      };
      sessionStorage.setItem('chart-popup-state', JSON.stringify(chartState));

      // Build popup URL with current symbol and all settings
      const popupUrl = new URL(window.location.origin + window.location.pathname);

      // Preserve current symbol from route
      const currentSymbol = chartState.symbol;
      popupUrl.pathname = `/chart/${currentSymbol}`;

      // Preserve all current chart settings
      popupUrl.searchParams.set('interval', chartState.interval);
      popupUrl.searchParams.set('theme', chartState.theme);
      popupUrl.searchParams.set('style', chartState.style);
      popupUrl.searchParams.set('candleInterval', chartState.candleInterval);
      popupUrl.searchParams.set('crosshair', chartState.crosshair);
      popupUrl.searchParams.set('volume', chartState.volume.toString());
      popupUrl.searchParams.set('gridLines', chartState.gridLines.toString());
      popupUrl.searchParams.set('priceLine', chartState.priceLine.toString());
      popupUrl.searchParams.set('markers', chartState.markers.toString());

      // Preserve indicators
      if (chartState.indicators) {
        popupUrl.searchParams.set('indicators', chartState.indicators);
      }

      // Preserve toolbar visibility (show all in popup)
      popupUrl.searchParams.set('showToolbar', 'true');
      popupUrl.searchParams.set('showSidebar', 'true');
      popupUrl.searchParams.set('showBottomToolbar', 'true');

      // Ensure socket identity works in the new window.
      // SocketConnectionService.connect() uses the `userId` from query params (mapped from `userId` or `user`),
      // and then passes it to the socket server as `query: { user: userId }`.
      const currentQueryParams = this.route.snapshot.queryParams;
      const effectiveUserId = currentQueryParams['userId'] || currentQueryParams['user'] || this.userId;
      if (effectiveUserId) {
        popupUrl.searchParams.set('userId', effectiveUserId.toString());
        popupUrl.searchParams.set('user', effectiveUserId.toString());
      }

      // Always propagate API auth token to fullscreen URL.
      this.appendAuthParamsToUrl(popupUrl, currentQueryParams);

      // Dedicated fullscreen research view:
      // - no embedded-style maximize button
      // - fullscreen layout controlled via query param `view=fullscreen`
      //
      // Auth in new window:
      // - Socket identity uses `user/userId` query param.
      // - API auth uses the existing `localStorage` token (same origin) when available.
      popupUrl.searchParams.set('view', 'fullscreen');
      popupUrl.searchParams.set('showPopupButton', 'false');
      popupUrl.searchParams.delete('isPopup');
      popupUrl.searchParams.delete('popupButton');

      console.log('[Popup] Opening popup with URL:', popupUrl.toString());
      console.log('[Popup] Chart state cached for faster loading');

      // Open fullscreen view in a normal browser tab (not popup window)
      const popupWindow = window.open(popupUrl.toString(), '_blank');

      if (popupWindow) {
        popupWindow.focus();

        // Poll for popup close to restore maximize button
        if (this.popupClosePoll) {
          window.clearInterval(this.popupClosePoll);
        }
        this.popupClosePoll = window.setInterval(() => {
          if (popupWindow.closed) {
            window.clearInterval(this.popupClosePoll);
            this.popupClosePoll = undefined;
            // Clean up sessionStorage after popup closes
            sessionStorage.removeItem('chart-popup-state');
            this.ngZone.run(() => {
              this.showPopupButton = true;
              this.changeDetectorRef.markForCheck();
            });
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error opening popup window:', error);
    }
  }

  openTickerInFullscreen(symbol: string, event?: Event, openSidebarPane: boolean = false, sidebarTab?: SidebarTab): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (!symbol) {
      return;
    }

    if (this.isFullscreenView) {
      if (openSidebarPane) {
        this.isRecentTickersPaneOpen = true;
      }
      if (sidebarTab === 'recent' || sidebarTab === 'watchlists') {
        this.activeSidebarTab = sidebarTab;
      }
      this.navigateWithQueryParams(symbol);
      return;
    }

    // Stay in-frame when embedded or running inside a parent iframe.
    if (this.isEmbeddedView || this.isRunningInParentFrame()) {
      this.navigateWithQueryParams(symbol);
      return;
    }

    try {
      if (typeof window === 'undefined') {
        return;
      }

      const chartState = {
        symbol,
        interval: this.currentInterval,
        theme: this.currentTheme,
        style: this.currentSeriesType,
        candleInterval: this.selectedInterval || 'auto',
        crosshair: this.crosshairMode,
        volume: this.showVolume,
        gridLines: this.showGridLines,
        priceLine: this.showPriceLine,
        markers: this.showMarkers,
        indicators: this.indicators.map(ind => ind.name).join(','),
        timestamp: moment().tz(environment.defaultTimezone).format('YYYY-MM-DD HH:mm:ss')
      };
      sessionStorage.setItem('chart-popup-state', JSON.stringify(chartState));

      const popupUrl = new URL(window.location.origin + `/chart/${symbol}`);
      popupUrl.searchParams.set('interval', chartState.interval);
      popupUrl.searchParams.set('theme', chartState.theme);
      popupUrl.searchParams.set('style', chartState.style);
      popupUrl.searchParams.set('candleInterval', chartState.candleInterval);
      popupUrl.searchParams.set('crosshair', chartState.crosshair);
      popupUrl.searchParams.set('volume', chartState.volume.toString());
      popupUrl.searchParams.set('gridLines', chartState.gridLines.toString());
      popupUrl.searchParams.set('priceLine', chartState.priceLine.toString());
      popupUrl.searchParams.set('markers', chartState.markers.toString());
      if (chartState.indicators) {
        popupUrl.searchParams.set('indicators', chartState.indicators);
      }
      popupUrl.searchParams.set('showToolbar', 'true');
      popupUrl.searchParams.set('showSidebar', 'true');
      popupUrl.searchParams.set('showBottomToolbar', 'true');
      popupUrl.searchParams.set('view', 'fullscreen');
      popupUrl.searchParams.set('showPopupButton', 'false');
      popupUrl.searchParams.set('sidebarOpen', openSidebarPane ? 'true' : 'false');
      if (sidebarTab === 'recent' || sidebarTab === 'watchlists') {
        popupUrl.searchParams.set('sidebarTab', sidebarTab);
      }
      popupUrl.searchParams.delete('isPopup');
      popupUrl.searchParams.delete('popupButton');

      const currentQueryParams = this.route.snapshot.queryParams;
      const effectiveUserId = currentQueryParams['userId'] || currentQueryParams['user'] || this.userId;
      if (effectiveUserId) {
        popupUrl.searchParams.set('userId', effectiveUserId.toString());
        popupUrl.searchParams.set('user', effectiveUserId.toString());
      }

      // Always propagate API auth token to fullscreen URL.
      this.appendAuthParamsToUrl(popupUrl, currentQueryParams);

      // Open selected ticker in a normal browser tab (not popup window)
      const popupWindow = window.open(popupUrl.toString(), '_blank');
      if (popupWindow) {
        popupWindow.focus();
      }
    } catch (error) {
      console.error('Error opening ticker fullscreen window:', error);
    }
  }


  /** Keep the embedded chart inside the parent iframe, including the time-range bar. */
  private syncEmbedDocumentHeight(embedded: boolean): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.documentElement.classList.toggle('chart-embed-root', embedded);
    document.body.classList.toggle('chart-embed-root', embedded);
  }

  private applyViewModeFromQuery(queryParams: Params): void {
    const embedParam = queryParams['embed'];
    const embedMode = this.parseBooleanParam(embedParam, false);

    const viewParam = queryParams['view'];
    const fullscreenMode = typeof viewParam === 'string' && viewParam.toLowerCase() === 'fullscreen';
    const fullscreenChanged = this.isFullscreenView !== fullscreenMode;
    this.isFullscreenView = fullscreenMode;

    // Switching view modes should restore the default indicators-panel UX.
    if (fullscreenChanged) {
      this.hasUserToggledIndicatorsPanel = false;
    }

    // `view=fullscreen` overrides embedded mode behaviors.
    this.isEmbeddedView = fullscreenMode ? false : (embedMode || this.isRunningInParentFrame());
    this.syncEmbedDocumentHeight(this.isEmbeddedView);
    const toolbarParam = queryParams['showToolbar'];
    const sidebarParam = queryParams['showSidebar'];
    const sidebarOpenParam = queryParams['sidebarOpen'];
    const sidebarTabParam = queryParams['sidebarTab'];
    const bottomToolbarParam = queryParams['showBottomToolbar'];
    // Debug: Log embed parameter reading
    console.log('[ChartComponent] Embed param from URL:', embedParam, 'Parsed as:', embedMode);

    // Check for auth token in query params and store it
    // (This handles token updates if query params change after initial load)
    const tokenParam = queryParams['token'] || queryParams['authToken'] || queryParams['accessToken'];
    const currentToken = this.storageService.get('token');
    if (tokenParam && tokenParam !== currentToken) {
      console.log('[ChartComponent] Token found/updated in query params, storing in localStorage');
      this.storageService.set('token', tokenParam);
      // Load API watchlists after token is set/updated
      setTimeout(() => {
        this.loadApiWatchlists();
      }, 100);
    }
    // In embed mode, default to showing toolbars unless explicitly set to false
    this.showToolbar = toolbarParam !== undefined ? this.parseBooleanParam(toolbarParam, true) : true;
    this.showSidebar = sidebarParam !== undefined ? this.parseBooleanParam(sidebarParam, true) : true;
    if (this.readHideLogo(queryParams)) {
      this.dedicatedChartHidesLogo = true;
    }
    this.showBrandLogo = !this.dedicatedChartHidesLogo;
    this.showBottomToolbar = bottomToolbarParam !== undefined ? this.parseBooleanParam(bottomToolbarParam, true) : true;

    // Fullscreen mode should remain fully interactive, but collapse non-essential layout.
    if (this.isFullscreenView) {
      this.showToolbar = true;
      this.showBottomToolbar = true;
      this.showPopupButton = false;
      this.showSidebar = true; // kept collapsed via CSS/state; needed for ticker changes
      // Only collapse the side pane when *entering* fullscreen — not on every route tick (e.g. symbol change).
      if (fullscreenChanged) {
        this.isRecentTickersPaneOpen = this.parseBooleanParam(sidebarOpenParam, false);
      }
      if (sidebarTabParam === 'recent' || sidebarTabParam === 'watchlists') {
        this.activeSidebarTab = sidebarTabParam;
      }
    }

    // In embedded mode, default to watchlists tab (panel visibility is user-controlled; survives ticker changes).
    if (this.isEmbeddedView) {
      this.activeSidebarTab = 'watchlists';
    }

    if (!this.showSidebar) {
      this.isRecentTickersPaneOpen = false;
    }

    const popupParam = queryParams['showPopupButton'] ?? queryParams['popupButton'];
    // Default to showing maximize button in non-embedded mode as well.
    // (Fullscreen view still forces showPopupButton=false below.)
    this.showPopupButton = popupParam !== undefined ? this.parseBooleanParam(popupParam, embedMode) : true;

    // Ensure maximize button never shows in fullscreen.
    if (this.isFullscreenView) {
      this.showPopupButton = false;
    }

    // Apply default active-indicators panel state (only until user toggles).
    this.applyDefaultIndicatorsPanelCollapseState();

    const popupWidthParam = parseInt(queryParams['popupWidth'], 10);
    if (!isNaN(popupWidthParam) && popupWidthParam > 0) {
      this.popupWidth = popupWidthParam;
    }

    const popupHeightParam = parseInt(queryParams['popupHeight'], 10);
    if (!isNaN(popupHeightParam) && popupHeightParam > 0) {
      this.popupHeight = popupHeightParam;
    }

    if (this.isFullscreenView && this.currentSymbol) {
      setTimeout(() => void this.hydrateFullscreenQuoteExtras(), 400);
    }

    this.changeDetectorRef.markForCheck();
  }

  private readHideLogo(queryParams: Params): boolean {
    if (queryParams['hideLogo'] !== undefined) {
      return this.parseBooleanParam(queryParams['hideLogo'], false);
    }
    try {
      return /(?:^|[?&])hideLogo=true(?:&|$)/i.test(window.location.search);
    } catch {
      return false;
    }
  }

  private parseBooleanParam(value: any, defaultValue: boolean): boolean {
    if (value === undefined || value === null) {
      return defaultValue;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    const normalized = value.toString().trim().toLowerCase();
    if (['1', 'true', 'yes', 'on'].includes(normalized)) {
      return true;
    }

    if (['0', 'false', 'no', 'off'].includes(normalized)) {
      return false;
    }

    return defaultValue;
  }

  private normalizeTimeRangeValue(value: any): string | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    const normalized = value.toString().trim().toLowerCase();
    const aliasMap: Record<string, string> = {
      '1day': '1d',
      '5day': '5d',
      '1 month': '1m',
      '1month': '1m',
      '6 month': '6m',
      '6month': '6m',
      '1 year': '1y',
      '1year': '1y',
      'alltime': 'all'
    };

    return aliasMap[normalized] || normalized;
  }

  private normalizeCandleIntervalValue(value: any): string | null | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    const normalized = value.toString().trim().toLowerCase();
    if (normalized === 'auto') {
      return null;
    }

    return normalized;
  }

  private getIndicatorTypeByName(name: string): IndicatorType | null {
    const baseName = name.split('(')[0].trim().toUpperCase();
    const nameMap: Record<string, IndicatorType> = {
      'EMA': 'moving_average',
      'SMA': 'moving_average',
      'MOVING_AVERAGE': 'moving_average',
      'RSI': 'rsi',
      'MACD': 'macd',
      'BB': 'bollinger_bands',
      'BOLLINGER_BANDS': 'bollinger_bands',
      'ATR': 'atr',
      'STOCH': 'stochastic',
      'STOCHASTIC': 'stochastic'
    };
    return nameMap[baseName] || null;
  }

  private getDefaultIndicatorSettings(type: IndicatorType): any {
    const defaultSettings: Record<IndicatorType, any> = {
      'moving_average': { period: 20, movingAverageType: 'exponential' },
      'atr': { period: 14 },
      'rsi': { period: 14 },
      'macd': { fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 },
      'bollinger_bands': { period: 20, stdDev: 2 },
      'stochastic': { kPeriod: 14, dPeriod: 3 }
    };
    return defaultSettings[type] || {};
  }

  private getRandomPresetColor(): string {
    const colors = this.presetColors;
    return colors[Math.floor(Math.random() * colors.length)].value;
  }

  // User preferences management
  /**
   * Load chart settings from API (dynamic data)
   */
  loadChartSettings(): void {
    if (this.settingsReadyFailsafeTimer) {
      clearTimeout(this.settingsReadyFailsafeTimer);
      this.settingsReadyFailsafeTimer = null;
    }
    this.settingsReadyFailsafeTimer = setTimeout(() => {
      this.settingsReadyFailsafeTimer = null;
      this.onSettingsReady();
    }, 3000);

    this.chartService.fetchChartSettings().subscribe({
      next: (settings: ChartSettings) => {
        console.log('[ChartComponent] Loaded chart settings from API:', settings);
        if (this.settingsReadyFailsafeTimer) {
          clearTimeout(this.settingsReadyFailsafeTimer);
          this.settingsReadyFailsafeTimer = null;
        }
        if (settings && Object.keys(settings).length > 0) {
          this.applyChartSettings(settings);
        }
        this.onSettingsReady();
        this.changeDetectorRef.markForCheck();
      },
      error: (error) => {
        console.error('[ChartComponent] Error loading chart settings from API:', error);
        if (this.settingsReadyFailsafeTimer) {
          clearTimeout(this.settingsReadyFailsafeTimer);
          this.settingsReadyFailsafeTimer = null;
        }
        this.loadUserPreferences();
        this.onSettingsReady();
      }
    });
  }

  /** Called once settings are loaded (or timed out). Triggers the first data request. */
  private onSettingsReady(): void {
    if (this.settingsLoaded) return;
    this.settingsLoaded = true;

    // Now that settings are applied, fire the initial data request if we have a symbol
    const symbol = (this.currentSymbol || '').trim();
    if (symbol) {
      console.log('[ChartComponent] Settings ready, requesting data for', symbol, 'range:', this.currentInterval);
      this.loadDataWithCurrentSettings();
    }
  }

  /**
   * Apply chart settings to component
   */
  private applyChartSettings(settings: ChartSettings): void {
    if (settings.theme) {
      this.currentTheme = settings.theme;
      this.applyTheme(settings.theme);
    }
    if (settings.interval) {
      const normalizedRange = this.normalizeTimeRangeValue(settings.interval);
      if (normalizedRange) {
        this.currentInterval = normalizedRange;
      }
    }
    if (settings.candleInterval !== undefined) {
      const normalizedCandleInterval = this.normalizeCandleIntervalValue(settings.candleInterval);
      if (normalizedCandleInterval !== undefined) {
        this.selectedInterval = normalizedCandleInterval;
        this.ensureSelectedIntervalValidForCurrentRange();
      }
    }
    if (settings.style) {
      const normalized = this.normalizeSeriesType(settings.style);
      this.currentSeriesType = normalized;
      this.chartService.changeSeriesType(normalized);
    }
    if (settings.crosshair) {
      this.crosshairMode = settings.crosshair;
      this.chartService.setCrosshairMode(settings.crosshair);
    }
    if (settings.volume !== undefined) {
      this.showVolume = settings.volume;
      this.chartService.setVolumeVisible(this.showVolume);
    }
    if (settings.gridLines !== undefined) {
      this.showGridLines = settings.gridLines;
      this.chartService.setGridLinesVisible(this.showGridLines);
    }

    if (settings.priceLine !== undefined) {
      this.showPriceLine = settings.priceLine;
      this.chartService.setPriceLineVisible(this.showPriceLine);
    }
    if (settings.markers !== undefined) {
      this.showMarkers = settings.markers;
    }
    if (settings.indicators) {
      this.parseAndApplyIndicators(settings.indicators);
    }
    // Bind drawings from get-chart-settings API to chart
    const drawings = settings.chartDrawings ?? (settings as any).drawings;
    if (Array.isArray(drawings) && drawings.length > 0) {
      this.chartService.loadDrawingsFromChartDrawingApi(drawings, true);
      this.setDrawingToolStateFromDrawings(drawings);
      this.updateDrawingState();
      [200, 500, 1000, 2000, 3000, 5000].forEach(delay => setTimeout(() => this.chartService.redrawPenDrawings(), delay));
    }
  }

  private loadUserPreferences(): void {
    try {
      const saved = localStorage.getItem(this.USER_PREFERENCES_KEY);
      if (saved) {
        const preferences = JSON.parse(saved);
        if (preferences.theme) {
          this.currentTheme = preferences.theme;
        }
        if (preferences.interval) {
          const normalizedRange = this.normalizeTimeRangeValue(preferences.interval);
          this.currentInterval = normalizedRange || this.currentInterval;
        }
        if (preferences.candleInterval !== undefined) {
          const normalizedCandleInterval = this.normalizeCandleIntervalValue(preferences.candleInterval);
          if (normalizedCandleInterval !== undefined) {
            this.selectedInterval = normalizedCandleInterval;
          }
        }
        if (preferences.style) {
          this.currentSeriesType = this.normalizeSeriesType(preferences.style);
        }
        if (preferences.crosshair) {
          this.crosshairMode = preferences.crosshair;
        }
        if (preferences.volume !== undefined) {
          this.showVolume = preferences.volume;
        }
        if (preferences.gridLines !== undefined) {
          this.showGridLines = preferences.gridLines;
        }
        if (preferences.priceLine !== undefined) {
          this.showPriceLine = preferences.priceLine;
        }
        if (preferences.markers !== undefined) {
          this.showMarkers = preferences.markers;
        }
        if (preferences.indicators) {
          this.parseAndApplyIndicators(preferences.indicators);
        }
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  }

  private saveUserPreferences(): void {
    try {
      const preferences: ChartSettings = {
        theme: this.currentTheme,
        interval: this.currentInterval,
        candleInterval: this.selectedInterval,
        style: this.currentSeriesType,
        crosshair: this.crosshairMode,
        volume: this.showVolume,
        gridLines: this.showGridLines,
        priceLine: this.showPriceLine,
        markers: this.showMarkers,
        indicators: this.indicators.map(ind => ind.name).join(',')
      };

      // Save to localStorage as fallback
      localStorage.setItem(this.USER_PREFERENCES_KEY, JSON.stringify(preferences));

      // Call update API only when user has changed something, not on initial load
      if (!this.isInitializingChartSettings) {
        this.chartService.updateChartSettings(preferences).subscribe({
          next: () => {
            console.log('[ChartComponent] Chart settings saved to API successfully');
          },
          error: (error) => {
            console.error('[ChartComponent] Error saving chart settings to API:', error);
            // Settings are already saved to localStorage as fallback
          }
        });
      }

      // Also save the last route state for fallback
      this.saveLastRouteState();
    } catch (error) {
      console.error('Error saving user preferences:', error);
    }
  }

  // Save the last used route state (symbol + toolbar) for fallback
  private saveLastRouteState(): void {
    try {
      const routeState = {
        symbol: this.currentSymbol,
        interval: this.currentInterval,
        theme: this.currentTheme,
        style: this.currentSeriesType,
        candleInterval: this.selectedInterval || 'auto',
        crosshair: this.crosshairMode,
        volume: this.showVolume,
        gridLines: this.showGridLines,
        priceLine: this.showPriceLine,
        markers: this.showMarkers,
        indicators: this.indicators.map(ind => ind.name).join(','),
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(this.LAST_ROUTE_STATE_KEY, JSON.stringify(routeState));
    } catch (error) {
      console.error('Error saving last route state:', error);
    }
  }

  // Load the last used route state for fallback
  private loadLastRouteState(): { symbol: string; queryParams: any } | null {
    try {
      const saved = localStorage.getItem(this.LAST_ROUTE_STATE_KEY);
      if (saved) {
        const routeState = JSON.parse(saved);
        const queryParams: any = {
          interval: this.normalizeTimeRangeValue(routeState.interval) || this.currentInterval,
          theme: routeState.theme,
          style: routeState.style,
          candleInterval: this.normalizeCandleIntervalValue(routeState.candleInterval) || 'auto',
          crosshair: routeState.crosshair,
          volume: routeState.volume.toString(),
          gridLines: routeState.gridLines.toString(),
          priceLine: routeState.priceLine.toString(),
          markers: routeState.markers.toString()
        };

        if (routeState.indicators) {
          queryParams.indicators = routeState.indicators;
        }

        return {
          symbol: routeState.symbol,
          queryParams
        };
      }
    } catch (error) {
      console.error('Error loading last route state:', error);
    }
    return null;
  }

  // Navigation helper methods
  navigateWithQueryParams(symbol?: string, interval?: string, theme?: string, style?: string, indicators?: string, compare?: string, candleInterval?: string, crosshair?: string, volume?: boolean, gridLines?: boolean, priceLine?: boolean, markers?: boolean): void {
    const targetSymbol = symbol || this.currentSymbol;
    const queryParams: any = {};

    // Preserve embed-related params from current route (important for embed mode)
    const currentQueryParams = this.route.snapshot.queryParams;
    if (currentQueryParams['view']) queryParams.view = currentQueryParams['view'];
    // Preserve socket identity for fullscreen/reloads ("User not found" otherwise)
    if (currentQueryParams['userId']) queryParams.userId = currentQueryParams['userId'];
    if (currentQueryParams['user']) queryParams.user = currentQueryParams['user'];
    // Preserve API auth params; fallback to stored token for fullscreen/live flows.
    const tokenFromQuery = currentQueryParams['token'] || currentQueryParams['authToken'] || currentQueryParams['accessToken'];
    const tokenFromStorage =
      this.storageService.get('token') ||
      this.storageService.get('authToken') ||
      this.storageService.get('accessToken');
    const effectiveToken = tokenFromQuery || tokenFromStorage;
    if (effectiveToken) {
      queryParams.token = effectiveToken;
    }
    if (currentQueryParams['embed']) queryParams.embed = currentQueryParams['embed'];
    if (currentQueryParams['showToolbar']) queryParams.showToolbar = currentQueryParams['showToolbar'];
    if (currentQueryParams['showSidebar']) queryParams.showSidebar = currentQueryParams['showSidebar'];
    if (currentQueryParams['showBottomToolbar']) queryParams.showBottomToolbar = currentQueryParams['showBottomToolbar'];
    if (currentQueryParams['showPopupButton']) queryParams.showPopupButton = currentQueryParams['showPopupButton'];
    if (currentQueryParams['popupButton']) queryParams.popupButton = currentQueryParams['popupButton'];
    if (currentQueryParams['popupWidth']) queryParams.popupWidth = currentQueryParams['popupWidth'];
    if (currentQueryParams['popupHeight']) queryParams.popupHeight = currentQueryParams['popupHeight'];
    if (currentQueryParams['isPopup']) queryParams.isPopup = currentQueryParams['isPopup'];
    if (currentQueryParams['hideLogo'] !== undefined) queryParams.hideLogo = currentQueryParams['hideLogo'];

    // Always include all current toolbar state in query params
    queryParams.interval = this.normalizeTimeRangeValue(interval || this.currentInterval) || '1d';
    queryParams.theme = theme || this.currentTheme;
    queryParams.style = style || this.currentSeriesType;
    queryParams.candleInterval = candleInterval !== undefined
      ? (this.normalizeCandleIntervalValue(candleInterval) || 'auto')
      : (this.selectedInterval || 'auto');
    queryParams.crosshair = crosshair || this.crosshairMode;
    queryParams.volume = (volume !== undefined ? volume : this.showVolume).toString();
    queryParams.gridLines = (gridLines !== undefined ? gridLines : this.showGridLines).toString();
    queryParams.priceLine = (priceLine !== undefined ? priceLine : this.showPriceLine).toString();
    queryParams.markers = (markers !== undefined ? markers : this.showMarkers).toString();

    // Include indicators if provided, otherwise use current indicators
    if (indicators !== undefined) {
      queryParams.indicators = indicators;
    } else if (this.indicators.length > 0) {
      queryParams.indicators = this.indicators.map(ind => ind.name).join(',');
    }

    // Include compare if provided
    if (compare !== undefined) {
      queryParams.compare = compare;
    }

    console.log('🔄 [ROUTE] Navigating with full state:', { symbol: targetSymbol, queryParams });

    // If symbol is changing, navigate to new symbol with all toolbar params
    if (symbol && symbol !== this.currentSymbol) {
      this.router.navigate(['/chart', targetSymbol], {
        queryParams,
        queryParamsHandling: 'replace' // Replace to avoid merging with old params
      });
      this.notifyEmbedParentSymbolChange(targetSymbol);
    } else {
      // If only toolbar is changing, update query params while keeping same symbol
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'replace' // Replace to ensure clean state
      });
    }
  }

  private appendAuthParamsToUrl(url: URL, currentQueryParams: Params): void {
    const tokenFromQuery = currentQueryParams['token'] || currentQueryParams['authToken'] || currentQueryParams['accessToken'];
    const tokenFromStorage =
      this.storageService.get('token') ||
      this.storageService.get('authToken') ||
      this.storageService.get('accessToken');
    const effectiveToken = tokenFromQuery || tokenFromStorage;

    if (effectiveToken) {
      url.searchParams.set('token', effectiveToken);
    }
    const hideLogo = currentQueryParams['hideLogo'];
    if (hideLogo !== undefined && hideLogo !== null && String(hideLogo) !== '') {
      url.searchParams.set('hideLogo', String(hideLogo));
    }
  }

  /** True when chart runs inside a parent page iframe (e.g. trade tracker stock info). */
  private isRunningInParentFrame(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    try {
      return window.parent !== window;
    } catch {
      // Cross-origin parent access can throw; still treat as embedded iframe.
      return true;
    }
  }

  /** Notify parent app (progressive-web-app) to sync stock-info route when symbol changes in embed. */
  private notifyEmbedParentSymbolChange(symbol: string): void {
    if (!symbol || typeof window === 'undefined') {
      return;
    }
    if (!this.isEmbeddedView && !this.isRunningInParentFrame()) {
      return;
    }

    const payload = {
      type: 'chart:symbol-change',
      symbol: symbol.toUpperCase(),
      source: 'ten-charts'
    };

    try {
      window.parent.postMessage(payload, '*');
      console.log('[ChartComponent] Notified parent of symbol change:', payload.symbol);
    } catch (error) {
      console.warn('[ChartComponent] Failed to notify parent of symbol change:', error);
    }
  }

  // Sync route with current toolbar state on initial load
  private syncRouteWithCurrentState(): void {
    // Only sync once on initial load
    if (this.hasInitialRouteSync) {
      return;
    }

    const currentQueryParams = this.route.snapshot.queryParams;
    const currentSymbol = this.route.snapshot.params['symbol'];

    // Check if we have a valid route with symbol and essential query parameters
    const hasValidRoute = currentSymbol &&
      currentQueryParams['interval'] &&
      currentQueryParams['theme'] &&
      currentQueryParams['style'] &&
      currentQueryParams['candleInterval'];

    if (!hasValidRoute) {
      console.log('🔄 [ROUTE SYNC] Route missing essential parameters, checking fallback options');

      // Try to load from localStorage fallback
      const lastRouteState = this.loadLastRouteState();

      if (lastRouteState) {
        console.log('🔄 [ROUTE SYNC] Loading from localStorage fallback:', lastRouteState);
        this.router.navigate(['/chart', lastRouteState.symbol], {
          queryParams: lastRouteState.queryParams,
          queryParamsHandling: 'replace'
        });
      } else {
        console.log('🔄 [ROUTE SYNC] No fallback available, using current toolbar state');
        // Update route with current toolbar state
        this.navigateWithQueryParams(
          undefined, // symbol - keep current
          this.currentInterval, // interval
          this.currentTheme, // theme
          this.currentSeriesType, // style
          undefined, // indicators - will be handled separately
          undefined, // compare
          this.selectedInterval || 'auto', // candleInterval
          this.crosshairMode, // crosshair
          this.showVolume, // volume
          this.showGridLines, // gridLines
          this.showPriceLine, // priceLine
          this.showMarkers // markers
        );
      }

      this.hasInitialRouteSync = true;
    } else {
      console.log('✅ [ROUTE SYNC] Route already has all essential parameters');
      this.hasInitialRouteSync = true;
    }
  }

  private initializeLucideIcons(): void {
    const attempts = [0, 100, 500, 1000, 2000];

    attempts.forEach(delay => {
      setTimeout(() => {
        if (typeof (window as any).lucide !== 'undefined') {
          (window as any).lucide.createIcons();
          const iconCount = document.querySelectorAll('i[data-lucide]').length;
          console.log(`[ICONS] Lucide icons attempt (${delay}ms) - found ${iconCount} icons`);

          // Log what icons we found
          document.querySelectorAll('i[data-lucide]').forEach((el, i) => {
            console.log(`Icon ${i}: ${el.getAttribute('data-lucide')}`);
          });
        } else {
          console.warn(`[ICONS] Lucide not available at ${delay}ms`);
        }
      }, delay);
    });
  }

  private setupDocumentClickListener(): void {
    this.documentClickListener = (event: Event) => {
      const target = event.target as HTMLElement;

      // Handle symbol search area clicks
      if (this.isEditingSymbol && !target.closest('.ticker-search-group') && !target.closest('.search-results-dropdown')) {
        this.ngZone.run(() => {
          this.hideSearchResults();

          const trimmedSymbol = this.editingSymbol.trim();
          if (trimmedSymbol === '' || trimmedSymbol.toUpperCase() === this.currentSymbol) {
            this.resetHeaderSearchInput(false);
          }
        });
      }

      // Don't close dropdowns if clicking on a tool button (let toggleDropdown handle it)
      const isToolButton = target.closest('.tool-btn') !== null ||
        target.closest('.settings-toolbar-btn') !== null ||
        target.closest('.interval-btn') !== null ||
        target.closest('.watchlist-btn') !== null;

      if (isToolButton) {
        return; // Let the button's click handler manage dropdown state
      }

      // Check if click is inside any dropdown, popup, or toolbar
      const isInsideDropdown = target.closest('.tool-dropdown') !== null;
      const isInsideToolMenu = target.closest('.tool-menu') !== null;
      const isInsidePopup = target.closest('.chart-settings-popup') !== null ||
        target.closest('.chart-settings-overlay') !== null ||
        target.closest('.indicator-settings-popup') !== null ||
        target.closest('.indicator-settings-overlay') !== null ||
        target.closest('.modal-overlay') !== null ||
        target.closest('.modal-content') !== null;
      const isInsideToolbar = target.closest('.compact-line-toolbar') !== null ||
        target.closest('.compact-text-toolbar') !== null ||
        target.closest('.compact-pen-toolbar') !== null ||
        target.closest('.compact-arrow-toolbar') !== null;
      const isInsideWatchlist = target.closest('.watchlist-selector-dropdown') !== null ||
        target.closest('.watchlist-menu') !== null ||
        target.closest('.watchlist-selector') !== null;
      const isInsideInterval = target.closest('.interval-dropdown') !== null ||
        target.closest('.interval-selector') !== null;

      // If clicking outside all dropdowns/popups/toolbars, close them
      if (!isInsideDropdown && !isInsideToolMenu && !isInsidePopup && !isInsideToolbar &&
        !isInsideWatchlist && !isInsideInterval) {
        this.ngZone.run(() => {
          // Close all dropdowns and popups
          this.closeAllMenus();
          this.changeDetectorRef.markForCheck();
        });
      }
    };
  }

  private setupSubscriptions(): void {
    // Register callback for real-time line updates (and debounced save to API)
    this.chartService.setOnLinesChangedCallback(() => {
      this.ngZone.run(() => {
        this.updateDrawingState();
        this.scheduleSaveDrawings();
        // OnPush: markForCheck defers work; line/text/pen/arrow edits must show immediately
        this.changeDetectorRef.detectChanges();
      });
    });

    // Register callback for tooltip updates
    this.chartService.setOnTooltipChangedCallback((tooltip: TooltipData | null) => {
      this.ngZone.run(() => {
        // Keep the last valid hovered candle visible when mouse leaves chart.
        // Tooltip should only clear on explicit context changes (e.g. symbol switch/reset).
        if (tooltip) {
          this.tooltipData = tooltip;
        }
        this.changeDetectorRef.markForCheck();
      });
    });

    // Register callback for marker tooltip updates
    this.chartService.setOnMarkerTooltipChangedCallback((tooltip: MarkerTooltipData | null) => {
      this.ngZone.run(() => {
        this.markerTooltipData = tooltip;
        this.changeDetectorRef.markForCheck();
      });
    });

    // Subscribe to chart rendered event — chart data is drawn first, THEN indicators/drawings
    this.chartService.chartRendered$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.ngZone.run(() => {
          if (this.isLoading && this.dataReceivedForCurrentRequest) {
            const maxLookback = this.indicatorService.getMaxLookbackPeriod();
            const needsWarmup = maxLookback > 0 &&
              !this.chartService.hasSufficientIndicatorWarmup(maxLookback, this.currentInterval);
            if (!needsWarmup) {
              console.log('[ChartComponent] Chart rendered with new data, hiding loader');
              this.isLoading = false;
              this.changeDetectorRef.markForCheck();
            } else {
              console.log('[ChartComponent] Chart rendered; waiting for indicator warm-up before hiding loader');
            }
          } else if (this.isLoading) {
            console.log('[ChartComponent] Chart rendered but waiting for new data, keeping loader active');
          }

          // Always (re)paint indicators after data/range apply — not only while loading.
          // Cache hits set isLoading=false before render, and visible-range races can clip MAs
          // until the user zooms. Re-syncing here keeps lines full-width immediately.
          setTimeout(() => {
            if (this.dataReceivedForCurrentRequest && this.indicators && this.indicators.length > 0) {
              //console.log('[INDICATORS] Syncing indicators after chart render');
              this.chartService.syncIndicators(this.indicators);
              this.indicatorsSyncedForCurrentRequest = true;
            }

            // Proactively fetch indicator warm-up history (EMA 200 on YTD, etc.).
            this.ensureIndicatorWarmupHistory();

            if (this.drawingsFetchPending) {
              this.drawingsFetchPending = false;
              if (!this.drawingsReceivedFromSocket) this.fetchChartDrawings();
              this.drawingsReceivedFromSocket = false;
            }
            this.changeDetectorRef.markForCheck();
          }, 50);

          const penDrawings = this.chartService.getPenDrawings();
          if (penDrawings.length > 0) {
            this.isPenMode = true;
            this.chartService.setPenMode(true);
            this.changeDetectorRef.markForCheck();
          }
          this.chartService.redrawPenDrawings();
        });
      });


    // Register callback for ATR tooltip updates
    this.chartService.setOnATRTooltipChangedCallback((tooltip: ATRTooltipData | null) => {
      this.ngZone.run(() => {
        if (tooltip) {
          // Apply safe positioning for ATR tooltip
          const safePosition = this.positionATRTooltipSafely(tooltip.x, tooltip.y);
          tooltip.x = safePosition.x;
          tooltip.y = safePosition.y;
        }
        this.atrTooltipData = tooltip;
        this.changeDetectorRef.markForCheck();
      });
    });

    // Subscribe to ticker details from market data service (for API data)
    this.marketDataService.tickerDetails$
      .pipe(takeUntil(this.destroy$))
      .subscribe(details => {
        // Ignore stale details coming from previous symbol.
        if (details?.symbol && this.currentSymbol && details.symbol.toUpperCase() !== this.currentSymbol.toUpperCase()) {
          return;
        }
        this.tickerDetails = details;
        if (details) {
          this.currentVolume = details.volume || 0;
        }
        this.changeDetectorRef.markForCheck();
      });

    // Register callback for ticker details from socket data
    this.chartService.setOnTickerDetailsChangedCallback((details: TickerDetails | null) => {
      this.ngZone.run(() => {
        if (details) {
          // Ignore stale details coming from previous symbol.
          if (details.symbol && this.currentSymbol && details.symbol.toUpperCase() !== this.currentSymbol.toUpperCase()) {
            return;
          }
          this.tickerDetails = details;
          this.currentVolume = details.volume || 0;
          // Also update marketDataService so other subscribers get the update
          // Note: We need to update the marketDataService's tickerDetailsSubject
          // Since it's private, we'll just update our local tickerDetails
          this.changeDetectorRef.markForCheck();
        }
      });
    });

    // Register callback for line context menu
    this.chartService.setOnLineSelectedCallback((line: HorizontalLine | null, x?: number, y?: number) => {
      this.ngZone.run(() => {
        this.selectedLine = line;
        if (line && x !== undefined && y !== undefined) {
          // Smart positioning to keep toolbar on screen
          this.positionToolbarSafely(x, y);
          this.showLineToolbar = true;
          // Refresh icons after toolbar is shown
          this.refreshLucideIcons();
        } else {
          this.showLineToolbar = false;
        }
        this.activeDropdown = null;
        this.activeToolbarOption = null;
        this.changeDetectorRef.markForCheck();
      });
    });

    // Register callback for text selection
    this.chartService.setOnTextSelectedCallback((text: TextAnnotation | null, x?: number, y?: number) => {
      this.ngZone.run(() => {
        this.selectedText = text;
        if (text && x !== undefined && y !== undefined) {
          // Position text toolbar
          this.positionTextToolbarSafely(x, y);
          this.showTextToolbar = true;
          // Refresh icons after toolbar is shown
          this.refreshLucideIcons();
        } else {
          this.showTextToolbar = false;
        }
        this.activeDropdown = null;
        this.activeTextToolbarOption = null;
        this.changeDetectorRef.markForCheck();
      });
    });

    // Register callback for pen drawing selection
    this.chartService.setOnPenDrawingSelectedCallback((drawing: PenDrawing | null, x?: number, y?: number) => {
      this.ngZone.run(() => {
        this.selectedPenDrawing = drawing;
        if (drawing && x !== undefined && y !== undefined) {
          // Position pen toolbar
          this.positionPenToolbarSafely(x, y);
          this.showPenToolbar = true;
          // Refresh icons after toolbar is shown
          this.refreshLucideIcons();
        } else {
          this.showPenToolbar = false;
        }
        this.activeDropdown = null;
        this.activePenToolbarOption = null;
        this.changeDetectorRef.markForCheck();
      });
    });

    this.chartService.setOnArrowSelectedCallback((drawing: ArrowDrawing | null, x?: number, y?: number) => {
      this.ngZone.run(() => {
        this.selectedArrowDrawing = drawing;
        if (drawing && x !== undefined && y !== undefined) {
          this.positionArrowToolbarSafely(x, y);
          this.showArrowToolbar = true;
          this.refreshLucideIcons();
        } else {
          this.showArrowToolbar = false;
        }
        this.activeDropdown = null;
        this.activeArrowToolbarOption = null;
        this.changeDetectorRef.markForCheck();
      });
    });

    this.chartService.setOnVisibleLogicalRangeChangedCallback((range: any) => {
      this.ngZone.run(() => {
        this.handleVisibleLogicalRangeChange(range);
      });
    });

    // Add document click listener
    if (this.documentClickListener) {
      document.addEventListener('click', this.documentClickListener);
    }
  }

  /**
   * Subscribe to socket data streams ONCE. These are RxJS subjects that buffer
   * and emit regardless of connection state, so subscribing once avoids
   * duplicate handlers on reconnection.
   */
  private setupSocketDataListeners(): void {
    // Listen to initial chart data (historical data)
    this.socketDataService.chartData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        if (!data) return;
        console.log('[ChartComponent] chart:data received');

        if (data.status === 'fetching-data') {
          const inlineCandles = this.getSocketCandleArray(data);
          // Cached/partial payloads can still include candles with this status.
          // Only wait when there is nothing to draw.
          if (inlineCandles.length === 0) {
            // For left-pagination, do not show full-screen loader; just wait for data payload.
            if (this.historyPageRequestInFlight) {
              // Backend acknowledged pagination request; keep request lock active.
              // Refresh timeout so we don't fire a duplicate request while backend is still fetching.
              if (this.historyRequestTimeout) {
                clearTimeout(this.historyRequestTimeout);
              }
              this.historyRequestTimeout = setTimeout(() => {
                if (this.historyPageRequestInFlight) {
                  this.clearHistoryRequestState();
                  this.lastHistoryRequestTo = null;
                  this.changeDetectorRef.markForCheck();
                }
              }, 12000);
              return;
            }
            console.log('[ChartComponent] Data is being fetched, keeping loading state');
            this.isLoading = true;
            this.scheduleHistoricalDataRetry();
            this.changeDetectorRef.markForCheck();
            return;
          }
        }

        const dataArray = this.getSocketCandleArray(data);
        const hasData = dataArray.length > 0;

        // Pagination response via existing chart:data socket event (no new socket/event name).
        if (this.historyPageRequestInFlight) {
          const isWarmupRequest = this.warmupFetchInFlight;
          this.clearHistoryRequestState();
          this.warmupFetchInFlight = false;
          if (!hasData) {
            if (isWarmupRequest) {
              this.warmupFetchExhausted = true;
              this.isLoading = false;
            } else {
              this.historyPaginationExhausted = true;
            }
            this.changeDetectorRef.markForCheck();
            return;
          }

          const oldestBeforeMerge = this.chartService.getOldestLoadedCandleTimeSec();
          const addedBars = this.chartService.prependHistoricalDataFromSocket(data);
          const oldestAfterMerge = this.chartService.getOldestLoadedCandleTimeSec();
          const responseOldestTime = this.getOldestTimeFromSocketPayload(data);
          const progressedLeft =
            oldestBeforeMerge !== null &&
            oldestAfterMerge !== null &&
            oldestAfterMerge < oldestBeforeMerge;

          if (progressedLeft || addedBars > 0) {
            this.nextHistoryToCursor = null;
          } else {
            const fallbackNextTo = typeof responseOldestTime === 'number'
              ? Math.floor(responseOldestTime - 1)
              : null;
            if (fallbackNextTo && fallbackNextTo > 0) {
              this.nextHistoryToCursor = fallbackNextTo;
              this.lastHistoryRequestTo = null;
            } else {
              this.lastHistoryRequestTo = null;
            }
          }

          if (isWarmupRequest) {
            if (this.indicators?.length) {
              this.chartService.syncIndicators(this.indicators);
            }
            this.chartService.reapplyVisibleTimeRange(this.currentInterval);

            const maxLookback = this.indicatorService.getMaxLookbackPeriod();
            const warmupComplete = this.chartService.hasSufficientIndicatorWarmup(maxLookback, this.currentInterval);
            if (warmupComplete || addedBars === 0) {
              if (addedBars === 0) {
                this.warmupFetchExhausted = true;
              }
              this.isLoading = false;
              this.changeDetectorRef.markForCheck();
            } else {
              setTimeout(() => this.ensureIndicatorWarmupHistory(), 0);
            }
            return;
          }

          // One request at a time. Next request should be user-scroll driven when
          // they reach the next left boundary, not auto-chained here.
          [0, 200, 500].forEach(delay =>
            setTimeout(() => this.chartService.redrawPenDrawings(), delay)
          );
          this.changeDetectorRef.markForCheck();
          return;
        }

        const symbol = (data.symbol || this.currentSymbol || '').toUpperCase();
        const interval = data.interval || this.currentEffectiveInterval;
        // Use payload range if present; otherwise fall back to pendingRange (NOT currentInterval,
        // which may have been asynchronously changed by loadChartSettings).
        const payloadRange = data.range ?? data.timeframe ?? data.timeRange;
        const range = payloadRange || this.pendingRange || this.currentInterval;

        // Reject payloads for a ticker the user already navigated away from
        if (symbol && this.currentSymbol && symbol !== this.currentSymbol) {
          console.warn(`[ChartComponent] Dropped chartData$ for ${symbol} — current symbol is ${this.currentSymbol}`);
          return;
        }

        // Reject stale data — only compare range when the payload explicitly includes one
        const symbolMismatch = !!this.pendingSymbol && !!symbol && symbol !== this.pendingSymbol;
        const rangeMismatch = !!payloadRange && !!this.pendingRange && payloadRange !== this.pendingRange;
        if (symbolMismatch || rangeMismatch) {
          console.warn(`[ChartComponent] Dropped stale chartData$ — got ${symbol}/${payloadRange}, expecting ${this.pendingSymbol}/${this.pendingRange}`);
          return;
        }

        this.ngZone.run(() => {
          if (hasData && this.isSparseInitialHistory(dataArray.length, range)) {
            // Backend often returns the live 5-bar snapshot as chart:data (mongodb cache)
            // before full YTD history. Painting it stretches those candles across the range.
            console.log(
              `[ChartComponent] Sparse chart:data (${dataArray.length} candles) for ${range} — waiting for full historical payload`
            );
            this.pendingSnapshotPayload = data;
            this.isLoading = true;
            this.scheduleHistoricalDataRetry();
            this.changeDetectorRef.markForCheck();
            return;
          }

          if (hasData) {
            this.clearHistoricalLoadWatchers();
            this.pendingSnapshotPayload = null;
            this.chartService.setChartDataFromSocket(data, symbol, range);
            this.dataReceivedForCurrentRequest = true;
            if (symbol) this.currentSymbol = symbol;
            if (range) this.currentInterval = range;
            const candleIv = (interval || this.currentEffectiveInterval || '').toString();
            if (!this.historyPageRequestInFlight) {
              this.rememberChartSocketPayload(data, symbol, range, candleIv);
            }
            [0, 200, 500, 1000, 2000].forEach(delay =>
              setTimeout(() => this.chartService.redrawPenDrawings(), delay)
            );
          } else {
            console.log('[ChartComponent] Empty data array, waiting for actual data');
            this.isLoading = true;
            this.scheduleHistoricalDataRetry();
            this.changeDetectorRef.markForCheck();
            return;
          }

          // Handle drawings from payload (only after accepting full history)
          const chartDrawings = data.drawings ?? data.chartDrawings ?? data.data?.chartDrawings ?? data.data?.drawings ?? [];
          if (Array.isArray(chartDrawings) && chartDrawings.length > 0) {
            this.drawingsReceivedFromSocket = true;
            this.drawingsFetchPending = false;
            this.isLoadingDrawingsFromApi = true;
            this.chartService.loadDrawingsFromChartDrawingApi(chartDrawings, true);
            this.setDrawingToolStateFromDrawings(chartDrawings);
            this.updateDrawingState();
            setTimeout(() => { this.isLoadingDrawingsFromApi = false; }, 200);
            [200, 500, 1000, 2000, 3000].forEach(delay =>
              setTimeout(() => this.chartService.redrawPenDrawings(), delay)
            );
          } else {
            this.drawingsReceivedFromSocket = false;
            this.drawingsFetchPending = false;
            setTimeout(() => this.fetchChartDrawings(), 400);
          }

          // Failsafe: force loader off after 2s if chartRendered$ hasn't fired
          setTimeout(() => {
            if (this.isLoading && this.dataReceivedForCurrentRequest &&
                !this.warmupFetchInFlight && !this.historyPageRequestInFlight) {
              console.log('[ChartComponent] Render failsafe triggered, hiding loader');
              this.isLoading = false;
              this.changeDetectorRef.markForCheck();
            }
          }, 2000);

          this.changeDetectorRef.markForCheck();
        });
      });

    // Listen to real-time chart updates (from backend cron or live feed)
    this.socketDataService.chartUpdate$
      .pipe(takeUntil(this.destroy$))
      .subscribe(updateData => {
        if (!updateData) return;
        console.log('[ChartComponent] chart:update received');

        const dataArray = this.getSocketCandleArray(updateData);
        const hasData = dataArray.length > 0;

        if (!hasData) {
          this.changeDetectorRef.markForCheck();
          return;
        }

        const symbol = (updateData.symbol || this.currentSymbol || '').toUpperCase();
        const payloadRange = updateData.range ?? updateData.timeframe ?? updateData.timeRange;
        const range = payloadRange
          ? (this.normalizeTimeRangeValue(payloadRange) || payloadRange)
          : (this.pendingRange || this.currentInterval);

        if (symbol && this.currentSymbol && symbol !== this.currentSymbol) {
          console.warn(`[ChartComponent] Dropped chartUpdate$ for ${symbol} — current symbol is ${this.currentSymbol}`);
          this.changeDetectorRef.markForCheck();
          return;
        }

        // Reject stale data — only compare range when the payload explicitly includes one
        const symbolMismatch = !!this.pendingSymbol && !!symbol && symbol !== this.pendingSymbol;
        const rangeMismatch = !!payloadRange && !!this.pendingRange &&
          (this.normalizeTimeRangeValue(payloadRange) || payloadRange) !== this.normalizeTimeRangeValue(this.pendingRange);
        if (symbolMismatch || rangeMismatch) {
          console.warn(`[ChartComponent] Dropped stale chartUpdate$ — got ${symbol}/${payloadRange}, expecting ${this.pendingSymbol}/${this.pendingRange}`);
          this.changeDetectorRef.markForCheck();
          return;
        }

        const isSnapshot = this.isSparseInitialHistory(dataArray.length, range);
        const isInitialData = this.isLoading && !this.dataReceivedForCurrentRequest && hasData && !isSnapshot;

        this.ngZone.run(() => {
          if (isInitialData) {
            console.log(`[ChartComponent] Initial data via chart:update (${dataArray.length} candles)`);
            this.clearHistoricalLoadWatchers();
            this.pendingSnapshotPayload = null;
            this.chartService.setChartDataFromSocket(updateData, symbol, range);
            if (symbol) this.currentSymbol = symbol;
            if (range) this.currentInterval = range;
            this.dataReceivedForCurrentRequest = true;

            const chartDrawings = updateData.drawings ?? updateData.chartDrawings ??
              updateData.data?.chartDrawings ?? updateData.data?.drawings ?? [];
            if (Array.isArray(chartDrawings) && chartDrawings.length > 0) {
              this.drawingsReceivedFromSocket = true;
              this.drawingsFetchPending = false;
              this.isLoadingDrawingsFromApi = true;
              this.chartService.loadDrawingsFromChartDrawingApi(chartDrawings, true);
              this.setDrawingToolStateFromDrawings(chartDrawings);
              this.updateDrawingState();
              setTimeout(() => { this.isLoadingDrawingsFromApi = false; }, 200);
              [200, 500, 1000, 2000, 3000].forEach(delay =>
                setTimeout(() => this.chartService.redrawPenDrawings(), delay)
              );
            } else {
              this.drawingsReceivedFromSocket = false;
              this.drawingsFetchPending = false;
              setTimeout(() => this.fetchChartDrawings(), 400);
            }

            [0, 200, 500, 1000, 2000].forEach(delay =>
              setTimeout(() => this.chartService.redrawPenDrawings(), delay)
            );

            setTimeout(() => {
              if (this.isLoading && this.dataReceivedForCurrentRequest) {
                console.log('[ChartComponent] chart:update render failsafe, hiding loader');
                this.isLoading = false;
                this.changeDetectorRef.markForCheck();
              }
            }, 2000);
          } else if (isSnapshot && this.isLoading && !this.dataReceivedForCurrentRequest) {
            // Live cron sends ~5 recent bars before historical chart:data.
            // Do not paint them — a 5-bar set fills the YTD viewport until history arrives.
            console.log(`[ChartComponent] Snapshot chart:update (${dataArray.length} candles) — waiting for historical chart:data`);
            this.pendingSnapshotPayload = updateData;
            this.scheduleHistoricalDataRetry();
          } else {
            this.chartService.updateChartDataFromSocket(updateData);
          }

          this.changeDetectorRef.markForCheck();
        });
      });
  }

  /** Minimum bars expected for a time range before treating history as complete. */
  private getMinBarsForInitialHistory(range?: string): number {
    const r = (this.normalizeTimeRangeValue(range) || range || '').toLowerCase();
    switch (r) {
      case '1d':
        return 1;
      case '5d':
      case '1w':
        return 3;
      case '1m':
        return 10;
      case '3m':
        return 20;
      case '6m':
      case 'ytd':
      case '1y':
        return 40;
      case '5y':
      case 'all':
        return 80;
      default:
        return 20;
    }
  }

  /** True when payload is too small to be full history for the selected range (e.g. 5-bar live snapshot). */
  private isSparseInitialHistory(candleCount: number, range?: string): boolean {
    if (candleCount <= 0) {
      return true;
    }
    return candleCount < this.getMinBarsForInitialHistory(range);
  }

  private getSocketCandleArray(payload: any): any[] {
    if (!payload) {
      return [];
    }
    const dataArray = payload.data ?? payload.candles ?? payload.ohlc ?? payload.series ?? payload;
    return Array.isArray(dataArray) ? dataArray : [];
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    this.performCleanup();
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(event: Event): void {
    this.syncIndicatorsPanelCollapseWithViewport();
    this.throttledResize('window');
  }

  private markChartInteracted(): void {
    this.userChartInteractedAtMs = Date.now();
    this.userHasChartInteracted = true;
  }

  private isEventFromChartContainer(target: any): boolean {
    try {
      const el = this.chartContainer?.nativeElement;
      return !!el && target instanceof Node && el.contains(target);
    } catch (_) {
      return false;
    }
  }

  @HostListener('wheel', ['$event'])
  onChartWheel(event: WheelEvent): void {
    if (!event) return;
    if (this.isEventFromChartContainer(event.target)) {
      this.markChartInteracted();
    }
  }

  @HostListener('pointerdown', ['$event'])
  onChartPointerDown(event: PointerEvent): void {
    if (!event) return;
    if (this.isEventFromChartContainer(event.target)) {
      this.markChartInteracted();
    }
  }

  @HostListener('touchstart', ['$event'])
  onChartTouchStart(event: TouchEvent): void {
    if (!event) return;
    const firstTouch = event.touches?.[0];
    if (!firstTouch) return;
    // touch events don't always populate a usable target; fallback to elementFromPoint.
    const elAtPoint = document.elementFromPoint(firstTouch.clientX, firstTouch.clientY);
    if (this.isEventFromChartContainer(elAtPoint)) {
      this.markChartInteracted();
    }
  }

  private throttledResize(source: string): void {
    const now = Date.now();
    const timeSinceLastResize = now - this.lastResizeTime;

    // Clear any pending resize
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    // If it's been less than 100ms since last resize, throttle it
    if (timeSinceLastResize < 100) {
      this.resizeTimeout = setTimeout(() => {
        this.performResize(source);
      }, 100);
    } else {
      this.performResize(source);
    }
  }

  private performResize(source: string): void {
    const containerElement = this.chartContainer?.nativeElement;
    if (containerElement) {
      const width = containerElement.clientWidth;
      const height = containerElement.clientHeight;

      if (width > 0 && height > 0) {
        this.lastResizeTime = Date.now();
        // Only log resize events occasionally to reduce console spam
        if (Math.random() < 0.1) { // Log ~10% of resize events
          console.log(`${source} resize - updating chart to:`, width, 'x', height);
        }
        this.chartService.resizeChart(width, height);
      }
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    // ESC key to exit current mode
    if (event.key === 'Escape') {
      if (this.showEditWatchlistModal) {
        this.closeEditWatchlistModal();
        event.preventDefault();
      } else if (this.showAddWatchlistSymbolModal) {
        this.closeAddWatchlistSymbolModal();
        event.preventDefault();
      } else if (this.showCreateWatchlist) {
        // Close create watchlist modal first
        this.cancelCreateWatchlist();
        event.preventDefault();
      } else if (this.showWatchlistSelector || this.showWatchlistMenu) {
        // Close watchlist dropdowns
        this.showWatchlistSelector = false;
        this.showWatchlistMenu = false;
        this.changeDetectorRef.markForCheck();
        event.preventDefault();
      } else if (this.activeDropdown || this.activeToolbarOption || this.activeTextToolbarOption || this.activePenToolbarOption || this.activeArrowToolbarOption) {
        // If any dropdown is open, close it first
        this.activeDropdown = null;
        this.activeToolbarOption = null;
        this.activeTextToolbarOption = null;
        this.activePenToolbarOption = null;
        this.activeArrowToolbarOption = null;
        this.changeDetectorRef.markForCheck();
        event.preventDefault();
      } else if (this.showArrowToolbar && this.selectedArrowDrawing) {
        this.deselectArrowDrawing();
        event.preventDefault();
      } else if (this.showPenToolbar && this.selectedPenDrawing) {
        // If pen drawing editing is active, deselect pen drawing first
        this.deselectPenDrawing();
        event.preventDefault();
      } else if (this.showTextToolbar && this.selectedText) {
        // If text editing is active, deselect text first
        this.deselectText();
        event.preventDefault();
      } else if (this.showLineToolbar && this.selectedLine) {
        // If line editing is active, deselect line
        this.deselectLine();
        event.preventDefault();
      } else if (this.isTextMode) {
        // If text mode is active, exit text mode
        this.toggleTextMode();
        event.preventDefault();
      } else if (this.isPenMode) {
        // If pen mode is active, exit pen mode
        this.togglePenMode();
        event.preventDefault();
      } else if (this.isMeasuringMode) {
        // If measuring mode is active, exit measuring mode
        this.toggleMeasuringMode();
        event.preventDefault();
      } else if (this.isArrowMode) {
        // If arrow mode is active, exit arrow mode
        this.toggleArrowMode();
        event.preventDefault();
      } else if (this.isDrawingMode) {
        // If drawing mode is active, exit drawing mode
        this.toggleDrawingMode();
        event.preventDefault();
      }
    }
  }

  private performCleanup(): void {
    // Complete the destroy subject
    this.destroy$.next();
    this.destroy$.complete();

    if (this.popupClosePoll) {
      window.clearInterval(this.popupClosePoll);
      this.popupClosePoll = undefined;
    }

    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }

    if (this.saveDrawingsTimer) {
      clearTimeout(this.saveDrawingsTimer);
      this.saveDrawingsTimer = null;
    }
    if (this.settingsReadyFailsafeTimer) {
      clearTimeout(this.settingsReadyFailsafeTimer);
      this.settingsReadyFailsafeTimer = null;
    }
    this.clearHistoricalRetryTimer();
    this.clearLoadingTimeout();
    this.pendingSnapshotPayload = null;
    this.clearHistoryRequestState();
    this.chartSocketPayloadCache.clear();

    // Clean up resize timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = undefined;
    }

    // Clean up document click listener
    if (this.documentClickListener) {
      document.removeEventListener('click', this.documentClickListener);
      this.documentClickListener = undefined;
    }

    // Clean up chart service callbacks to prevent memory leaks
    this.chartService.setOnLinesChangedCallback(undefined);
    this.chartService.setOnTooltipChangedCallback(undefined);
    this.chartService.setOnMarkerTooltipChangedCallback(undefined);
    this.chartService.setOnATRTooltipChangedCallback(undefined);
    this.chartService.setOnLineSelectedCallback(undefined);
    this.chartService.setOnVisibleLogicalRangeChangedCallback(undefined);
    this.chartService.setOnTickerDetailsChangedCallback(undefined);

    // Destroy chart service
    this.chartService.destroy();

    // Clean up socket subscriptions
    if (this.socketConnectionService.isConnected()) {
      const unsubscribeSymbol = this.activeSocketSymbol || this.currentSymbol;
      const unsubscribeInterval = this.activeSocketInterval || this.currentEffectiveInterval || '1d';
      this.socketDataService.unsubscribeChart(unsubscribeSymbol, unsubscribeInterval);
      this.socketDataService.destroy();
    }

    // Clear references to prevent memory leaks
    this.horizontalLines = [];
    this.systemLines = [];
    this.tooltipData = null;
    this.markerTooltipData = null;
    this.atrTooltipData = null;
    this.selectedLine = null;
    this.tickerDetails = null;
    this.recentTickers = [];
  }

  private chartInitialized = false;

  private initializeChart(): void {
    if (this.chartInitialized) return;

    if (!this.chartContainer || !this.chartContainer.nativeElement) {
      console.error('Chart container not available');
      return;
    }

    const containerElement = this.chartContainer.nativeElement;
    if (!containerElement || typeof containerElement.clientWidth !== 'number') {
      console.error('Invalid chart container element');
      return;
    }

    console.log('Initializing chart container (symbol:', this.currentSymbol || 'pending', ')');

    // Wait for container to be properly sized by the layout
    setTimeout(() => {
      if (!this.chartContainer?.nativeElement) {
        console.error('Chart container became unavailable during initialization');
        return;
      }

      const containerElement = this.chartContainer.nativeElement;
      const containerWidth = containerElement.clientWidth || 800;
      const containerHeight = containerElement.clientHeight || 500;

      console.log('Container dimensions:', containerWidth, 'x', containerHeight);

      const chartOptions = {
        ...this.options,
        symbol: this.currentSymbol || 'LOADING',
        width: containerWidth,
        height: containerHeight
      };

      const chart = this.chartService.initializeChart(containerElement, chartOptions);

      if (!chart) {
        console.error('Failed to initialize chart');
        return;
      }

      this.chartInitialized = true;
      this.setupResponsiveChart();

      setTimeout(() => {
        if (this.chartContainer?.nativeElement) {
          this.chartService.resizeChart(containerElement.clientWidth, containerElement.clientHeight);
          console.log('Chart resized to:', containerElement.clientWidth, 'x', containerElement.clientHeight);
        }
      }, 100);

      console.log('Chart initialized successfully');

      this.chartService.applyTheme(this.currentTheme);
      this.chartService.changeSeriesType(this.currentSeriesType);

      // If we don't have a symbol yet, keep loading and wait for route/socket
      if (!this.currentSymbol) {
        this.isLoading = true;
        this.changeDetectorRef.markForCheck();
      }

      // If socket data already arrived before chart was ready, the service retry logic
      // will handle applying it via updateMainSeriesData
    }, 50);
  }

  private setupResponsiveChart(): void {
    // Clean up existing ResizeObserver if it exists
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // Create new ResizeObserver with proper cleanup
    this.resizeObserver = new ResizeObserver((entries) => {
      // Use throttled resize to prevent excessive calls
      this.throttledResize('observer');
    });

    // Observe the chart container
    if (this.chartContainer?.nativeElement) {
      this.resizeObserver.observe(this.chartContainer.nativeElement);
    }
  }

  async loadSymbol(): Promise<void> {
    if (!this.currentSymbol || typeof this.currentSymbol !== 'string' || this.currentSymbol.trim().length === 0) {
      console.warn('[WARNING] Invalid symbol provided to loadSymbol');
      return;
    }

    const symbol = this.currentSymbol.toUpperCase().trim();
    this.currentSymbol = symbol;

    // During initial load, DON'T request data yet — onSettingsReady() will fire after
    // loadChartSettings completes and will issue the first request with correct settings.
    if (!this.settingsLoaded) {
      console.log(`[ChartComponent] Deferring data request for ${symbol} until settings are loaded`);
      this.isLoading = true;
      this.changeDetectorRef.detectChanges();
      this.addToRecentTickers(symbol);
      return;
    }

    this.isLoading = true;
    this.dataReceivedForCurrentRequest = false;
    this.changeDetectorRef.detectChanges();

    try {
      await this.loadDataWithCurrentSettings();
      this.addToRecentTickers(symbol);

      setTimeout(() => {
        if (this.chartContainer?.nativeElement) {
          const containerElement = this.chartContainer.nativeElement;
          if (containerElement && typeof containerElement.clientWidth === 'number') {
            this.chartService.resizeChart(containerElement.clientWidth, containerElement.clientHeight);
          }
        }
      }, 300);

      console.log(`[SUCCESS] Chart loaded for ${symbol} with range: ${this.currentInterval}, interval: ${this.currentEffectiveInterval}`);
    } catch (error) {
      console.error('Error loading symbol:', error);
      this.isLoading = false;
    } finally {
      this.changeDetectorRef.markForCheck();
      if (this.isRecentTickersApiMode) {
        setTimeout(() => this.loadRecentTickersFromApi(), 0);
      }
      if (this.isFullscreenView) {
        setTimeout(() => void this.hydrateFullscreenQuoteExtras(), 500);
      }
    }
  }

  /** Merge exchange / session / pre-market fields from quote API (fullscreen strip only). */
  private async hydrateFullscreenQuoteExtras(): Promise<void> {
    if (!this.isFullscreenView || !this.currentSymbol?.trim()) {
      return;
    }
    if (this.fullscreenQuoteHydrateInFlight) {
      return;
    }
    this.fullscreenQuoteHydrateInFlight = true;
    try {
      const extra = await this.marketDataService.fetchExtendedQuoteData(this.currentSymbol);
      this.ngZone.run(() => {
        this.fullscreenQuoteHydrateInFlight = false;
        if (!extra || !this.tickerDetails) {
          this.changeDetectorRef.markForCheck();
          return;
        }
        this.tickerDetails = { ...this.tickerDetails, ...extra };
        this.changeDetectorRef.markForCheck();
      });
    } catch {
      this.fullscreenQuoteHydrateInFlight = false;
    }
  }

  /**
   * Change the time range interval (1d, 5d, 1m, etc.)
   * All data is loaded via socket connection
   */
  changeInterval(interval: string): void {
    const normalizedInterval = this.normalizeTimeRangeValue(interval) || this.currentInterval;

    // Force loading overlay visible IMMEDIATELY so old data is hidden
    this.isLoading = true;
    this.changeDetectorRef.detectChanges();

    this.currentInterval = normalizedInterval;
    console.log(`[UPDATE] Time range changed to ${normalizedInterval}`);

    // Clear drawings from previous timeframe; drawings for this range will load from API/socket
    this.clearDrawingsForTimeframeChange();
    this.drawingsFetchPending = true;
    this.drawingsReceivedFromSocket = false;

    // ALL range always uses 1d candles.
    if (normalizedInterval.toLowerCase() === 'all') {
      this.selectedInterval = '1d';
      this.userManuallySelectedInterval = false;
    }

    // Smart candle interval selection: use optimal interval for the time range
    // unless the user has manually selected a different interval recently
    const optimalInterval = this.getOptimalCandleInterval(normalizedInterval);

    // Validate that optimal interval is valid for this time range.
    const validIntervals = this.getValidIntervalsForTimeRange(normalizedInterval);

    // Prefer the range's optimal interval whenever it's valid; otherwise fall back to
    // the first valid interval for this range.
    const finalOptimalInterval = validIntervals.includes(optimalInterval)
      ? optimalInterval
      : (validIntervals.length > 0 ? validIntervals[0] : optimalInterval);

    // Only use user's manual selection if it's valid for this time range
    const userSelectedInterval = this.hasUserManuallySelectedInterval() && this.selectedInterval !== null
      ? this.selectedInterval
      : null;

    const candleInterval = (userSelectedInterval && validIntervals.includes(userSelectedInterval))
      ? userSelectedInterval
      : finalOptimalInterval;

    // Update selectedInterval to match what we're using (reset manual flag if using optimal)
    if (!this.hasUserManuallySelectedInterval() || candleInterval === finalOptimalInterval) {
      this.selectedInterval = candleInterval;
      this.userManuallySelectedInterval = false;
    }

    console.log(`[UPDATE] Using candle interval: ${candleInterval} (optimal: ${finalOptimalInterval}, user selected: ${userSelectedInterval}, valid intervals: ${validIntervals.join(', ')})`);

    // Request data from socket (loading state is managed in requestSocketChartData)
    this.requestSocketChartData(this.currentSymbol, candleInterval, normalizedInterval);
    console.log(`[SUCCESS] Chart data requested via socket for ${normalizedInterval} range with ${candleInterval} candles`);

    // Update URL with new interval and candle interval
    this.navigateWithQueryParams(undefined, normalizedInterval || undefined, undefined, undefined, undefined, undefined, candleInterval);

    // Persist timeframe and interval so user gets same view after refresh
    this.saveUserPreferences();

    // Ensure chart resizes properly after interval change
    setTimeout(() => {
      if (this.chartContainer?.nativeElement) {
        const containerElement = this.chartContainer.nativeElement;
        this.chartService.resizeChart(containerElement.clientWidth, containerElement.clientHeight);
      }
    }, 300);

    this.changeDetectorRef.markForCheck();
  }

  isActiveTimeRange(rangeValue: string): boolean {
    const normalizedCurrent = this.normalizeTimeRangeValue(this.currentInterval);
    const normalizedRange = this.normalizeTimeRangeValue(rangeValue);
    return !!normalizedCurrent && !!normalizedRange && normalizedCurrent === normalizedRange;
  }

  // Toggle interval dropdown
  toggleIntervalDropdown(): void {
    // Close other dropdowns when opening interval dropdown
    if (!this.showIntervalDropdown) {
      this.closeAllMenus();
    }
    this.showIntervalDropdown = !this.showIntervalDropdown;
    this.changeDetectorRef.markForCheck();
  }

  // Toggle watchlist selector dropdown
  toggleWatchlistSelector(): void {
    // Close other dropdowns when opening watchlist selector
    if (!this.showWatchlistSelector) {
      this.closeAllMenus();
      // Refresh API watchlists when opening selector
      this.loadApiWatchlists();
    }
    this.showWatchlistSelector = !this.showWatchlistSelector;
    this.changeDetectorRef.markForCheck();
  }

  // Toggle watchlist menu dropdown
  toggleWatchlistMenu(): void {
    // Close other dropdowns when opening watchlist menu
    if (!this.showWatchlistMenu) {
      this.closeAllMenus();
    }
    this.showWatchlistMenu = !this.showWatchlistMenu;
    this.changeDetectorRef.markForCheck();
  }

  isAnyDropdownOpen(): boolean {
    return !!(
      this.activeDropdown ||
      this.showScreenshotDropdown ||
      this.showWatchlistSelector ||
      this.showWatchlistMenu ||
      this.showIntervalDropdown ||
      this.showChartSettingsPopup ||
      this.showIndicatorSettings
    );
  }

  // Select manual interval
  selectInterval(interval: string | null): void {
    console.log('selectInterval', interval);
    // ALL range supports only 1d candles.
    if (this.currentInterval.toLowerCase() === 'all') {
      if (interval === null || interval.toLowerCase() !== '1d') {
        console.warn('[ChartComponent] Only 1d interval is allowed for ALL range');
        return;
      }
    }

    // If interval is not valid for current range, don't select it
    // Use the normalized check via isIntervalValidForCurrentRange
    if (interval !== null && !this.isIntervalValidForCurrentRange(interval)) {
      console.warn(`[ChartComponent] Interval ${interval} is not valid for current range ${this.currentInterval}`);
      // Ensure specific intervals are enabled for 5d range
      if (this.currentInterval.toLowerCase() === '5d' && ['5m', '15m', '30m', '1d'].includes(interval!.toLowerCase())) {
        console.log('[ChartComponent] Allowing valid 5d interval despite check failure (fallback)');
      } else {
        return;
      }
    }

    // Explicitly set loading state immediately for better UX
    this.isLoading = true;
    this.changeDetectorRef.markForCheck();

    this.selectedInterval = interval;
    this.userManuallySelectedInterval = true; // Track that user manually selected an interval
    this.showIntervalDropdown = false;
    this.changeDetectorRef.markForCheck();

    // Clear drawings from previous interval; drawings for this interval will load from API/socket
    this.clearDrawingsForTimeframeChange();
    this.drawingsFetchPending = true;
    this.drawingsReceivedFromSocket = false;

    // Update URL with new candle interval
    this.navigateWithQueryParams(undefined, undefined, undefined, undefined, undefined, undefined, interval || 'auto');

    // Persist timeframe and interval so user gets same view after refresh
    this.saveUserPreferences();

    // Reload data with new interval
    this.loadDataWithCurrentSettings();
  }

  // Toggle chart settings dropdown
  toggleDropdown(dropdownName: string): void {
    if (dropdownName === 'chartSettings') {
      // For chart settings, close other menus but preserve chart settings state
      this.closeAllMenusExcept('chartSettings');

      this.showChartSettingsPopup = !this.showChartSettingsPopup;
    } else if (dropdownName === 'screenshot') {
      // Close all other menus first
      this.closeAllMenus();

      this.showScreenshotDropdown = !this.showScreenshotDropdown;
    } else {
      // If clicking the same dropdown, close it
      if (this.activeDropdown === dropdownName) {
        this.activeDropdown = null;
      } else {
        // Close all other menus first, then open the new one
        this.closeAllMenus();
        this.activeDropdown = dropdownName;

        // Refresh Lucide icons when opening dropdowns that contain dynamic icon content
        if (dropdownName === 'indicators' || dropdownName === 'drawing') {
          setTimeout(() => this.refreshLucideIcons(), 50);
        }
      }
    }

    this.changeDetectorRef.markForCheck();

    // Refresh icons after DOM update
    setTimeout(() => {
      this.refreshLucideIcons();
    }, 0);
  }

  // Close all open menus and dropdowns
  private closeAllMenus(): void {
    // Close dropdown menus
    this.activeDropdown = null;

    // Close popups and overlays
    this.showChartSettingsPopup = false;
    this.showIndicatorSettings = false;
    this.showScreenshotDropdown = false;

    // Close other menu states
    this.showWatchlistMenu = false;
    this.showWatchlistSelector = false;
    this.showIntervalDropdown = false;

    // Close toolbar states
    this.activeToolbarOption = null;
    this.activeTextToolbarOption = null;
    this.activePenToolbarOption = null;
    this.activeArrowToolbarOption = null;
  }

  /** Public wrapper: close menus after selecting an option. */
  closeMenusAfterAction(): void {
    this.closeAllMenus();
    this.changeDetectorRef.markForCheck();
  }

  // Close all open menus except the specified one
  private closeAllMenusExcept(exceptMenu: string): void {
    // Close dropdown menus
    this.activeDropdown = null;

    // Close popups and overlays (except the specified one)
    if (exceptMenu !== 'chartSettings') {
      this.showChartSettingsPopup = false;
    }
    if (exceptMenu !== 'indicatorSettings') {
      this.showIndicatorSettings = false;
    }
    if (exceptMenu !== 'screenshot') {
      this.showScreenshotDropdown = false;
    }

    // Close other menu states
    this.showWatchlistMenu = false;
    this.showWatchlistSelector = false;
    this.showIntervalDropdown = false;

    // Close toolbar states
    this.activeToolbarOption = null;
    this.activeTextToolbarOption = null;
    this.activePenToolbarOption = null;
    this.activeArrowToolbarOption = null;
  }

  // Chart settings methods
  setCrosshairMode(mode: CrosshairMode): void {
    this.crosshairMode = mode;
    this.chartService.setCrosshairMode(mode);

    // Update URL with new crosshair mode
    this.navigateWithQueryParams(undefined, undefined, undefined, undefined, undefined, undefined, undefined, mode);

    // Save user preferences
    this.saveUserPreferences();

    this.changeDetectorRef.markForCheck();
  }

  getCrosshairMode(): CrosshairMode {
    return this.crosshairMode;
  }

  // Screenshot functionality
  async downloadChartImage(): Promise<void> {
    try {
      console.log('📸 Taking screenshot for download...');

      // Close the dropdown first
      this.showScreenshotDropdown = false;
      this.changeDetectorRef.markForCheck();

      // Wait a moment for the dropdown to close
      await new Promise(resolve => setTimeout(resolve, 100));

      // Take screenshot using html2canvas
      const canvas = await this.captureChartAsCanvas();
      if (!canvas) {
        console.error('❌ Failed to capture chart as canvas');
        return;
      }

      const blob = await this.canvasToBlob(canvas);
      if (!blob) {
        console.error('❌ Failed to create blob from canvas');
        return;
      }

      const fileName = `chart-${this.currentSymbol}-${new Date().toISOString().slice(0, 10)}.png`;
      this.downloadBlob(blob, fileName);
      console.log('✅ Chart image downloaded successfully');

    } catch (error) {
      console.error('❌ Error downloading chart image:', error);
    }
  }

  async copyChartImage(): Promise<void> {
    try {
      console.log('📸 Taking screenshot for clipboard...');

      // Close the dropdown first
      this.showScreenshotDropdown = false;
      this.changeDetectorRef.markForCheck();

      // Wait a moment for the dropdown to close
      await new Promise(resolve => setTimeout(resolve, 100));

      // Take screenshot
      const canvas = await this.captureChartAsCanvas();
      if (!canvas) {
        console.error('❌ Failed to capture chart as canvas');
        return;
      }

      const base64Image = canvas.toDataURL('image/png');
      const blob = await this.canvasToBlob(canvas);
      if (!blob) {
        console.error('❌ Failed to create blob from canvas');
        return;
      }

      try {
        const clipboardItemCtor = (window as any).ClipboardItem;
        if (navigator.clipboard && typeof navigator.clipboard.write === 'function' && clipboardItemCtor) {
          try {
            await navigator.clipboard.write([
              new clipboardItemCtor({ 'image/png': blob })
            ]);
            console.log('✅ Chart image copied to system clipboard successfully');
          } catch (clipboardError) {
            console.warn('⚠️ Clipboard API write failed, attempting legacy execCommand fallback...', clipboardError);

            try {
              const img = document.createElement('img');
              img.src = base64Image;

              const div = document.createElement('div');
              div.contentEditable = 'true';
              div.appendChild(img);
              div.style.position = 'fixed';
              div.style.left = '-9999px';
              div.style.top = '-9999px';
              document.body.appendChild(div);

              const selection = window.getSelection();
              const range = document.createRange();
              range.selectNodeContents(div);
              selection?.removeAllRanges();
              selection?.addRange(range);

              const success = document.execCommand('copy');

              selection?.removeAllRanges();
              document.body.removeChild(div);

              if (success) {
                console.log('✅ Chart image copied to clipboard via legacy fallback');
              } else {
                throw new Error('execCommand returned false');
              }
            } catch (legacyError) {
              console.error('❌ Legacy fallback also failed. Browser completely blocking copy:', legacyError);
              this.copyImageAsDataUrl(canvas);
            }
          }
        } else {
          console.warn('⚠️ Clipboard API unavailable, attempting data URL fallback.');
          this.copyImageAsDataUrl(canvas);
        }
      } catch (error) {
        console.error('❌ Error in clipboard process:', error);
      }

    } catch (error) {
      console.error('❌ Error copying chart image:', error);
    }
  }

  private async captureChartAsCanvas(): Promise<HTMLCanvasElement | null> {
    try {
      const chartContainer = this.chartContainer?.nativeElement;
      if (!chartContainer) {
        console.error('❌ Chart container not found');
        return null;
      }

      // Capture the visible chart app region (toolbar + chart) without oversized scroll padding.
      const targetElement = (chartContainer.closest('.chart-app-container') as HTMLElement) || chartContainer;
      const rect = targetElement.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      const scale = Math.min(2, Math.max(1, window.devicePixelRatio || 1));

      // Use html2canvas with viewport bounds to avoid extra whitespace from scrollWidth/scrollHeight.
      const canvas = await html2canvas(targetElement, {
        backgroundColor: this.currentTheme === 'dark' ? '#1a1a1a' : '#ffffff',
        scale,
        useCORS: true,
        allowTaint: true,
        logging: false,
        width,
        height,
        x: Math.round(rect.left + window.scrollX),
        y: Math.round(rect.top + window.scrollY),
        scrollX: -window.scrollX,
        scrollY: -window.scrollY,
        windowWidth: document.documentElement.clientWidth,
        windowHeight: document.documentElement.clientHeight
      });

      return canvas;
    } catch (error) {
      console.error('❌ Error capturing chart:', error);
      return null;
    }
  }

  private async canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
    if (typeof canvas.toBlob === 'function') {
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/png', 1.0);
      });
      if (blob) return blob;
    }

    // Fallback for environments where toBlob is missing or returns null.
    try {
      const dataUrl = canvas.toDataURL('image/png');
      return this.dataUrlToBlob(dataUrl);
    } catch (error) {
      console.error('❌ Failed to convert canvas to blob via fallback:', error);
      return null;
    }
  }

  private dataUrlToBlob(dataUrl: string): Blob | null {
    try {
      const parts = dataUrl.split(',');
      if (parts.length < 2) return null;
      const mimeMatch = parts[0].match(/:(.*?);/);
      const mimeType = mimeMatch?.[1] || 'image/png';
      const binary = atob(parts[1]);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new Blob([bytes], { type: mimeType });
    } catch (error) {
      console.error('❌ Failed to convert data URL to blob:', error);
      return null;
    }
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.warn('⚠️ Native download click failed, opening image in new tab fallback.', error);
      window.open(url, '_blank', 'noopener');
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  }

  private addWatermarkToCanvas(canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('❌ Could not get canvas context');
      return;
    }

    // Get current date and time
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    const timeStr = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    // Get current timeframe info
    const timeframeInfo = this.getCurrentTimeframeInfo();
    const currentSeriesType = this.chartService.getCurrentSeriesType();

    // Set up watermark styling
    const isDark = this.currentTheme === 'dark';
    const bgColor = isDark ? 'rgba(0, 0, 0, 0.85)' : 'rgba(255, 255, 255, 0.95)';
    const textColor = isDark ? '#ffffff' : '#1a1a1a';
    const accentColor = isDark ? '#3b82f6' : '#2563eb';
    const secondaryColor = isDark ? '#94a3b8' : '#64748b';

    // Calculate watermark position (upper left with padding)
    const padding = 20;
    const watermarkWidth = 220;
    const watermarkHeight = 75; // More compact since date/time are on same line
    const x = padding;
    const y = padding;

    // Draw watermark background with rounded corners
    this.drawRoundedRect(ctx, x, y, watermarkWidth, watermarkHeight, 8);
    ctx.fillStyle = bgColor;
    ctx.fill();

    // Draw border
    ctx.strokeStyle = accentColor;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Set text properties for ticker symbol
    ctx.font = 'bold 26px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillStyle = accentColor;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    // Draw ticker symbol
    ctx.fillText(this.currentSymbol, x + 15, y + 12);

    // Draw timeframe and chart type info
    ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillStyle = secondaryColor;
    ctx.fillText(`${timeframeInfo} • ${currentSeriesType.toUpperCase()}`, x + 15, y + 38);

    // Draw date and time on the same line
    ctx.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillStyle = textColor;
    ctx.fillText(`${dateStr} ${timeStr}`, x + 15, y + 54);

    console.log(`✅ Watermark added: ${this.currentSymbol} - ${timeframeInfo} - ${dateStr} ${timeStr}`);
  }

  private getCurrentTimeframeInfo(): string {
    // Map current interval to readable format
    const intervalMap: Record<string, string> = {
      '1d': '1 Day',
      '5d': '5 Days',
      '1m': '1 Month',
      '6m': '6 Months',
      'ytd': 'YTD',
      '1y': '1 Year',
      '5y': '5 Years',
      'all': 'All Time'
    };

    return intervalMap[this.currentInterval] || this.currentInterval.toUpperCase();
  }

  private drawRoundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }

  private copyImageAsDataUrl(canvas: HTMLCanvasElement): void {
    try {
      const dataUrl = canvas.toDataURL('image/png');
      const textArea = document.createElement('textarea');
      textArea.value = dataUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      console.log('✅ Chart image copied as data URL (fallback)');
    } catch (error) {
      console.error('❌ Error copying as data URL:', error);
    }
  }

  toggleVolume(): void {
    this.showVolume = !this.showVolume;
    this.chartService.setVolumeVisible(this.showVolume);

    // Update URL with new volume setting
    this.navigateWithQueryParams(undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, this.showVolume);

    // Save user preferences
    this.saveUserPreferences();

    this.changeDetectorRef.markForCheck();
  }

  toggleGridLines(): void {
    this.showGridLines = !this.showGridLines;
    this.chartService.setGridLinesVisible(this.showGridLines);

    // Update URL with new grid lines setting
    this.navigateWithQueryParams(undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, this.showGridLines);

    // Save user preferences
    this.saveUserPreferences();

    this.changeDetectorRef.markForCheck();
  }

  togglePriceLine(): void {
    this.showPriceLine = !this.showPriceLine;
    this.chartService.setPriceLineVisible(this.showPriceLine);

    // Update URL with new price line setting
    this.navigateWithQueryParams(undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, this.showPriceLine);

    // Save user preferences
    this.saveUserPreferences();

    this.changeDetectorRef.markForCheck();
  }

  toggleTheme(): void {
    const wasSettingsPopupOpen = this.showChartSettingsPopup;
    this.currentTheme = this.currentTheme === 'dark' ? 'light' : 'dark';

    this.applyTheme(this.currentTheme);
    this.navigateWithQueryParams(undefined, undefined, this.currentTheme);
    this.saveUserPreferences();

    if (wasSettingsPopupOpen) {
      this.showChartSettingsPopup = true;
    }

    this.changeDetectorRef.markForCheck();
  }

  private applyTheme(theme: 'light' | 'dark'): void {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);

    if (theme === 'light') {
      // Light theme colors
      root.style.setProperty('--text-inverse', '#ffffff');
      root.style.setProperty('--bg-primary', '#ffffff');
      root.style.setProperty('--bg-secondary', '#f8f9fa');
      root.style.setProperty('--bg-tertiary', '#e9ecef');
      root.style.setProperty('--text-primary', '#212529');
      root.style.setProperty('--text-secondary', '#6c757d');
      root.style.setProperty('--text-tertiary', '#9ca3af');
      root.style.setProperty('--border-color', '#dee2e6');
      root.style.setProperty('--primary-100', '#e3f2fd');
      root.style.setProperty('--primary-600', '#1976d2');
      root.style.setProperty('--primary-700', '#1565c0');
      root.style.setProperty('--dialog-scrim', 'rgba(15, 23, 42, 0.55)');
      root.style.setProperty('--shadow-dialog', '0 8px 32px rgba(15, 23, 42, 0.12)');
      root.style.setProperty('--chart-tooltip-surface', 'rgba(255, 255, 255, 0.94)');
      root.style.setProperty('--chart-tooltip-border', 'rgba(148, 163, 184, 0.5)');
      root.style.setProperty('--toolbar-tooltip-bg', '#1e293b');
      root.style.setProperty('--toolbar-tooltip-fg', '#f8fafc');
      root.style.setProperty('--toolbar-bg', '#e8eaed');
      root.style.setProperty('--toolbar-search-bg', '#ffffff');
      root.style.setProperty('--toolbar-search-border', '#d1d5db');
      root.style.setProperty('--toolbar-border', 'rgba(15, 23, 42, 0.1)');
      root.style.setProperty('--brand-insider-green', '#72be3a');
    } else {
      // Dark theme colors (default)
      root.style.setProperty('--text-inverse', '#ffffff');
      root.style.setProperty('--bg-primary', '#1a1a1a');
      root.style.setProperty('--bg-secondary', '#2d2d2d');
      root.style.setProperty('--bg-tertiary', '#404040');
      root.style.setProperty('--text-primary', '#f3f4f6');
      root.style.setProperty('--text-secondary', '#cbd5e1');
      root.style.setProperty('--text-tertiary', '#94a3b8');
      root.style.setProperty('--border-color', '#525252');
      root.style.setProperty('--primary-100', '#1e3a8a');
      root.style.setProperty('--primary-600', '#3b82f6');
      root.style.setProperty('--primary-700', '#2563eb');
      root.style.setProperty('--dialog-scrim', 'rgba(0, 0, 0, 0.65)');
      root.style.setProperty('--shadow-dialog', '0 12px 40px rgba(0, 0, 0, 0.55)');
      root.style.setProperty('--chart-tooltip-surface', 'rgba(26, 26, 26, 0.94)');
      root.style.setProperty('--chart-tooltip-border', 'rgba(82, 82, 82, 0.9)');
      root.style.setProperty('--toolbar-tooltip-bg', '#334155');
      root.style.setProperty('--toolbar-tooltip-fg', '#f8fafc');
      root.style.setProperty('--toolbar-bg', '#2d2d32');
      root.style.setProperty('--toolbar-search-bg', '#3c3c42');
      root.style.setProperty('--toolbar-search-border', '#4a4a52');
      root.style.setProperty('--toolbar-border', 'rgba(255, 255, 255, 0.08)');
      root.style.setProperty('--brand-insider-green', '#72be3a');
    }

    // Apply theme to chart
    this.chartService.applyTheme(theme);
  }

  // Check if interval is valid for current range (dynamic from API or fallback)
  isIntervalValidForCurrentRange(interval: string): boolean {
    // 1. Check our custom restrictions first (prioritize requested UI behavior)
    // Normalize to lowercase for case-insensitive matching
    const normalizedRange = this.currentInterval.toLowerCase();
    const normalizedInterval = interval.toLowerCase();

    if (this.TIME_RANGE_TO_VALID_INTERVALS[normalizedRange]) {
      return this.TIME_RANGE_TO_VALID_INTERVALS[normalizedRange].includes(normalizedInterval);
    }

    // 2. Try to get from dynamic API data (time ranges)
    if (this.timeRangesLoaded && this.timeRanges.length > 0) {
      const timeRangeData = this.timeRanges.find(r => r.value.toLowerCase() === normalizedRange);
      if (timeRangeData?.validIntervals) {
        return timeRangeData.validIntervals.some(i => i.toLowerCase() === normalizedInterval);
      }
    }

    // 3. Try from intervals data (chart intervals)
    if (this.timeRangesLoaded && this.chartIntervals.length > 0) {
      const intervalData = this.chartIntervals.find(i => i.value.toLowerCase() === normalizedInterval);
      if (intervalData?.validRanges) {
        return intervalData.validRanges.some(r => r.toLowerCase() === normalizedRange);
      }
    }

    // 4. Fallback to MarketDataService
    return this.marketDataService.isValidIntervalRange(interval, this.currentInterval);
  }

  /** Reset selected candle interval when it is not allowed for the active time range. */
  private ensureSelectedIntervalValidForCurrentRange(): void {
    if (this.selectedInterval === null) {
      return;
    }
    if (this.isIntervalValidForCurrentRange(this.selectedInterval)) {
      return;
    }

    const validIntervals = this.getValidIntervalsForTimeRange(this.currentInterval);
    const optimal = this.getOptimalCandleInterval(this.currentInterval);
    this.selectedInterval = validIntervals.includes(optimal)
      ? optimal
      : (validIntervals[0] || '1d');
    this.userManuallySelectedInterval = false;
  }

  private chartSocketCacheKey(symbol: string, timeRange: string, candleInterval: string): string {
    return `${(symbol || '').toUpperCase()}|${timeRange}|${candleInterval || ''}`;
  }

  private rememberChartSocketPayload(payload: any, symbol: string, timeRange: string, candleInterval: string): void {
    if (!payload || !symbol || !timeRange) {
      return;
    }
    const key = this.chartSocketCacheKey(symbol, timeRange, candleInterval);
    this.chartSocketPayloadCache.set(key, { payload, storedAt: Date.now() });
    while (this.chartSocketPayloadCache.size > this.chartSocketCacheMaxEntries) {
      let oldestKey: string | null = null;
      let oldestAt = Infinity;
      for (const [k, v] of this.chartSocketPayloadCache.entries()) {
        if (v.storedAt < oldestAt) {
          oldestAt = v.storedAt;
          oldestKey = k;
        }
      }
      if (oldestKey) {
        this.chartSocketPayloadCache.delete(oldestKey);
      } else {
        break;
      }
    }
  }

  /**
   * Request chart data from socket
   * This is the primary method for loading chart data - all data comes from socket
   */
  private requestSocketChartData(symbol: string, interval: string, range: string, unsubscribePrevious: boolean = true): void {
    const trimmedSymbol = (symbol || '').trim();
    if (!trimmedSymbol) {
      console.warn('[WARNING] Cannot request chart data with empty symbol');
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
      return;
    }

    // Capture the currently active socket target before changing request state.
    this.previousSymbol = this.activeSocketSymbol || '';
    this.previousInterval = this.activeSocketInterval || '';
    this.previousRange = this.activeSocketRange || '';

    // Track what we are requesting so socket subscribers can reject stale responses
    this.pendingSymbol = trimmedSymbol.toUpperCase();
    this.pendingRange = range;
    // Reset interaction gating for fresh loads; pagination should only start after
    // the user actually scrolls/pans.
    this.userChartInteractedAtMs = 0;
    this.userHasChartInteracted = false;
    this.clearHistoryRequestState();
    this.resetWarmupState();
    this.historyPaginationExhausted = false;
    this.lastHistoryRequestTo = null;
    this.nextHistoryToCursor = null;
    this.historyPrevLeftEdgeInZone = false;

    const cacheKey = this.chartSocketCacheKey(trimmedSymbol.toUpperCase(), range, interval);
    const cached = this.chartSocketPayloadCache.get(cacheKey);
    const cacheAge = cached ? Date.now() - cached.storedAt : Infinity;
    const useCache = !!cached?.payload && cacheAge < this.chartSocketCacheTtlMs;

    // 1) Repeat visits: paint from cache immediately (no blank canvas), then socket still refreshes below.
    if (useCache) {
      this.isLoading = false;
      this.dataReceivedForCurrentRequest = true;
      this.indicatorsSyncedForCurrentRequest = false;
      this.drawingsFetchPending = true;
      this.drawingsReceivedFromSocket = false;
      this.chartService.setChartDataFromSocket(cached!.payload, trimmedSymbol.toUpperCase(), range);
      this.changeDetectorRef.detectChanges();
    } else {
      // 1) Show loading overlay IMMEDIATELY (detectChanges forces sync DOM update with OnPush)
      this.isLoading = true;
      this.dataReceivedForCurrentRequest = false;
      this.indicatorsSyncedForCurrentRequest = false;
      this.drawingsFetchPending = true;
      this.drawingsReceivedFromSocket = false;
      this.changeDetectorRef.detectChanges();

      // 2) Now clear all stale chart + indicator data (overlay is already covering the canvas)
      this.chartService.clearChartData();
    }

    // If socket isn't connected yet, wait for connection then emit (fire-once with first())
    if (!this.socketConnectionService.isConnected()) {
      console.log('[ChartComponent] Socket not connected yet, queuing data request for', trimmedSymbol);
      this.socketConnectionService.connectionStatus$
        .pipe(
          filter(connected => connected),
          first(),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          // Ensure socket listeners are initialized before emitting
          this.socketDataService.init();
          // Re-check that this is still the request we want (symbol/range hasn't changed)
          if (this.pendingSymbol !== trimmedSymbol.toUpperCase() || this.pendingRange !== range) return;
          this.emitSocketDataRequest(trimmedSymbol, interval, range, unsubscribePrevious);
        });
      return;
    }

    this.emitSocketDataRequest(trimmedSymbol, interval, range, unsubscribePrevious);
  }

  private emitSocketDataRequest(symbol: string, interval: string, range: string, unsubscribePrevious: boolean): void {
    // Unsubscribe from previous active stream before moving to a new request target.
    if (unsubscribePrevious && this.previousSymbol && this.previousInterval) {
      const hasSubscriptionTargetChanged =
        this.previousSymbol !== symbol.toUpperCase() ||
        this.previousInterval !== interval ||
        this.previousRange !== range;

      if (hasSubscriptionTargetChanged) {
        this.socketDataService.unsubscribeChart(this.previousSymbol, this.previousInterval);
      }
    }

    console.log(`[Socket] Requesting chart data: ${symbol}, interval: ${interval}, range: ${range}`);
    this.pendingSnapshotPayload = null;
    this.historicalRetryCount = 0;
    this.clearHistoricalRetryTimer();
    this.socketDataService.getChartData(symbol, interval, range);
    this.socketDataService.subscribeChart(symbol, interval);
    this.activeSocketSymbol = symbol.toUpperCase();
    this.activeSocketInterval = interval;
    this.activeSocketRange = range;

    this.startLoadingTimeout();
  }

  private startLoadingTimeout(): void {
    this.clearLoadingTimeout();
    this.loadingTimeoutTimer = setTimeout(() => {
      this.loadingTimeoutTimer = null;
      if (!this.isLoading) {
        return;
      }
      if (!this.dataReceivedForCurrentRequest) {
        this.applyPendingSnapshotAsFallback();
      }
      console.warn('[ChartComponent] Loading timeout reached, forcing loader off');
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
    }, 20000);
  }

  private clearLoadingTimeout(): void {
    if (this.loadingTimeoutTimer) {
      clearTimeout(this.loadingTimeoutTimer);
      this.loadingTimeoutTimer = null;
    }
  }

  private clearHistoricalRetryTimer(): void {
    if (this.historicalRetryTimer) {
      clearTimeout(this.historicalRetryTimer);
      this.historicalRetryTimer = null;
    }
  }

  private clearHistoricalLoadWatchers(): void {
    this.clearHistoricalRetryTimer();
    this.historicalRetryCount = this.MAX_HISTORICAL_RETRIES;
    this.clearLoadingTimeout();
  }

  private scheduleHistoricalDataRetry(): void {
    if (this.dataReceivedForCurrentRequest) {
      return;
    }
    if (this.historicalRetryCount >= this.MAX_HISTORICAL_RETRIES) {
      return;
    }
    if (this.historicalRetryTimer) {
      return;
    }

    const delayMs = 1500 * (this.historicalRetryCount + 1);
    const expectedSymbol = this.pendingSymbol;
    const expectedRange = this.pendingRange;
    this.historicalRetryTimer = setTimeout(() => {
      this.historicalRetryTimer = null;
      if (this.dataReceivedForCurrentRequest) {
        return;
      }
      if (this.pendingSymbol !== expectedSymbol || this.pendingRange !== expectedRange) {
        return;
      }

      this.historicalRetryCount++;
      const symbol = this.pendingSymbol || this.currentSymbol;
      const interval = this.currentEffectiveInterval;
      const range = this.pendingRange || this.currentInterval;
      if (!symbol || !interval || !range) {
        return;
      }
      console.log(
        `[ChartComponent] Retrying historical chart:data (${this.historicalRetryCount}/${this.MAX_HISTORICAL_RETRIES}) for ${symbol} ${range}`
      );
      this.socketDataService.getChartData(symbol, interval, range, undefined, undefined, true);
      this.scheduleHistoricalDataRetry();
    }, delayMs);
  }

  private applyPendingSnapshotAsFallback(): void {
    if (this.dataReceivedForCurrentRequest || !this.pendingSnapshotPayload) {
      return;
    }
    if (this.chartService.hasChartData()) {
      return;
    }
    const payload = this.pendingSnapshotPayload;
    const symbol = (payload.symbol || this.currentSymbol || '').toUpperCase();
    const range = this.pendingRange || this.currentInterval;
    console.warn('[ChartComponent] Applying snapshot fallback after historical data did not arrive');
    this.chartService.setChartDataFromSocket(payload, symbol, range);
    if (symbol) {
      this.currentSymbol = symbol;
    }
    if (range) {
      this.currentInterval = range;
    }
  }

  private handleVisibleLogicalRangeChange(range: any): void {
    if (!range) return;
    const from = Number(range?.from);
    if (!Number.isFinite(from)) return;

    // Ignore the very first logical-range callback on initial render/load.
    // This prevents pagination from firing automatically at startup.
    if (this.lastVisibleLogicalFrom === null) {
      this.lastVisibleLogicalFrom = from;
      // IMPORTANT: start with "not in zone" so the first time the user pans into
      // the left-edge zone triggers exactly once (fixes first-load stalling).
      this.historyPrevLeftEdgeInZone = false;
      return;
    }

    const previousFrom = this.lastVisibleLogicalFrom;
    this.lastVisibleLogicalFrom = from;
    const movedLeft = from < (previousFrom - 0.5);
    const inLeftEdgeZone = from <= this.HISTORY_LEFT_EDGE_THRESHOLD_BARS;
    this.historyPrevLeftEdgeInZone = inLeftEdgeZone;

    // Trigger while user is panning left and is within prefetch zone.
    if (!movedLeft || !inLeftEdgeZone) return;
    this.maybeRequestOlderHistory();
  }

  private maybeRequestOlderHistory(force: boolean = false): void {
    // Prevent the "second call with limit=500" right after initial load.
    // Pagination is only allowed after a real user interaction (wheel/drag/touch) on the chart.
    if (!this.userHasChartInteracted) return;

    if (this.isLoading || this.historyPageRequestInFlight || this.historyPaginationExhausted) return;
    if (!this.currentSymbol || !this.currentEffectiveInterval || !this.currentInterval) return;
    if (!force) {
      if (this.lastVisibleLogicalFrom === null || this.lastVisibleLogicalFrom > this.HISTORY_LEFT_EDGE_THRESHOLD_BARS) return;
    } else {
      // If forcing continuation, only do it if the user is still effectively in the left zone.
      if (!this.historyPrevLeftEdgeInZone) return;
    }
    if (this.HISTORY_REQUEST_COOLDOWN_MS > 0) {
      if ((Date.now() - this.lastHistoryRequestAtMs) < this.HISTORY_REQUEST_COOLDOWN_MS) return;
    }

    const oldestLoaded = this.chartService.getOldestLoadedCandleTimeSec();
    if (!oldestLoaded || oldestLoaded <= 1) return;

    const to = Math.floor((this.nextHistoryToCursor ?? (oldestLoaded - 1)));
    if (to <= 0) return;
    if (this.lastHistoryRequestTo === to) return;

    this.historyPageRequestInFlight = true;
    this.lastHistoryRequestAtMs = Date.now();
    this.lastHistoryRequestTo = to;
    this.nextHistoryToCursor = null;
    this.socketDataService.getChartData(
      this.currentSymbol,
      this.currentEffectiveInterval,
      this.currentInterval,
      to,
      this.HISTORY_PAGE_LIMIT
    );

    if (this.historyRequestTimeout) {
      clearTimeout(this.historyRequestTimeout);
    }
    this.historyRequestTimeout = setTimeout(() => {
      if (this.historyPageRequestInFlight) {
        this.clearHistoryRequestState();
        // Allow retry for the same boundary if this request timed out.
        this.lastHistoryRequestTo = null;
        this.changeDetectorRef.markForCheck();
      }
    }, 12000);
  }

  private clearHistoryRequestState(): void {
    this.historyPageRequestInFlight = false;
    if (this.historyRequestTimeout) {
      clearTimeout(this.historyRequestTimeout);
      this.historyRequestTimeout = null;
    }
  }

  private resetWarmupState(): void {
    this.warmupFetchInFlight = false;
    this.warmupFetchExhausted = false;
    this.warmupChainCount = 0;
  }

  /**
   * Fetch older candles so indicators (e.g. EMA 200 on YTD) have enough warm-up bars
   * before the visible window — without requiring the user to scroll left first.
   */
  private ensureIndicatorWarmupHistory(): void {
    if (this.warmupFetchExhausted) {
      return;
    }
    if (this.warmupFetchInFlight || this.historyPageRequestInFlight) {
      return;
    }
    if (!this.dataReceivedForCurrentRequest) {
      return;
    }
    if (!this.currentSymbol || !this.currentEffectiveInterval || !this.currentInterval) {
      return;
    }

    const maxLookback = this.indicatorService.getMaxLookbackPeriod();
    if (maxLookback <= 0) {
      return;
    }

    if (this.chartService.hasSufficientIndicatorWarmup(maxLookback, this.currentInterval)) {
      return;
    }

    if (this.warmupChainCount >= this.WARMUP_MAX_CHAIN_REQUESTS) {
      console.warn('[ChartComponent] Indicator warm-up chain limit reached');
      this.warmupFetchExhausted = true;
      return;
    }

    this.requestWarmupHistoryPage();
  }

  private requestWarmupHistoryPage(): void {
    const oldestLoaded = this.chartService.getOldestLoadedCandleTimeSec();
    if (!oldestLoaded || oldestLoaded <= 1) {
      this.warmupFetchExhausted = true;
      return;
    }

    const to = Math.floor(oldestLoaded - 1);
    if (to <= 0) {
      this.warmupFetchExhausted = true;
      return;
    }
    if (this.lastHistoryRequestTo === to) {
      this.warmupFetchExhausted = true;
      return;
    }

    this.warmupFetchInFlight = true;
    this.historyPageRequestInFlight = true;
    this.warmupChainCount++;
    this.lastHistoryRequestAtMs = Date.now();
    this.lastHistoryRequestTo = to;
    this.nextHistoryToCursor = null;
    this.isLoading = true;
    this.changeDetectorRef.markForCheck();

    console.log(`[ChartComponent] Fetching indicator warm-up history to=${to} (chain ${this.warmupChainCount})`);
    this.socketDataService.getChartData(
      this.currentSymbol,
      this.currentEffectiveInterval,
      this.currentInterval,
      to,
      this.HISTORY_PAGE_LIMIT
    );

    if (this.historyRequestTimeout) {
      clearTimeout(this.historyRequestTimeout);
    }
    this.historyRequestTimeout = setTimeout(() => {
      if (this.historyPageRequestInFlight && this.warmupFetchInFlight) {
        this.clearHistoryRequestState();
        this.warmupFetchInFlight = false;
        this.warmupFetchExhausted = true;
        this.lastHistoryRequestTo = null;
        this.isLoading = false;
        this.changeDetectorRef.markForCheck();
      }
    }, 12000);
  }

  private getOldestTimeFromSocketPayload(payload: any): number | null {
    const rows = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
    if (!rows.length) return null;
    let minTime = Number.POSITIVE_INFINITY;
    for (const row of rows) {
      const t = Number(row?.time);
      if (Number.isFinite(t) && t > 0 && t < minTime) {
        minTime = t;
      }
    }
    return Number.isFinite(minTime) ? Math.floor(minTime) : null;
  }

  /**
   * Load data with current settings (range + interval) using socket
   * All chart data is loaded via socket connection
   */
  private loadDataWithCurrentSettings(): void {
    // Always use the selected interval (defaults to 1d, or auto if null)
    const candleInterval = this.selectedInterval || this.getOptimalCandleInterval(this.currentInterval);
    console.log(`[LOADING] Loading data via socket for ${this.currentSymbol} with range: ${this.currentInterval}, candle interval: ${candleInterval}`);

    // Request data from socket (loading state is managed in requestSocketChartData)
    this.requestSocketChartData(this.currentSymbol, candleInterval, this.currentInterval);
    // Note: isLoading will be set to false when socket data arrives in the subscription
  }

  /** Refresh chart data (reload from socket) */
  refreshChart(): void {
    if (!this.currentSymbol || this.isLoading) return;
    this.closeAllMenus();
    this.chartSocketPayloadCache.clear();
    this.loadDataWithCurrentSettings();
  }

  // Volume is now always enabled by default

  toggleMarkers(): void {
    this.showMarkers = !this.showMarkers;
    // TODO: Implement markers toggle in service
    console.log('Markers display:', this.showMarkers);

    // Update URL with new markers setting
    this.navigateWithQueryParams(undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, this.showMarkers);

    // Save user preferences
    this.saveUserPreferences();

    this.changeDetectorRef.markForCheck();
  }

  private updateDrawingState(): void {
    // Update component state from service
    this.horizontalLines = this.chartService.getHorizontalLines();
    this.systemLines = this.chartService.getSystemLines();
    this.textAnnotations = this.chartService.getTextAnnotations();
    this.penDrawings = this.chartService.getPenDrawings();
    this.measurements = this.chartService.getMeasurements();
    this.systemLineVisibility = this.chartService.getSystemLineVisibility();

    const lineCount = this.horizontalLines.length;
    // Only clear Line tool when lines were removed down to zero (not when user just enabled Line tool with 0 lines)
    if (lineCount === 0 && this.prevHorizontalLinesCount > 0) {
      this.isDrawingMode = false;
      this.chartService.setDrawingMode(false);
      this.showLineToolbar = false;
      this.selectedLine = null;
      this.activeToolbarOption = null;
      this.chartService.deselectLine();
      this.changeDetectorRef.detectChanges(); // Update toolbar icon immediately so highlight is removed
    } else if (lineCount > 0) {
      this.isDrawingMode = this.chartService.getDrawingMode();
      // If the selected line was removed, clear selection
      if (this.selectedLine && !this.horizontalLines.some(l => l.id === this.selectedLine!.id)) {
        this.showLineToolbar = false;
        this.selectedLine = null;
        this.activeToolbarOption = null;
        this.chartService.deselectLine();
      }
    } else {
      this.isDrawingMode = this.chartService.getDrawingMode();
    }
    this.prevHorizontalLinesCount = lineCount;

    this.isTextMode = this.chartService.getTextMode();
    this.isPenMode = this.chartService.getPenMode();
    this.isMeasuringMode = this.chartService.getMeasuringMode();
    this.isArrowMode = this.chartService.getArrowMode();
    this.arrowDrawings = this.chartService.getArrowDrawings();
    this.arrowColor = this.chartService.getArrowDefaultColor();
    this.arrowLineWidth = this.chartService.getArrowDefaultWidth();
    if (this.selectedArrowDrawing && !this.arrowDrawings.some(a => a.id === this.selectedArrowDrawing?.id)) {
      this.selectedArrowDrawing = null;
      this.showArrowToolbar = false;
      this.activeArrowToolbarOption = null;
    }

    // Force change detection for real-time updates
    console.log('Drawing state updated:', {
      isDrawingMode: this.isDrawingMode,
      isTextMode: this.isTextMode,
      isPenMode: this.isPenMode,
      isMeasuringMode: this.isMeasuringMode,
      isArrowMode: this.isArrowMode,
      linesCount: this.horizontalLines.length,
      systemLinesCount: this.systemLines.length,
      textAnnotationsCount: this.textAnnotations.length,
      penDrawingsCount: this.penDrawings.length,
      measurementsCount: this.measurements.length,
      arrowDrawingsCount: this.arrowDrawings.length
    });
    this.changeDetectorRef.markForCheck();
  }

  // Drawing Mode Methods
  toggleDrawingMode(): void {
    this.isDrawingMode = !this.isDrawingMode;

    if (this.isDrawingMode) {
      // Exit other modes when entering drawing mode
      if (this.isTextMode) {
        this.isTextMode = false;
        this.chartService.setTextMode(false);
      }
      if (this.isPenMode) {
        this.isPenMode = false;
        this.chartService.setPenMode(false);
      }
      if (this.isMeasuringMode) {
        this.isMeasuringMode = false;
        this.chartService.setMeasuringMode(false);
      }
      if (this.isArrowMode) {
        this.isArrowMode = false;
        this.chartService.setArrowMode(false);
      }
    }

    this.chartService.setDrawingMode(this.isDrawingMode);
    this.updateDrawingState();
    // If this was chosen from the Drawing dropdown, close it after selection.
    if (this.activeDropdown === 'drawing') {
      this.closeMenusAfterAction();
      return;
    }
    this.changeDetectorRef.markForCheck();
  }

  // Text Mode Methods
  toggleTextMode(): void {
    this.isTextMode = !this.isTextMode;

    if (this.isTextMode) {
      // Exit other modes when entering text mode
      if (this.isDrawingMode) {
        this.isDrawingMode = false;
        this.chartService.setDrawingMode(false);
      }
      if (this.isPenMode) {
        this.isPenMode = false;
        this.chartService.setPenMode(false);
      }
      if (this.isMeasuringMode) {
        this.isMeasuringMode = false;
        this.chartService.setMeasuringMode(false);
      }
      if (this.isArrowMode) {
        this.isArrowMode = false;
        this.chartService.setArrowMode(false);
      }
    }

    this.chartService.setTextMode(this.isTextMode);
    this.updateDrawingState();
    if (this.activeDropdown === 'drawing') {
      this.closeMenusAfterAction();
      return;
    }
    this.changeDetectorRef.markForCheck();
  }

  clearAllTextAnnotations(): void {
    this.chartService.clearAllTextAnnotations();
    this.showTextToolbar = false;
    this.selectedText = null;
    this.activeTextToolbarOption = null;
    this.isTextMode = false;
    this.chartService.setTextMode(false);
    if (this.activeDropdown === 'drawing') {
      this.closeMenusAfterAction();
      return;
    }
    this.changeDetectorRef.markForCheck();
  }

  // Pen drawing methods
  togglePenMode(): void {
    this.isPenMode = !this.isPenMode;

    if (this.isPenMode) {
      // Exit other modes when entering pen mode
      if (this.isDrawingMode) {
        this.isDrawingMode = false;
        this.chartService.setDrawingMode(false);
      }
      if (this.isTextMode) {
        this.isTextMode = false;
        this.chartService.setTextMode(false);
      }
      if (this.isMeasuringMode) {
        this.isMeasuringMode = false;
        this.chartService.setMeasuringMode(false);
      }
      if (this.isArrowMode) {
        this.isArrowMode = false;
        this.chartService.setArrowMode(false);
      }
    }

    this.chartService.setPenMode(this.isPenMode);
    this.updateDrawingState();
    if (this.activeDropdown === 'drawing') {
      this.closeMenusAfterAction();
      return;
    }
    this.changeDetectorRef.markForCheck();
  }

  /** Set drawing tool menus as active when we have drawings from socket/API. */
  private setDrawingToolStateFromDrawings(drawings: any[]): void {
    if (!Array.isArray(drawings) || drawings.length === 0) return;
    const tools = new Set(
      drawings
        .map((d: any) => (d?.tool ?? d?.type ?? d?.data?.tool ?? '').toString().toLowerCase())
        .filter(Boolean)
    );
    if (tools.has('pen')) {
      this.isPenMode = true;
      this.chartService.setPenMode(true);
    }
    if (tools.has('line')) {
      this.isDrawingMode = true;
      this.chartService.setDrawingMode(true);
    }
    if (tools.has('text')) {
      this.isTextMode = true;
      this.chartService.setTextMode(true);
    }
    if (tools.has('arrow')) {
      this.isArrowMode = true;
      this.chartService.setArrowMode(true);
    }
  }

  setPenSize(size: number): void {
    this.penSize = size;
    this.chartService.setPenSize(size);
    this.changeDetectorRef.markForCheck();
  }

  setPenColor(color: string): void {
    this.penColor = color;
    this.chartService.setPenColor(color);
    this.changeDetectorRef.markForCheck();
  }

  // Measuring tool methods
  toggleMeasuringMode(): void {
    this.isMeasuringMode = !this.isMeasuringMode;

    if (this.isMeasuringMode) {
      // Exit other modes when entering measuring mode
      if (this.isDrawingMode) {
        this.isDrawingMode = false;
        this.chartService.setDrawingMode(false);
      }
      if (this.isTextMode) {
        this.isTextMode = false;
        this.chartService.setTextMode(false);
      }
      if (this.isPenMode) {
        this.isPenMode = false;
        this.chartService.setPenMode(false);
      }
      if (this.isArrowMode) {
        this.isArrowMode = false;
        this.chartService.setArrowMode(false);
      }
    }

    this.chartService.setMeasuringMode(this.isMeasuringMode);
    this.updateDrawingState();
    this.changeDetectorRef.markForCheck();
  }

  clearAllPenDrawings(): void {
    this.chartService.clearAllPenDrawings();
    this.showPenToolbar = false;
    this.selectedPenDrawing = null;
    this.activePenToolbarOption = null;
    this.isPenMode = false;
    this.chartService.setPenMode(false);
    if (this.activeDropdown === 'drawing') {
      this.closeMenusAfterAction();
      return;
    }
    this.changeDetectorRef.markForCheck();
  }

  // Arrow tool methods
  toggleArrowMode(): void {
    this.isArrowMode = !this.isArrowMode;

    if (this.isArrowMode) {
      this.selectedArrowDrawing = null;
      this.showArrowToolbar = false;
      this.activeArrowToolbarOption = null;
      this.chartService.deselectArrowDrawing();
      // Exit other drawing modes when entering arrow mode
      if (this.isDrawingMode) {
        this.isDrawingMode = false;
        this.chartService.setDrawingMode(false);
      }
      if (this.isTextMode) {
        this.isTextMode = false;
        this.chartService.setTextMode(false);
      }
      if (this.isPenMode) {
        this.isPenMode = false;
        this.chartService.setPenMode(false);
      }
      if (this.isMeasuringMode) {
        this.isMeasuringMode = false;
        this.chartService.setMeasuringMode(false);
      }
    }

    this.chartService.setArrowMode(this.isArrowMode);
    this.updateDrawingState();
    if (this.activeDropdown === 'drawing') {
      this.closeMenusAfterAction();
      return;
    }
    this.changeDetectorRef.markForCheck();
  }

  clearAllArrowDrawings(): void {
    this.chartService.clearAllArrowDrawings();
    this.isArrowMode = false;
    this.chartService.setArrowMode(false);
    this.arrowDrawings = [];
    this.selectedArrowDrawing = null;
    this.showArrowToolbar = false;
    this.activeArrowToolbarOption = null;
    if (this.activeDropdown === 'drawing') {
      this.closeMenusAfterAction();
      return;
    }
    this.changeDetectorRef.markForCheck();
  }

  setArrowColor(color: string): void {
    this.arrowColor = color;
    this.chartService.setArrowDefaultColor(color);
    this.changeDetectorRef.markForCheck();
  }

  setArrowLineWidth(width: number): void {
    this.arrowLineWidth = width;
    this.chartService.setArrowDefaultWidth(width);
    this.changeDetectorRef.markForCheck();
  }

  deselectArrowDrawing(): void {
    this.showArrowToolbar = false;
    this.selectedArrowDrawing = null;
    this.activeArrowToolbarOption = null;
    this.chartService.deselectArrowDrawing();
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }

  toggleArrowToolbarOption(option: string): void {
    this.closeAllMenus();
    this.activeArrowToolbarOption = this.activeArrowToolbarOption === option ? null : option;
    this.changeDetectorRef.markForCheck();
    this.refreshLucideIcons();
  }

  updateSelectedArrowColor(color: string): void {
    if (!this.selectedArrowDrawing) return;
    this.chartService.updateArrowDrawing(this.selectedArrowDrawing.id, { color });
    this.activeArrowToolbarOption = null;
    this.showArrowToolbar = false;
    this.selectedArrowDrawing = null;
    this.chartService.deselectArrowDrawing();
    this.arrowDrawings = this.chartService.getArrowDrawings();
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }

  updateSelectedArrowLineWidth(width: number): void {
    if (!this.selectedArrowDrawing) return;
    this.chartService.updateArrowDrawing(this.selectedArrowDrawing.id, { lineWidth: width });
    this.activeArrowToolbarOption = null;
    this.showArrowToolbar = false;
    this.selectedArrowDrawing = null;
    this.chartService.deselectArrowDrawing();
    this.arrowDrawings = this.chartService.getArrowDrawings();
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }

  deleteSelectedArrowFromContext(): void {
    if (!this.selectedArrowDrawing) return;
    this.chartService.removeArrowDrawing(this.selectedArrowDrawing.id);
    this.showArrowToolbar = false;
    this.selectedArrowDrawing = null;
    this.activeArrowToolbarOption = null;
    this.arrowDrawings = this.chartService.getArrowDrawings();
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }

  /** Clear all drawings and UI state when user changes timeframe or interval; new range drawings load from API/socket. */
  private clearDrawingsForTimeframeChange(): void {
    this.chartService.clearAllHorizontalLines();
    this.chartService.clearAllTextAnnotations();
    this.chartService.clearAllPenDrawings();
    this.chartService.clearAllMeasurements();
    this.chartService.clearAllArrowDrawings();
    this.horizontalLines = [];
    this.textAnnotations = [];
    this.penDrawings = [];
    this.measurements = [];
    this.arrowDrawings = [];
    this.selectedLine = null;
    this.selectedText = null;
    this.selectedPenDrawing = null;
    this.selectedArrowDrawing = null;
    this.showLineToolbar = false;
    this.showTextToolbar = false;
    this.showPenToolbar = false;
    this.showArrowToolbar = false;
    this.activeArrowToolbarOption = null;
    this.updateDrawingState();
    this.changeDetectorRef.markForCheck();
  }

  /** Fetch drawings from API and bind to chart (called after chart is ready). */
  private fetchChartDrawings(): void {
    if (!this.currentSymbol || !this.currentInterval) return;
    this.chartDrawingsService.getChartDrawings(this.currentSymbol, this.currentInterval, this.currentEffectiveInterval).subscribe({
      next: (drawings) => {
        if (Array.isArray(drawings) && drawings.length > 0) {
          this.isLoadingDrawingsFromApi = true;
          this.chartService.loadDrawingsFromChartDrawingApi(drawings, true);
          this.setDrawingToolStateFromDrawings(drawings);
          this.updateDrawingState();
          setTimeout(() => { this.isLoadingDrawingsFromApi = false; }, 200);
          [200, 500, 1000, 2000, 3000, 5000].forEach(delay => setTimeout(() => this.chartService.redrawPenDrawings(), delay));
          [200, 500, 1000, 2000, 3000, 5000].forEach(delay => setTimeout(() => this.chartService.redrawArrowDrawings(), delay));
        }
        this.changeDetectorRef.markForCheck();
      },
      error: (err) => {
        console.warn('[Chart] Failed to fetch chart drawings', err);
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  /** Debounced save of current drawings to update-chart-drawings API. */
  private scheduleSaveDrawings(): void {
    if (this.saveDrawingsTimer) clearTimeout(this.saveDrawingsTimer);
    this.saveDrawingsTimer = setTimeout(() => {
      this.saveDrawingsTimer = null;
      if (this.isLoadingDrawingsFromApi) {
        // Drawings are still being hydrated from API/socket; retry instead of dropping this save.
        this.scheduleSaveDrawings();
        return;
      }
      if (!this.currentSymbol || !this.currentInterval) return;
      const payload = this.chartService.getDrawingsForApi(this.currentSymbol, this.currentInterval, this.currentEffectiveInterval);
      console.log('[ChartComponent] Saving chart drawings payload:', {
        symbol: this.currentSymbol,
        timeframe: this.currentInterval,
        interval: this.currentEffectiveInterval,
        drawingsCount: payload.length
      });
      this.chartDrawingsService.updateChartDrawings(this.currentSymbol, this.currentInterval, this.currentEffectiveInterval, payload).subscribe({
        next: () => { /* saved */ },
        error: (err) => console.warn('[Chart] Failed to save chart drawings', err)
      });
    }, this.SAVE_DRAWINGS_DEBOUNCE_MS);
  }

  /**
   * Bind drawing tools from API/backend JSON (e.g. after fetching saved drawings elsewhere).
   * @param drawings Array in ChartDrawingApi format (id, tool, data, style)
   * @param clearExisting If true (default), clears existing drawings before applying
   */
  loadDrawingsFromApi(drawings: ChartDrawingApi[], clearExisting: boolean = true): void {
    this.chartService.loadDrawingsFromChartDrawingApi(drawings, clearExisting);
    this.updateDrawingState();
    this.changeDetectorRef.markForCheck();
  }

  clearAllMeasurements(): void {
    this.chartService.clearAllMeasurements();
    this.isMeasuringMode = false;
    this.chartService.setMeasuringMode(false);
    if (this.activeDropdown === 'drawing') {
      this.closeMenusAfterAction();
      return;
    }
    this.changeDetectorRef.markForCheck();
  }

  // Helper method to check if any drawing tool is active
  hasActiveDrawingTool(): boolean {
    return this.isDrawingMode || this.isTextMode || this.isPenMode || this.isMeasuringMode || this.isArrowMode;
  }

  // Method to deselect all drawing tools
  deselectAllDrawingTools(): void {
    if (this.isDrawingMode) {
      this.isDrawingMode = false;
      this.chartService.setDrawingMode(false);
    }
    if (this.isTextMode) {
      this.isTextMode = false;
      this.chartService.setTextMode(false);
    }
    if (this.isPenMode) {
      this.isPenMode = false;
      this.chartService.setPenMode(false);
    }
    if (this.isMeasuringMode) {
      this.isMeasuringMode = false;
      this.chartService.setMeasuringMode(false);
    }
    if (this.isArrowMode) {
      this.isArrowMode = false;
      this.chartService.setArrowMode(false);
    }

    this.updateDrawingState();
    this.changeDetectorRef.markForCheck();
  }

  // Text editing methods
  toggleTextToolbarOption(option: string): void {
    // Close all other menus before opening text toolbar option
    this.closeAllMenus();

    this.activeTextToolbarOption = this.activeTextToolbarOption === option ? null : option;
    this.changeDetectorRef.markForCheck();
    // Refresh icons after dropdown state changes
    this.refreshLucideIcons();
  }

  /** Plain inputs do not insert images on paste; read image/png from clipboard and store as data URL. */
  onPasteImageInTextField(event: ClipboardEvent, _textArea: HTMLTextAreaElement): void {
    const cd = event.clipboardData;
    if (!cd) {
      return;
    }
    for (let i = 0; i < cd.items.length; i++) {
      const item = cd.items[i];
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        event.preventDefault();
        const file = item.getAsFile();
        if (!file) {
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result as string;
          if (this.selectedText) {
            this.chartService.updateTextAnnotation(this.selectedText.id, { text: dataUrl });
            this.deselectText();
          }
        };
        reader.readAsDataURL(file);
        return;
      }
    }
  }

  updateTextContent(inputElement: HTMLInputElement | HTMLTextAreaElement): void {
    if (!this.selectedText) return;
    let newText = inputElement.value;
    if (!/^data:image\//i.test(newText.trim())) {
      newText = newText.trim();
    }
    if (newText) {
      this.chartService.updateTextAnnotation(this.selectedText.id, { text: newText });
      this.deselectText();
    }
  }

  updateTextColor(color: string): void {
    if (!this.selectedText) return;
    console.log('[STYLE] Updating text color to:', color, 'for text:', this.selectedText.id);
    this.chartService.updateTextAnnotation(this.selectedText.id, { fontColor: color });
    this.deselectText();
  }

  updateTextSize(size: number): void {
    if (!this.selectedText) return;
    this.chartService.updateTextAnnotation(this.selectedText.id, { fontSize: size });
    this.deselectText();
  }

  updateTextStyle(style: 'normal' | 'bold' | 'italic'): void {
    if (!this.selectedText) return;
    this.chartService.updateTextAnnotation(this.selectedText.id, { style: style });
    this.deselectText();
  }

  updateTextBackgroundColor(color: string): void {
    if (!this.selectedText) return;
    console.log('[STYLE] Updating background color to:', color, 'for text:', this.selectedText.id);
    const backgroundColor = color === 'transparent' ? undefined : color;
    this.chartService.updateTextAnnotation(this.selectedText.id, { backgroundColor: backgroundColor });
    this.deselectText();
  }

  updateTextBorderColor(color: string): void {
    if (!this.selectedText) return;
    const hasVisibleBorderColor = color !== 'transparent';
    const borderColor = hasVisibleBorderColor ? color : undefined;

    // If user picks a border color while width is None, auto-enable a visible width.
    const updates: Partial<TextAnnotation> = { borderColor };
    if (hasVisibleBorderColor && (this.selectedText.borderWidth || 0) === 0) {
      updates.borderWidth = 1;
    }
    if (!hasVisibleBorderColor) {
      updates.borderWidth = 0;
    }

    this.chartService.updateTextAnnotation(this.selectedText.id, updates);
    this.deselectText();
  }

  updateTextBorderWidth(width: number): void {
    if (!this.selectedText) return;
    this.chartService.updateTextAnnotation(this.selectedText.id, { borderWidth: width });
    this.deselectText();
  }

  deleteTextFromContext(): void {
    if (!this.selectedText) return;
    this.chartService.removeTextAnnotation(this.selectedText.id);
    this.showTextToolbar = false;
    this.selectedText = null;
    this.activeTextToolbarOption = null;
    this.isTextMode = false;
    this.chartService.setTextMode(false);
    this.changeDetectorRef.markForCheck();
  }

  // Method to deselect current text
  deselectText(): void {
    this.showTextToolbar = false;
    this.selectedText = null;
    this.activeTextToolbarOption = null;
    this.chartService.deselectAllTextAnnotations();
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }

  clearAllLines(): void {
    this.chartService.clearAllHorizontalLines();
    this.showLineToolbar = false;
    this.selectedLine = null;
    this.activeToolbarOption = null;
    this.chartService.deselectLine();
    this.isDrawingMode = false;
    this.chartService.setDrawingMode(false);
    if (this.activeDropdown === 'drawing') {
      this.closeMenusAfterAction();
      return;
    }
    this.changeDetectorRef.markForCheck();
  }

  removeLine(lineId: string): void {
    const wasSelected = this.selectedLine?.id === lineId;
    this.chartService.removeHorizontalLine(lineId);
    if (wasSelected) {
      this.showLineToolbar = false;
      this.selectedLine = null;
      this.activeToolbarOption = null;
      this.chartService.deselectLine();
      this.isDrawingMode = false;
      this.chartService.setDrawingMode(false);
    }
    this.changeDetectorRef.markForCheck();
  }

  updateLineName(lineId: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const newName = target.value.trim();
    if (newName) {
      this.chartService.updateHorizontalLineName(lineId, newName);
      // updateDrawingState will be called automatically via callback
      this.changeDetectorRef.markForCheck();
    }
  }

  // Line Context Menu Methods
  updateLineNameFromContext(event: Event): void {
    if (!this.selectedLine) return;
    const target = event.target as HTMLInputElement;
    const newName = target.value.trim();
    if (newName) {
      this.chartService.updateHorizontalLineName(this.selectedLine.id, newName);
      this.deselectLine();
    }
  }

  updateLineColor(color: string): void {
    if (!this.selectedLine) return;
    this.chartService.updateHorizontalLineColor(this.selectedLine.id, color);
    this.deselectLine();
  }

  deleteLineFromContext(): void {
    if (!this.selectedLine) return;
    const lineId = this.selectedLine.id;
    this.chartService.removeHorizontalLine(lineId);
    this.showLineToolbar = false;
    this.selectedLine = null;
    this.activeToolbarOption = null;
    this.chartService.deselectLine();
    // Exit Line drawing mode so the Line tool icon is no longer active in the topbar
    this.isDrawingMode = false;
    this.chartService.setDrawingMode(false);
    this.changeDetectorRef.markForCheck();
  }

  // Compact Toolbar Methods
  toggleToolbarOption(option: string): void {
    // Close all other menus before opening toolbar option
    this.closeAllMenus();

    this.activeToolbarOption = this.activeToolbarOption === option ? null : option;
    this.changeDetectorRef.markForCheck();
    // Refresh icons after dropdown state changes
    this.refreshLucideIcons();
  }

  updateLinePriceFromInput(event: Event): void {
    if (!this.selectedLine) return;
    const target = event.target as HTMLInputElement;
    const newPrice = parseFloat(target.value);
    if (!isNaN(newPrice) && newPrice > 0) {
      // Update the line price properly without creating a duplicate
      this.chartService.updateHorizontalLinePrice(this.selectedLine.id, newPrice);
      this.deselectLine();
    }
  }

  updateLinePriceFromButton(inputElement: HTMLInputElement): void {
    if (!this.selectedLine) return;
    const newPrice = parseFloat(inputElement.value);
    if (!isNaN(newPrice) && newPrice > 0) {
      // Update the line price properly without creating a duplicate
      this.chartService.updateHorizontalLinePrice(this.selectedLine.id, newPrice);
      this.deselectLine();
    }
  }

  // TrackBy function for ngFor performance
  trackByLineId(index: number, line: HorizontalLine): string {
    return line.id;
  }

  trackBySystemLineId(index: number, line: SystemLine): string {
    return line.id;
  }

  trackByTicker(index: number, ticker: RecentTicker): string {
    return ticker.symbol;
  }

  // Dropdown Management
  private refreshLucideIcons(scope?: ParentNode): void {
    setTimeout(() => {
      const lucide = (window as any).lucide;
      if (!lucide?.createIcons) {
        return;
      }

      const root = scope ?? document;
      // Only re-render placeholder elements; Lucide replaces <i> with <svg data-lucide>.
      root.querySelectorAll('i[data-lucide]').forEach((node) => {
        const el = node as HTMLElement;
        while (el.firstChild) {
          el.removeChild(el.firstChild);
        }
      });

      lucide.createIcons();
    }, 0);
  }


  // Helper methods for UI state (updated for new indicator system)
  hasActiveIndicators(): boolean {
    return this.getActiveIndicatorTypeCount() > 0;
  }

  // Modular Indicator System Methods
  getVisibleIndicators(): Indicator[] {
    return this.indicators.filter(indicator => indicator.visible);
  }

  private isMobileViewportForIndicatorsPanel(): boolean {
    try {
      return typeof window !== 'undefined' && window.innerWidth <= this.INDICATORS_PANEL_MOBILE_MAX_WIDTH;
    } catch (_) {
      return false;
    }
  }

  private syncIndicatorsPanelCollapseWithViewport(): void {
    const isMobile = this.isMobileViewportForIndicatorsPanel();

    // If the viewport crossed the breakpoint, reset "user toggled" and apply the correct default.
    if (this.lastIndicatorsPanelViewportWasMobile === null) {
      this.lastIndicatorsPanelViewportWasMobile = isMobile;
    } else if (this.lastIndicatorsPanelViewportWasMobile !== isMobile) {
      this.lastIndicatorsPanelViewportWasMobile = isMobile;
      this.hasUserToggledIndicatorsPanel = false;
      this.indicatorsPanelCollapsed = isMobile;
      this.changeDetectorRef.markForCheck();
      return;
    }

    // Within the same viewport category, only enforce the default until the user toggles.
    if (!this.hasUserToggledIndicatorsPanel) {
      this.indicatorsPanelCollapsed = isMobile;
      this.changeDetectorRef.markForCheck();
    }
  }

  private applyDefaultIndicatorsPanelCollapseState(): void {
    if (this.hasUserToggledIndicatorsPanel) return;
    this.indicatorsPanelCollapsed = this.isMobileViewportForIndicatorsPanel();
    if (this.lastIndicatorsPanelViewportWasMobile === null) {
      this.lastIndicatorsPanelViewportWasMobile = this.isMobileViewportForIndicatorsPanel();
    }
  }

  toggleIndicatorsPanelCollapsed(): void {
    this.hasUserToggledIndicatorsPanel = true;
    this.indicatorsPanelCollapsed = !this.indicatorsPanelCollapsed;
  }

  getActiveIndicatorsForPanel(): Indicator[] {
    // Show both visible + hidden indicators so the eye-toggle works from the chip panel.
    return this.indicators || [];
  }

  getIndicatorChipValue(indicator: Indicator): string {
    const settings = indicator.settings || {};
    switch (indicator.type) {
      case 'moving_average': {
        const period = settings['period'];
        const maType = (settings['movingAverageType'] || 'exponential') === 'exponential' ? 'EMA' : 'SMA';
        return `${period ?? ''} • ${maType}`;
      }
      case 'atr': {
        const period = settings['period'];
        return `${period ?? ''}-Day ATR`;
      }
      case 'rsi': {
        const period = settings['period'];
        const source = settings['source'] || 'close';
        return `${period ?? ''} • ${source.toString().toUpperCase()}`;
      }
      case 'macd': {
        const fast = settings['fastPeriod'] ?? '';
        const slow = settings['slowPeriod'] ?? '';
        const signal = settings['signalPeriod'] ?? '';
        return `(${fast}/${slow}/${signal})`;
      }
      case 'bollinger_bands': {
        const period = settings['period'];
        const std = settings['standardDeviations'] ?? 2;
        return `${period ?? ''} • ${std}σ`;
      }
      case 'stochastic': {
        const k = settings['kPeriod'] ?? 14;
        const d = settings['dPeriod'] ?? 3;
        const smooth = settings['smooth'] ?? 3;
        return `%K ${k}, %D ${d}${smooth ? `, S ${smooth}` : ''}`;
      }
      default:
        return '';
    }
  }

  getIndicatorChipLiveValue(indicator: Indicator): number | null {
    const movingAverages = this.tooltipData?.movingAverages;
    if (!movingAverages || !indicator?.id) return null;
    const live = movingAverages[indicator.id];
    if (!live || typeof live.value !== 'number' || !Number.isFinite(live.value)) return null;
    return live.value;
  }

  getDisplayTooltipData(): TooltipData | null {
    if (this.tooltipData?.visible) return this.tooltipData;

    const latestCandle = this.chartService.getLatestCandleOhlc();
    if (!latestCandle) return null;
    const latestVolume = this.chartService.getLatestCandleVolume();

    return {
      visible: true,
      x: 0,
      y: 0,
      data: latestCandle,
      volumeData: latestVolume !== null ? { value: latestVolume } : undefined,
      time: null
    };
  }

  // All Moving Average indicators (visible + hidden) - for left card so eye only hides on chart, not from card
  getMovingAverageIndicators(): Indicator[] {
    return this.indicators.filter(ind => ind.type === 'moving_average');
  }

  // Number of visible Moving Average indicators (for UI badges)
  getMovingAverageIndicatorCount(): number {
    return this.getVisibleIndicators().filter(ind => ind.type === 'moving_average').length;
  }

  getTooltipMovingAverages(movingAverages: any): any[] {
    if (!movingAverages) return [];
    return Object.values(movingAverages);
  }

  isIndicatorCollapsed(indicator: Indicator): boolean {
    const visibleIndicators = this.getVisibleIndicators();
    const index = visibleIndicators.findIndex(ind => ind.id === indicator.id);
    return index >= this.maxVisibleIndicators;
  }

  selectIndicator(indicator: Indicator): void {
    this.selectedIndicator = indicator;

    // Close all other menus before opening indicator settings
    this.closeAllMenus();
    this.showIndicatorSettings = true;
  }

  toggleIndicatorVisibility(indicatorId: string): void {
    this.indicatorService.toggleIndicatorVisibility(indicatorId);
    // Sync with chart service to update the visual lines
    this.chartService.syncIndicators(this.indicators);
  }

  getIndicatorValue(indicator: Indicator): string {
    // Real-time values will be wired when chart service exposes a value stream.
    // For now, return empty so no misleading placeholder values are shown.
    return '';
  }

  getIndicatorDisplayTitle(type: IndicatorType): string {
    const titles: Record<IndicatorType, string> = {
      moving_average: 'Moving Average',
      rsi: 'Relative Strength Index',
      macd: 'MACD',
      bollinger_bands: 'Bollinger Bands',
      atr: 'Average True Range',
      stochastic: 'Stochastic Oscillator'
    };
    return titles[type] || 'Indicator Settings';
  }

  getIndicatorTooltip(indicator: Indicator): string {
    return `${indicator.name}\nType: ${indicator.type}\nColor: ${indicator.color}\nCreated: ${indicator.createdAt.toLocaleDateString()}`;
  }

  trackByIndicatorId(index: number, indicator: Indicator): string {
    return indicator.id;
  }

  trackByChartIntervalValue(_index: number, interval: { value: string }): string {
    return interval.value;
  }

  toggleShowAllIndicators(): void {
    this.showAllIndicators = !this.showAllIndicators;
  }

  getIndicatorIcon(type: IndicatorType): string {
    const iconMap: Record<IndicatorType, string> = {
      moving_average: 'trending-up',
      atr: 'bar-chart-3',
      rsi: 'activity',
      macd: 'layers',
      bollinger_bands: 'bar-chart-2',
      stochastic: 'audio-waveform'
    };
    return iconMap[type] || 'bar-chart-3';
  }

  isIndicatorTypeActive(type: IndicatorType): boolean {
    return this.indicators.some(ind => ind.type === type);
  }

  getActiveIndicatorTypeCount(): number {
    const activeTypes = new Set<IndicatorType>();
    this.indicators.forEach(ind => {
      if (ind.visible !== false) {
        activeTypes.add(ind.type);
      }
    });
    return activeTypes.size;
  }

  toggleIndicatorType(type: IndicatorType): void {
    // Special handling for Moving Average: toggle all three (50 / 100 / 200) together
    if (type === 'moving_average') {
      if (this.isIndicatorTypeActive(type)) {
        // Remove all moving averages in one click
        const movingAverages = this.indicators.filter(ind => ind.type === 'moving_average');
        movingAverages.forEach(ma => this.removeIndicator(ma.id));
      } else {
        // Add all three default moving averages in one click
        const defaultPeriods = [50, 100, 200];
        defaultPeriods.forEach(period => {
          this.addIndicator('moving_average', { period });
        });
      }
      this.closeMenusAfterAction();
      return;
    }

    // Default behaviour for other indicators: toggle single instance
    if (this.isIndicatorTypeActive(type)) {
      const first = this.indicators.find(ind => ind.type === type);
      if (first) this.removeIndicator(first.id);
    } else {
      this.addIndicator(type);
    }

    this.closeMenusAfterAction();
  }

  addIndicator(type: IndicatorType, customSettings?: Partial<IndicatorSettings>): void {
    const indicatorId = this.indicatorService.addIndicator(type, customSettings);
    this.activeDropdown = null;

    // Get current indicators immediately
    const currentIndicators = this.indicatorService.getIndicators();
    console.log('[INDICATOR] Adding indicator:', type, 'Total indicators:', currentIndicators.length);

    // Ensure chart updates immediately - sync with chart service
    this.chartService.syncIndicators(currentIndicators);

    // Force change detection
    this.changeDetectorRef.markForCheck();

    // Also trigger another change detection after a short delay to ensure UI updates
    setTimeout(() => {
      this.changeDetectorRef.markForCheck();
    }, 100);

    // URL will be updated automatically via the indicators subscription
    // refreshIndicators() will be called via the subscription
  }

  removeIndicator(indicatorId: string): void {
    this.indicatorService.removeIndicator(indicatorId);

    // Get current indicators immediately
    const currentIndicators = this.indicatorService.getIndicators();
    console.log('[INDICATOR] Removing indicator:', indicatorId, 'Remaining indicators:', currentIndicators.length);

    // Sync with chart service to update the visual lines immediately
    this.chartService.syncIndicators(currentIndicators);
    this.changeDetectorRef.markForCheck();

    // URL will be updated automatically via the indicators subscription
    // refreshIndicators() will be called via the subscription
  }

  toggleIndicatorFavorite(indicatorId: string): void {
    this.indicatorService.toggleIndicatorFavorite(indicatorId);
    // No need to sync with chart service as favorites don't affect visual display
  }

  /**
   * Load chart indicators from API (dynamic data only)
   */
  loadChartIndicators(): void {
    this.chartService.fetchChartIndicators().subscribe({
      next: (chartIndicators: ChartIndicator[]) => {
        // Only use dynamic data from API, no fallback
        this.availableIndicatorTypes = chartIndicators || [];
        console.log('[ChartComponent] Loaded chart indicators:', chartIndicators.length, 'indicators');
        console.log('[ChartComponent] Favorite indicators:', chartIndicators.filter(ind => ind.isFavorite).length);
        this.changeDetectorRef.markForCheck();
        // Refresh Lucide icons after chart indicators are loaded
        setTimeout(() => {
          this.refreshLucideIcons();
          // Force change detection to update indicators
          this.changeDetectorRef.markForCheck();
        }, 100);
      },
      error: (error) => {
        console.error('[ChartComponent] Error loading chart indicators from API:', error);
        // Keep empty array - only show dynamic data
        this.availableIndicatorTypes = [];
        this.changeDetectorRef.markForCheck();
        // Still refresh icons
        setTimeout(() => this.refreshLucideIcons(), 100);
      }
    });

    // Also subscribe to chart indicators updates
    this.chartService.getChartIndicatorsObservable().pipe(
      takeUntil(this.destroy$)
    ).subscribe(chartIndicators => {
      // Only update with dynamic data from API
      this.availableIndicatorTypes = chartIndicators || [];
      console.log('[ChartComponent] Chart indicators updated:', chartIndicators.length, 'indicators');
      this.changeDetectorRef.markForCheck();
      // Refresh icons when chart indicators update
      setTimeout(() => this.refreshLucideIcons(), 50);
    });
  }

  // Favorite indicator types functionality
  toggleIndicatorTypeFavorite(indicatorType: IndicatorType): void {
    const indicator = this.availableIndicatorTypes.find(ind => ind.type === indicatorType);
    if (!indicator) {
      console.warn('[ChartComponent] Indicator type not found:', indicatorType);
      return;
    }

    console.log('[ChartComponent] Toggling favorite for indicator:', indicator.displayName, 'Current favorite status:', indicator.isFavorite);

    this.chartService.toggleChartIndicatorFavorite(indicator.id).subscribe({
      next: () => {
        // Update local state - the service already updates the observable
        console.log('[ChartComponent] Chart indicator favorite toggled successfully:', indicatorType);
        this.changeDetectorRef.markForCheck();
      },
      error: (error) => {
        console.error('[ChartComponent] Error toggling chart indicator favorite:', error);
      }
    });
  }

  isIndicatorTypeFavorite(indicatorType: IndicatorType): boolean {
    return this.chartService.isChartIndicatorFavorite(indicatorType);
  }

  // View toggle functionality
  showFavoritesOnly: boolean = false;

  toggleFavoritesView(): void {
    this.showFavoritesOnly = !this.showFavoritesOnly;
    this.saveFavoritesViewPreference();
    // Refresh Lucide icons after view change
    setTimeout(() => this.refreshLucideIcons(), 100);
  }

  getFavoriteIndicatorTypes(): ChartIndicator[] {
    return this.chartService.getFavoriteChartIndicators();
  }

  private saveFavoritesViewPreference(): void {
    try {
      localStorage.setItem('show-favorites-only', JSON.stringify(this.showFavoritesOnly));
    } catch (error) {
      console.error('Error saving favorites view preference:', error);
    }
  }

  private loadFavoritesViewPreference(): void {
    try {
      const saved = localStorage.getItem('show-favorites-only');
      if (saved) {
        this.showFavoritesOnly = JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading favorites view preference:', error);
    }
  }

  // Chart Type Favorites functionality
  showChartTypeFavoritesOnly: boolean = false;

  /**
   * Load chart types from API (dynamic data only)
   */
  loadChartTypes(): void {
    this.chartService.fetchChartTypes().subscribe({
      next: (chartTypes: ChartType[]) => {
        // Only use dynamic data from API, no fallback
        this.seriesTypes = chartTypes || [];
        this.changeDetectorRef.markForCheck();
        // Refresh Lucide icons after chart types are loaded
        setTimeout(() => {
          this.refreshLucideIcons();
          // Force change detection to update icon
          this.changeDetectorRef.markForCheck();
        }, 100);
      },
      error: (error) => {
        console.error('[ChartComponent] Error loading chart types from API:', error);
        // Keep empty array - only show dynamic data
        this.seriesTypes = [];
        this.changeDetectorRef.markForCheck();
        // Still refresh icons with fallback icon
        setTimeout(() => this.refreshLucideIcons(), 100);
      }
    });

    // Also subscribe to chart types updates
    this.chartService.getChartTypesObservable().pipe(
      takeUntil(this.destroy$)
    ).subscribe(chartTypes => {
      // Only update with dynamic data from API
      this.seriesTypes = chartTypes || [];
      this.changeDetectorRef.markForCheck();
      // Refresh icons when chart types update
      setTimeout(() => this.refreshLucideIcons(), 50);
    });
  }

  toggleChartTypeFavorite(chartTypeValue: SeriesType): void {
    const chartType = this.seriesTypes.find(type => type.value === chartTypeValue);
    if (!chartType) {
      console.warn('[ChartComponent] Chart type not found:', chartTypeValue);
      return;
    }

    this.chartService.toggleChartTypeFavorite(chartType.id).subscribe({
      next: () => {
        // Update local state - the service already updates the observable
        console.log('[ChartComponent] Chart type favorite toggled:', chartTypeValue);
        this.changeDetectorRef.markForCheck();
      },
      error: (error) => {
        console.error('[ChartComponent] Error toggling chart type favorite:', error);
      }
    });
  }

  isChartTypeFavorite(chartTypeValue: SeriesType): boolean {
    return this.chartService.isChartTypeFavorite(chartTypeValue);
  }

  toggleChartTypeFavoritesView(): void {
    this.showChartTypeFavoritesOnly = !this.showChartTypeFavoritesOnly;
    this.saveChartTypeFavoritesViewPreference();
    // Refresh Lucide icons after view change
    setTimeout(() => this.refreshLucideIcons(), 100);
  }

  getFavoriteChartTypes(): ChartType[] {
    return this.chartService.getFavoriteChartTypes();
  }

  trackByChartTypeId(index: number, item: ChartType): string {
    return item.id || item.value;
  }

  private saveChartTypeFavoritesViewPreference(): void {
    try {
      localStorage.setItem('show-chart-type-favorites-only', JSON.stringify(this.showChartTypeFavoritesOnly));
    } catch (error) {
      console.error('Error saving chart type favorites view preference:', error);
    }
  }

  private loadChartTypeFavoritesViewPreference(): void {
    try {
      const saved = localStorage.getItem('show-chart-type-favorites-only');
      if (saved) {
        this.showChartTypeFavoritesOnly = JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading chart type favorites view preference:', error);
    }
  }

  /**
   * Load chart time ranges from API (dynamic data)
   */
  loadChartTimeRanges(): void {
    console.log('[ChartComponent] Loading time ranges from API...');
    this.chartTypeService.getChartTimeRangesList().subscribe({
      next: (response: any) => {
        try {
          console.log('[ChartComponent] Time ranges API response:', response);

          // Handle different response structures (similar to chart types)
          let timeRangesData: any[] = [];

          // Check for nested structure: response.data.timeRanges or response.data.chartTimeRanges
          if (response?.data?.timeRanges && Array.isArray(response.data.timeRanges)) {
            timeRangesData = response.data.timeRanges;
            console.log('[ChartComponent] Found time ranges in response.data.timeRanges');
          } else if (response?.data?.chartTimeRanges && Array.isArray(response.data.chartTimeRanges)) {
            timeRangesData = response.data.chartTimeRanges;
            console.log('[ChartComponent] Found time ranges in response.data.chartTimeRanges');
          } else if (response?.body?.timeRanges && Array.isArray(response.body.timeRanges)) {
            timeRangesData = response.body.timeRanges;
            console.log('[ChartComponent] Found time ranges in response.body.timeRanges');
          } else if (response?.body?.chartTimeRanges && Array.isArray(response.body.chartTimeRanges)) {
            timeRangesData = response.body.chartTimeRanges;
            console.log('[ChartComponent] Found time ranges in response.body.chartTimeRanges');
          } else if (Array.isArray(response)) {
            timeRangesData = response;
            console.log('[ChartComponent] Response is direct array');
          } else if (response?.data && Array.isArray(response.data)) {
            timeRangesData = response.data;
            console.log('[ChartComponent] Found time ranges in response.data');
          } else if (response?.body && Array.isArray(response.body)) {
            timeRangesData = response.body;
            console.log('[ChartComponent] Found time ranges in response.body');
          } else if (response?.timeRanges && Array.isArray(response.timeRanges)) {
            timeRangesData = response.timeRanges;
            console.log('[ChartComponent] Found time ranges in response.timeRanges');
          } else if (response?.chartTimeRanges && Array.isArray(response.chartTimeRanges)) {
            timeRangesData = response.chartTimeRanges;
            console.log('[ChartComponent] Found time ranges in response.chartTimeRanges');
          } else {
            console.warn('[ChartComponent] Unexpected API response structure for time ranges:', response);
            console.warn('[ChartComponent] Response keys:', response ? Object.keys(response) : 'null');
            this.timeRanges = [];
            this.changeDetectorRef.markForCheck();
            return;
          }

          if (timeRangesData.length > 0) {
            this.timeRanges = timeRangesData.map((range: any) => ({
              id: range._id || range.id || range.chartTimeRangeId || '',
              value: this.normalizeTimeRangeValue(range.value || range.code || range.timeRange || '') || '',
              label: range.name || range.label || range.title || '',
              isDefault: range.isDefault !== undefined
                ? Boolean(range.isDefault)
                : (range.default !== undefined ? Boolean(range.default) : false),
              order: range.order || range.sortOrder || 0,
              optimalInterval: range.optimalInterval || range.optimalCandleInterval || range.defaultInterval || undefined,
              validIntervals: range.validIntervals || range.validCandleIntervals || range.allowedIntervals || undefined
            })).sort((a: ChartTimeRange, b: ChartTimeRange) => (a.order || 0) - (b.order || 0));

            console.log('[ChartComponent] Loaded time ranges from API:', this.timeRanges);
            this.timeRangesLoaded = true;

            // Set default time range if available
            const defaultRange = this.timeRanges.find(r => r.isDefault);
            if (defaultRange) {
              console.log('[ChartComponent] Found default time range:', defaultRange.value);
              // Always prefer API default over hardcoded default
              if (this.currentInterval === '1d' || !this.currentInterval) {
                this.currentInterval = defaultRange.value;
                console.log('[ChartComponent] Set default time range to:', this.currentInterval);
                // Reload chart with new default range
                if (this.currentSymbol) {
                  this.loadSymbol();
                }
              }
            }

            this.changeDetectorRef.markForCheck();
          } else {
            console.warn('[ChartComponent] Time ranges array is empty');
            this.timeRanges = [];
            this.changeDetectorRef.markForCheck();
          }
        } catch (error) {
          console.error('[ChartComponent] Error parsing time ranges response:', error);
          console.error('[ChartComponent] Error details:', error);
          this.timeRanges = [];
          this.changeDetectorRef.markForCheck();
        }
      },
      error: (error) => {
        console.error('[ChartComponent] Error loading time ranges from API:', error);
        console.error('[ChartComponent] Error status:', error?.status);
        console.error('[ChartComponent] Error message:', error?.message);
        // Fallback to default time ranges if API fails
        if (this.timeRanges.length === 0) {
          console.log('[ChartComponent] Using fallback time ranges');
          this.timeRanges = [
            { id: '1', value: '1d', label: '1D', isDefault: false, order: 1 },
            { id: '2', value: '5d', label: '5D', isDefault: false, order: 2 },
            { id: '3', value: '1m', label: '1M', isDefault: false, order: 3 },
            { id: '4', value: '6m', label: '6M', isDefault: false, order: 4 },
            { id: '5', value: 'ytd', label: 'YTD', isDefault: false, order: 5 },
            { id: '6', value: '1y', label: '1Y', isDefault: true, order: 6 },
            { id: '7', value: 'all', label: 'ALL', isDefault: false, order: 7 }
          ];
        }
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  /**
   * Load chart intervals from API (dynamic data)
   */
  loadChartIntervals(): void {
    console.log('[ChartComponent] Loading intervals from API...');
    this.chartTypeService.getChartIntervalsList().subscribe({
      next: (response: any) => {
        try {
          console.log('[ChartComponent] Intervals API response:', response);

          // Handle different response structures (similar to chart types)
          let intervalsData: any[] = [];

          // Check for nested structure: response.data.intervals or response.data.chartIntervals
          if (response?.data?.intervals && Array.isArray(response.data.intervals)) {
            intervalsData = response.data.intervals;
            console.log('[ChartComponent] Found intervals in response.data.intervals');
          } else if (response?.data?.chartIntervals && Array.isArray(response.data.chartIntervals)) {
            intervalsData = response.data.chartIntervals;
            console.log('[ChartComponent] Found intervals in response.data.chartIntervals');
          } else if (response?.body?.intervals && Array.isArray(response.body.intervals)) {
            intervalsData = response.body.intervals;
            console.log('[ChartComponent] Found intervals in response.body.intervals');
          } else if (response?.body?.chartIntervals && Array.isArray(response.body.chartIntervals)) {
            intervalsData = response.body.chartIntervals;
            console.log('[ChartComponent] Found intervals in response.body.chartIntervals');
          } else if (Array.isArray(response)) {
            intervalsData = response;
            console.log('[ChartComponent] Response is direct array');
          } else if (response?.data && Array.isArray(response.data)) {
            intervalsData = response.data;
            console.log('[ChartComponent] Found intervals in response.data');
          } else if (response?.body && Array.isArray(response.body)) {
            intervalsData = response.body;
            console.log('[ChartComponent] Found intervals in response.body');
          } else if (response?.intervals && Array.isArray(response.intervals)) {
            intervalsData = response.intervals;
            console.log('[ChartComponent] Found intervals in response.intervals');
          } else if (response?.chartIntervals && Array.isArray(response.chartIntervals)) {
            intervalsData = response.chartIntervals;
            console.log('[ChartComponent] Found intervals in response.chartIntervals');
          } else {
            console.warn('[ChartComponent] Unexpected API response structure for intervals:', response);
            console.warn('[ChartComponent] Response keys:', response ? Object.keys(response) : 'null');
            this.chartIntervals = [];
            this.changeDetectorRef.markForCheck();
            return;
          }

          if (intervalsData.length > 0) {
            this.chartIntervals = intervalsData.map((interval: any) => ({
              id: interval._id || interval.id || interval.chartIntervalId || '',
              value: (this.normalizeCandleIntervalValue(interval.value || interval.code || interval.interval || '') || '').toString(),
              label: interval.name || interval.label || interval.title || '',
              description: interval.description || interval.desc || '',
              isDefault: interval.isDefault !== undefined
                ? Boolean(interval.isDefault)
                : (interval.default !== undefined ? Boolean(interval.default) : false),
              order: interval.order || interval.sortOrder || 0,
              validRanges: interval.validRanges || interval.validTimeRanges || interval.allowedRanges || undefined
            })).sort((a: ChartInterval, b: ChartInterval) => (a.order || 0) - (b.order || 0));

            console.log('[ChartComponent] Loaded intervals from API:', this.chartIntervals);
            this.intervalsLoaded = true;

            // Set default interval if available
            const defaultInterval = this.chartIntervals.find(i => i.isDefault);
            if (defaultInterval) {
              console.log('[ChartComponent] Found default interval:', defaultInterval.value);
              // Always prefer API default over hardcoded default
              if (this.selectedInterval === null || this.selectedInterval === '1d') {
                this.selectedInterval = defaultInterval.value;
                console.log('[ChartComponent] Set default interval to:', this.selectedInterval);
              }
            }

            this.changeDetectorRef.markForCheck();
          } else {
            console.warn('[ChartComponent] Intervals array is empty');
            this.chartIntervals = [];
            this.changeDetectorRef.markForCheck();
          }
        } catch (error) {
          console.error('[ChartComponent] Error parsing intervals response:', error);
          console.error('[ChartComponent] Error details:', error);
          this.chartIntervals = [];
          this.changeDetectorRef.markForCheck();
        }
      },
      error: (error) => {
        console.error('[ChartComponent] Error loading intervals from API:', error);
        console.error('[ChartComponent] Error status:', error?.status);
        console.error('[ChartComponent] Error message:', error?.message);
        // Keep empty array - fallback to MarketDataService
        this.chartIntervals = [];
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  saveIndicatorSettings(): void {
    this.updateIndicatorSettings();
    this.closeIndicatorSettings();
  }

  setIndicatorColor(color: string): void {
    if (this.selectedIndicator) {
      this.selectedIndicator.color = color;
      this.updateIndicatorSettings();
    }
  }

  applyMovingAveragePreset(period: number): void {
    if (!this.selectedIndicator || this.selectedIndicator.type !== 'moving_average') {
      return;
    }

    this.updateIndicatorSetting('period', period);

    // Align color with common MA conventions so users immediately recognize it
    const periodColorMap: Record<number, string> = {
      50: '#4CAF50',   // Short-term – green
      100: '#FFC107',  // Medium-term – yellow / orange
      200: '#F44336'   // Long-term – red
    };
    const color = periodColorMap[period];
    if (color) {
      this.setIndicatorColor(color);
    }
  }

  getMovingAveragePresetSelection(period?: number): string {
    if (period === 50) return '50';
    if (period === 100) return '100';
    if (period === 200) return '200';
    return 'custom';
  }

  onMovingAveragePresetChange(value: string): void {
    if (value === 'custom') {
      // Leave existing period; user can type manually
      return;
    }

    const period = Number(value);
    if (!isNaN(period)) {
      this.applyMovingAveragePreset(period);
    }
  }

  updateIndicatorSetting(key: string, value: any): void {
    if (this.selectedIndicator) {
      let parsedValue: any = value;

      // Parse numeric fields properly to avoid "30" -> "3" type mismatches
      if (key === 'lineWidth') {
        const num = Number(value);
        if (value === '' || value === null || value === undefined || isNaN(num)) {
          return;
        }
        parsedValue = Math.min(4, Math.max(1, Math.round(num)));
      } else if ([
        'period', 'fastPeriod', 'slowPeriod', 'signalPeriod',
        'kPeriod', 'dPeriod', 'smooth', 'standardDeviations',
        'overboughtLevel', 'oversoldLevel'
      ].includes(key)) {
        const num = Number(value);
        if (value === '' || value === null || value === undefined || isNaN(num)) {
          return; // Ignore empty or invalid
        }
        if (key === 'period' || key === 'fastPeriod' || key === 'slowPeriod' || key === 'signalPeriod' ||
            key === 'kPeriod' || key === 'dPeriod' || key === 'smooth') {
          if (num <= 0) return;
        }
        parsedValue = key === 'standardDeviations' ? num : Math.round(num);
      }

      // Create a new settings object to ensure change detection and avoid reference issues
      const newSettings = {
        ...this.selectedIndicator.settings,
        [key]: parsedValue
      };
      
      this.selectedIndicator.settings = newSettings;
      
      // Update the name if period changed
      if (key === 'period' || key === 'movingAverageType') {
        const typeName = (newSettings['movingAverageType'] || 'exponential') === 'exponential' ? 'EMA' : 'SMA';
        this.selectedIndicator.name = `${typeName}(${newSettings['period']})`;
      }

      // Live update the chart settings
      this.updateIndicatorSettings();
    }
  }

  closeIndicatorSettings(): void {
    this.showIndicatorSettings = false;
    this.selectedIndicator = null;
    this.changeDetectorRef.markForCheck();
  }

  updateIndicatorSettings(): void {
    if (this.selectedIndicator) {
      const id = this.selectedIndicator.id;
      const settings = { ...this.selectedIndicator.settings };
      const period = Number(settings['period']);
      if (!isNaN(period) && period > 0) {
        settings['period'] = period;
      }

      const success = this.indicatorService.updateIndicator(id, {
        settings,
        color: this.selectedIndicator.color
      });

      if (success) {
        const freshIndicators = this.indicatorService.getIndicators();
        this.indicators = freshIndicators;
        this.chartService.syncIndicators(freshIndicators);
        this.selectedIndicator = this.indicatorService.getIndicatorById(id) ?? this.selectedIndicator;
        this.saveUserPreferences();
        this.changeDetectorRef.markForCheck();
      }
    }
  }

  getIndicatorDisplayName(type: IndicatorType): string {
    const indicatorType = this.availableIndicatorTypes.find(t => t.type === type);
    return indicatorType?.displayName || type;
  }


  private refreshIndicators(): void {
    this.indicators = this.indicatorService.getIndicators();
    // Refresh icons after DOM update
    setTimeout(() => this.refreshLucideIcons(), 50);
  }

  hasActiveEvents(): boolean {
    return Object.values(this.eventStates).some(state => state);
  }


  // Event Management
  toggleEventType(eventType: 'earnings' | 'dividend' | 'split' | 'news' | 'other'): void {
    this.eventStates[eventType] = !this.eventStates[eventType];
    this.chartService.toggleMarkerVisibility(eventType);
    console.log(`[EVENTS] ${eventType} events: ${this.eventStates[eventType] ? 'SHOWN' : 'HIDDEN'}`);
    this.changeDetectorRef.markForCheck();
  }

  // Helper method to format date for tooltip
  formatTooltipDate(timestamp: number): string {
    const unixSeconds = timestamp > 1e12 ? Math.floor(timestamp / 1000) : Math.floor(timestamp);
    // Show date-only for daily candles, and date+time for intraday candles.
    const normalizedInterval = (this.currentEffectiveInterval || '').toLowerCase();
    if (normalizedInterval === '1d') {
      // For daily candles, preserve the source calendar day (UTC day boundary).
      return moment.unix(unixSeconds).utc().format('ddd, MMM D, YYYY');
    }
    return moment.unix(unixSeconds).tz(environment.defaultTimezone).format('ddd, MMM D, YYYY h:mm A z');
  }

  private normalizeTooltipTimeToUnixSeconds(time: any): number | null {
    if (typeof time === 'number' && Number.isFinite(time)) {
      return time > 1e12 ? Math.floor(time / 1000) : Math.floor(time);
    }

    if (typeof time === 'string') {
      const numeric = Number(time);
      if (Number.isFinite(numeric)) {
        return numeric > 1e12 ? Math.floor(numeric / 1000) : Math.floor(numeric);
      }
      const parsedMs = Date.parse(time);
      if (!Number.isNaN(parsedMs)) {
        return Math.floor(parsedMs / 1000);
      }
    }

    // lightweight-charts BusinessDay shape: { year, month, day }
    if (time && typeof time === 'object' && 'year' in time && 'month' in time && 'day' in time) {
      const y = Number((time as any).year);
      const m = Number((time as any).month);
      const d = Number((time as any).day);
      if (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)) {
        return Math.floor(Date.UTC(y, m - 1, d) / 1000);
      }
    }

    const nested = time?.timestamp ?? time?.time;
    if (typeof nested === 'number' && Number.isFinite(nested)) {
      return nested > 1e12 ? Math.floor(nested / 1000) : Math.floor(nested);
    }

    return null;
  }

  // Helper method to format ATR tooltip date
  formatATRTooltipDate(time: any): string {
    const timestamp = this.normalizeTooltipTimeToUnixSeconds(time);
    if (timestamp === null) return '--';
    return this.formatTooltipDate(timestamp);
  }

  // Helper method to calculate price change
  getPriceChange(data: any): { amount: number; percentage: number; isPositive: boolean } {
    const change = data.close - data.open;
    const percentage = (change / data.open) * 100;
    return {
      amount: change,
      percentage: percentage,
      isPositive: change >= 0
    };
  }

  // System Line Management
  toggleSystemLineType(type: SystemLine['type']): void {
    this.chartService.toggleSystemLineVisibility(type);
    // updateDrawingState will be called automatically via callback
    this.changeDetectorRef.markForCheck();
  }

  toggleSystemLineIndividual(lineId: string): void {
    this.chartService.toggleSystemLineIndividualVisibility(lineId);
    // updateDrawingState will be called automatically via callback
    this.changeDetectorRef.markForCheck();
  }

  loadSampleSystemLines(): void {
    // Sample system lines for demonstration
    const sampleLines: SystemLine[] = [
      {
        id: 'support_1',
        price: 145.50,
        color: '#4CAF50',
        lineWidth: 2,
        lineStyle: 0, // solid
        name: 'Support Level',
        type: 'SUPPORT',
        description: 'Key support level based on historical data',
        isVisible: true,
        source: 'calculation',
        metadata: {
          riskLevel: 'MEDIUM'
        }
      },
      {
        id: 'resistance_1',
        price: 155.75,
        color: '#FF9800',
        lineWidth: 2,
        lineStyle: 0, // solid
        name: 'Resistance Zone',
        type: 'RESISTANCE',
        description: 'Strong resistance level - expect selling pressure',
        isVisible: true,
        source: 'api',
        metadata: {
          riskLevel: 'LOW'
        }
      }
    ];

    this.chartService.loadSystemLines(sampleLines);
    this.changeDetectorRef.markForCheck();
  }

  clearAllSystemLines(): void {
    this.chartService.clearAllSystemLines();
    this.systemLines = [];
    if (this.activeDropdown === 'drawing') {
      this.closeMenusAfterAction();
      return;
    }
    // updateDrawingState will be called automatically via callback
    this.changeDetectorRef.markForCheck();
  }

  // Method to clear all system lines from localStorage for all symbols
  clearAllSystemLinesFromStorage(): void {
    // Clear for current symbol
    localStorage.removeItem(`system_lines_${this.currentSymbol}`);
    localStorage.removeItem(`system_line_visibility_${this.currentSymbol}`);

    // Clear for common symbols
    const commonSymbols = ['', 'GOOGL', 'MSFT', 'AMZN', 'TSLA', 'META', 'NVDA'];
    commonSymbols.forEach(symbol => {
      localStorage.removeItem(`system_lines_${symbol}`);
      localStorage.removeItem(`system_line_visibility_${symbol}`);
    });

    // Clear any remaining system line data
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('system_lines_') || key.startsWith('system_line_visibility_')) {
        localStorage.removeItem(key);
      }
    });

    console.log('All system lines cleared from localStorage');
    this.changeDetectorRef.markForCheck();
  }

  getSystemLineIcon(type: SystemLine['type']): string {
    switch (type) {
      case 'SUPPORT': return 'shield';
      case 'RESISTANCE': return 'zap';
      default: return 'bar-chart-3';
    }
  }

  getSystemLineTypeLabel(type: SystemLine['type']): string {
    switch (type) {
      case 'SUPPORT': return 'Support';
      case 'RESISTANCE': return 'Resistance';
      default: return 'System';
    }
  }

  getVisibleSystemLinesByType(type: SystemLine['type']): SystemLine[] {
    return this.systemLines.filter(line =>
      line.type === type &&
      line.isVisible &&
      this.systemLineVisibility[type]
    );
  }

  hasSystemLines(): boolean {
    return this.systemLines.length > 0;
  }

  hasVisibleSystemLines(): boolean {
    return this.systemLines.some(line =>
      line.isVisible && this.systemLineVisibility[line.type]
    );
  }

  // Helper methods for template
  get Math() { return Math; }

  refreshCalendarEvents(): void {
    console.log('Refreshing calendar events for:', this.currentSymbol);
    this.chartService.loadCalendarEvents(this.currentSymbol);
    this.changeDetectorRef.markForCheck();
  }

  private syncEventStates(): void {
    // Sync component event states with service marker visibility
    const markerVisibility = this.chartService.getMarkerVisibility();
    this.eventStates.earnings = markerVisibility.earnings;
    this.eventStates.dividend = markerVisibility.dividend;
    this.eventStates.split = markerVisibility.split;
    this.eventStates.news = markerVisibility.news;
    this.eventStates.other = markerVisibility.other;
    console.log('Synced event states:', this.eventStates);
    this.changeDetectorRef.markForCheck();
  }

  // Hide marker tooltip
  hideMarkerTooltip(): void {
    this.chartService.hideMarkerTooltip();
    this.changeDetectorRef.markForCheck();
  }

  // Change series type
  changeSeriesType(seriesType: SeriesType): void {
    this.currentSeriesType = seriesType;
    this.closeMenusAfterAction();

    // Show loader while switching chart types
    this.isLoading = true;
    this.dataReceivedForCurrentRequest = true; // We already have data, just re-rendering
    this.changeDetectorRef.markForCheck();

    // Update chart service immediately
    this.chartService.changeSeriesType(seriesType);
    console.log(`[CHART] Chart series type changed to: ${seriesType}`);

    // Force chart update
    this.changeDetectorRef.markForCheck();

    // Refresh icon after series type change
    setTimeout(() => this.refreshLucideIcons(), 50);

    // Update URL with new style (preserves embed params)
    this.navigateWithQueryParams(undefined, undefined, undefined, seriesType);

    // Save user preferences
    this.saveUserPreferences();

    // Just resize the chart to fit container, don't override time ranges
    setTimeout(() => {
      if (this.chartContainer?.nativeElement) {
        const containerElement = this.chartContainer.nativeElement;
        this.chartService.resizeChart(containerElement.clientWidth, containerElement.clientHeight);
        this.changeDetectorRef.markForCheck();
      }
    }, 300);
  }

  /** Normalize chart style string to SeriesType so Chart Style menu active state matches (e.g. Area -> area, candles -> candlestick). */
  private normalizeSeriesType(value: string | SeriesType): SeriesType {
    const s = String(value).toLowerCase().trim();
    if (s === 'candles') return 'candlestick';
    const valid: SeriesType[] = ['candlestick', 'line', 'area', 'bar', 'baseline', 'heikin-ashi'];
    return valid.includes(s as SeriesType) ? (s as SeriesType) : 'candlestick';
  }

  /** Whether the given chart type is the currently selected one (for Chart Style menu active state). */
  isActiveChartType(type: ChartType): boolean {
    const current = this.normalizeSeriesType(String(this.currentSeriesType));
    const itemValue = this.normalizeSeriesType(String(type.value));
    return current === itemValue;
  }

  // Get current series type info
  getCurrentSeriesTypeInfo(): ChartType | null {
    return this.seriesTypes.find(s => s.value === this.currentSeriesType) || this.seriesTypes[0] || null;
  }

  // Get current chart type icon (always returns a valid icon name)
  getCurrentChartTypeIcon(): string {
    // Use local icons so the toolbar keeps candlestick/line/area glyphs
    // instead of API names like "activity".
    const iconMap: Record<SeriesType, string> = {
      'candlestick': 'candlestick-chart',
      'line': 'line-chart',
      'area': 'area-chart',
      'bar': 'bar-chart-3',
      'baseline': 'trending-down',
      'heikin-ashi': 'candlestick-chart'
    };
    return iconMap[this.currentSeriesType] || 'candlestick-chart';
  }

  getChartTypeItemIcon(type: ChartType): string {
    const iconMap: Record<SeriesType, string> = {
      'candlestick': 'candlestick-chart',
      'line': 'line-chart',
      'area': 'area-chart',
      'bar': 'bar-chart-3',
      'baseline': 'trending-down',
      'heikin-ashi': 'candlestick-chart'
    };
    return iconMap[type?.value] || type?.icon || 'candlestick-chart';
  }

  // Recent Tickers functionality
  get isRecentTickersApiMode(): boolean {
    return !!this.storageService.get('token');
  }

  loadRecentTickers(): void {
    // Prefer API data when token is present; fallback to localStorage.
    const token = this.storageService.get('token');
    if (token) {
      this.loadRecentTickersFromApi();
    } else {
      this.loadRecentTickersFromLocalStorage();
    }
  }

  private loadRecentTickersFromLocalStorage(): void {
    try {
      const storedTickers = localStorage.getItem(this.RECENT_TICKERS_STORAGE_KEY);
      if (!storedTickers) {
        this.recentTickers = [];
        return;
      }
      const parsed = JSON.parse(storedTickers);
      const arr = Array.isArray(parsed) ? parsed : [];
      this.recentTickers = arr
        .filter(Boolean)
        .map((ticker: any) => ({
          ...ticker,
          symbol: (ticker?.symbol || '').toString().toUpperCase(),
          lastViewed: ticker?.lastViewed ? new Date(ticker.lastViewed) : new Date()
        }))
        .filter((t: RecentTicker) => !!t.symbol);
    } catch (e) {
      console.warn('[ChartComponent] Failed to load recent tickers from localStorage', e);
      this.recentTickers = [];
    }
  }

  private loadRecentTickersFromApi(): void {
    this.isLoadingRecentTickers = true;
    this.chartTypeService.getRecentViewTickers('true').subscribe({
      next: async (response: any) => {
        const apiTickers = this.parseRecentViewTickersResponse(response);
        // If API returns empty, show empty list (no local fallback)
        this.recentTickers = apiTickers.slice(0, this.MAX_RECENT_TICKERS);

        // Keep localStorage in sync as offline fallback (optional)
        try {
          localStorage.setItem(this.RECENT_TICKERS_STORAGE_KEY, JSON.stringify(this.recentTickers));
        } catch { /* ignore */ }

        // Enrich with quote data if API doesn't include it (only if we have tickers)
        if (this.recentTickers.length > 0) {
          await this.hydrateRecentTickersQuotes();
        }
        this.isLoadingRecentTickers = false;
        this.changeDetectorRef.markForCheck();
      },
      error: (error: any) => {
        console.warn('[ChartComponent] Error loading recent tickers from API, falling back to localStorage', error);
        this.isLoadingRecentTickers = false;
        this.loadRecentTickersFromLocalStorage();
        // Best-effort hydrate (non-blocking)
        this.hydrateRecentTickersQuotes();
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  private parseRecentViewTickersResponse(response: any): RecentTicker[] {
    const raw =
      response?.data?.recentViewTickers ??
      response?.data?.recentTickers ??
      response?.data?.tickers ??
      response?.data ??
      response?.body?.recentTickers ??
      response?.body?.tickers ??
      response?.body ??
      response?.recentTickers ??
      response?.tickers ??
      response;

    const list = Array.isArray(raw) ? raw : [];
    const result: RecentTicker[] = [];
    for (const item of list) {
      if (!item || typeof item !== 'object') continue;
      const symbol = (item?.symbol || item?.ticker || item?.code || '').toString().trim().toUpperCase();
      if (!symbol) continue;

      const lastViewedRaw = item?.viewedAt ?? item?.lastViewed ?? item?.updatedAt ?? item?.createdAt;
      const lastViewed = lastViewedRaw ? new Date(lastViewedRaw) : new Date();

      const lastPriceRaw = item?.regularMarketPrice ?? item?.lastPrice ?? item?.price ?? item?.close;
      const changeRaw = item?.regularMarketChange ?? item?.change;
      const changePercentRaw = item?.regularMarketChangePercent ?? item?.changePercent;

      result.push({
        symbol,
        displayName: (item?.displayName || item?.name || symbol).toString(),
        lastViewed,
        lastPrice: this.parseOptionalNumber(lastPriceRaw),
        change: this.parseOptionalNumber(changeRaw),
        changePercent: this.parseOptionalNumber(changePercentRaw),
        lastUpdated: item?.lastUpdated ? new Date(item.lastUpdated) : undefined
      });
    }
    return result;
  }

  private parseOptionalNumber(value: any): number | undefined {
    if (value === null || value === undefined) return undefined;
    const n = typeof value === 'number' ? value : parseFloat(String(value));
    return Number.isFinite(n) ? n : undefined;
  }

  private async hydrateRecentTickersQuotes(): Promise<void> {
    try {
      if (!this.recentTickers?.length) return;
      const symbolsNeedingQuotes = this.recentTickers
        .filter(t => !!t.symbol && (t.lastPrice === undefined || t.change === undefined || t.changePercent === undefined))
        .map(t => t.symbol);
      if (symbolsNeedingQuotes.length === 0) return;

      const quotes = await this.marketDataService.fetchMultipleQuotes(symbolsNeedingQuotes);
      if (!quotes || typeof quotes !== 'object') return;

      this.recentTickers = this.recentTickers.map(t => {
        const q = quotes[t.symbol];
        if (!q) return t;
        return {
          ...t,
          lastPrice: t.lastPrice ?? q.lastPrice,
          change: t.change ?? q.change,
          changePercent: t.changePercent ?? q.changePercent,
          lastUpdated: new Date()
        };
      });

      try {
        localStorage.setItem(this.RECENT_TICKERS_STORAGE_KEY, JSON.stringify(this.recentTickers));
      } catch { /* ignore */ }
    } catch (e) {
      // Silent: sidebar should still work without quotes
    }
  }

  addRecentTicker(ticker: RecentTicker): void {
    if (this.recentTickers.length >= this.MAX_RECENT_TICKERS) {
      this.recentTickers.pop();
    }
    this.recentTickers.unshift(ticker);
    localStorage.setItem(this.RECENT_TICKERS_STORAGE_KEY, JSON.stringify(this.recentTickers));
  }

  removeRecentTicker(ticker: RecentTicker): void {
    this.recentTickers = this.recentTickers.filter(t => t.symbol !== ticker.symbol);
    localStorage.setItem(this.RECENT_TICKERS_STORAGE_KEY, JSON.stringify(this.recentTickers));
  }

  addToRecentTickers(symbol: string): void {
    // In API mode, recents are controlled by backend.
    if (this.isRecentTickersApiMode) {
      return;
    }
    // Null safety check for symbol
    if (!symbol || typeof symbol !== 'string' || symbol.trim().length === 0) {
      console.warn('[WARNING] Invalid symbol provided to addToRecentTickers');
      return;
    }

    const trimmedSymbol = symbol.trim().toUpperCase();

    // Remove existing entry if it exists
    this.recentTickers = this.recentTickers.filter(t => t && t.symbol !== trimmedSymbol);

    // Add to the beginning
    const newTicker: RecentTicker = {
      symbol: trimmedSymbol,
      lastViewed: new Date(),
      displayName: trimmedSymbol
    };

    this.recentTickers.unshift(newTicker);

    // Keep only the most recent MAX_RECENT_TICKERS
    if (this.recentTickers.length > this.MAX_RECENT_TICKERS) {
      this.recentTickers = this.recentTickers.slice(0, this.MAX_RECENT_TICKERS);
    }

    try {
      localStorage.setItem(this.RECENT_TICKERS_STORAGE_KEY, JSON.stringify(this.recentTickers));
    } catch (error) {
      console.error('Error saving recent tickers to localStorage:', error);
    }
  }

  // New methods for TradingView-style ticker editing
  startEditingSymbol(): void {
    this.isEditingSymbol = true;
    setTimeout(() => {
      const symbolInput = document.querySelector('.ticker-input-enhanced') as HTMLInputElement;
      if (!symbolInput) {
        return;
      }
      symbolInput.focus();
      if (this.editingSymbol === this.currentSymbol) {
        symbolInput.select();
      }
    }, 0);
  }

  confirmSymbolEdit(): void {
    const typed = (this.editingSymbol || '').trim().toUpperCase();
    if (!typed) {
      this.cancelSymbolEdit();
      return;
    }

    const exact = this.searchResults.find(r => r.symbol === typed);
    const prefix = this.searchResults.find(r => r.symbol.startsWith(typed));
    const symbol = exact?.symbol || prefix?.symbol || typed;
    this.applyHeaderTickerSearch(symbol);
  }

  cancelSymbolEdit(): void {
    this.resetHeaderSearchInput(true);
  }

  clearHeaderSearchInput(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    this.isEditingSymbol = true;
    this.editingSymbol = '';
    this.showSearchResults = false;
    this.searchResults = [];
    this.searchQuery = '';
    this.isSearching = false;
    this.changeDetectorRef.detectChanges();

    setTimeout(() => {
      const symbolInput = document.querySelector('.ticker-input-enhanced') as HTMLInputElement;
      symbolInput?.focus();
      this.refreshLucideIcons();
    }, 0);
  }

  private resetHeaderSearchInput(blurInput: boolean): void {
    this.isEditingSymbol = false;
    this.editingSymbol = this.currentSymbol;
    this.showSearchResults = false;
    this.searchResults = [];
    this.searchQuery = '';
    this.isSearching = false;

    this.changeDetectorRef.detectChanges();

    if (blurInput) {
      const symbolInput = document.querySelector('.ticker-input-enhanced') as HTMLInputElement;
      symbolInput?.blur();
    }

    setTimeout(() => this.refreshLucideIcons(), 100);
  }

  // Setup debounced search for Add Symbol to Watchlist modal
  private setupWatchlistModalSearch(): void {
    this.watchlistSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (!query || query.trim().length < 2) {
          this.watchlistSymbolSearchLoading = false;
          this.watchlistSymbolSearchResults = [];
          this.changeDetectorRef.markForCheck();
          return of([]);
        }
        this.watchlistSymbolSearchLoading = true;
        this.changeDetectorRef.markForCheck();
        return from(this.marketDataService.searchSymbols(query)).pipe(
          map((results: any[]) => results.map((r: any) => ({
            symbol: r.symbol,
            name: r.shortname,
            exchange: r.exchange || r.index
          }))),
          catchError(() => of([]))
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (results) => {
        this.watchlistSymbolSearchLoading = false;
        this.watchlistSymbolSearchResults = results;
        this.changeDetectorRef.markForCheck();
      },
      error: () => {
        this.watchlistSymbolSearchLoading = false;
        this.watchlistSymbolSearchResults = [];
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  // Setup debounced search
  private setupDebouncedSearch(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (query.length < 2) {
          this.isSearching = false;
          this.showSearchResults = false;
          this.searchResults = [];
          this.changeDetectorRef.detectChanges();
          return of([]);
        }

        this.isSearching = true;
        this.showSearchResults = true;
        this.changeDetectorRef.detectChanges();
        return from(this.searchTickersWithFallback(query)).pipe(
          catchError((error) => {
            console.error('Search error:', error);
            return of([]);
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (results) => {
        this.isSearching = false;
        this.searchResults = (results || []).map((result: any) => ({
          symbol: (result?.symbol || result?.ticker || result?.code || '').toString().trim().toUpperCase(),
          name: result.name || result.shortname || result.longname || result.displayName,
          exchange: result.exchange || result.exchangeName || result.fullExchangeName || result.exchangeDisplay || result.index,
          assetType: (result.asset || result.quoteType || result.type || result.assetType || '').toString().trim().toLowerCase() || undefined,
          lastPrice: this.parseOptionalNumber(result.regularMarketPrice ?? result.lastPrice ?? result.price ?? result.close),
          change: this.parseOptionalNumber(result.regularMarketChange ?? result.change),
          changePercent: this.parseOptionalNumber(result.regularMarketChangePercent ?? result.changePercent),
          marketState: (result.marketState || result.quoteState || result.status || '').toString().trim() || undefined,
          isActivelyTrading: typeof result.isActivelyTrading === 'boolean' ? result.isActivelyTrading : undefined
        })).filter(result => !!result.symbol);
        this.showSearchResults = this.searchQuery.trim().length >= 2;
        console.log(`[SEARCH] Mapped search results:`, this.searchResults);
        this.changeDetectorRef.detectChanges();
      },
      error: (error) => {
        console.error('Search error:', error);
        this.isSearching = false;
        this.searchResults = [];
        this.showSearchResults = this.searchQuery.trim().length >= 2;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  private async searchTickersWithFallback(query: string): Promise<any[]> {
    const primary = await this.marketDataService.searchTickersFromMF(query);
    if (Array.isArray(primary) && primary.length > 0) {
      return primary;
    }
    return this.marketDataService.searchSymbols(query);
  }

  // Search functionality
  onSearchInput(event: Event): void {
    // Null safety check for event
    if (!event || !event.target) {
      console.warn('[WARNING] Invalid event provided to onSearchInput');
      return;
    }

    const target = event.target as HTMLInputElement;
    if (!target || typeof target.value !== 'string') {
      console.warn('[WARNING] Invalid input element in onSearchInput');
      return;
    }

    const query = target.value.trim();
    this.searchQuery = query;
    this.searchSubject.next(query);

    // Clear immediately for short queries; API search waits on debounceTime(300).
    if (query.length < 2) {
      this.searchResults = [];
      this.showSearchResults = false;
      this.isSearching = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  selectSearchResult(result: {
    symbol: string;
    name?: string;
    exchange?: string;
    assetType?: string;
    lastPrice?: number;
    change?: number;
    changePercent?: number;
    marketState?: string;
    isActivelyTrading?: boolean;
  }): void {
    // Null safety check for result
    if (!result || typeof result !== 'object') {
      console.warn('[WARNING] Invalid search result provided to selectSearchResult');
      return;
    }

    if (!result.symbol || typeof result.symbol !== 'string' || result.symbol.trim().length === 0) {
      console.warn('[WARNING] Invalid symbol in search result');
      return;
    }

    this.applyHeaderTickerSearch(result.symbol.trim().toUpperCase());
  }

  private applyHeaderTickerSearch(symbol: string): void {
    const next = (symbol || '').trim().toUpperCase();
    if (!next) {
      return;
    }

    this.searchResults = [];
    this.showSearchResults = false;
    this.isSearching = false;
    this.isEditingSymbol = false;
    this.editingSymbol = next;
    this.changeDetectorRef.detectChanges();

    // Embedded / iframe: switch chart in place. Normal standalone: open fullscreen tab.
    if (this.isFullscreenView || this.isEmbeddedView || this.isRunningInParentFrame()) {
      if (next === this.currentSymbol) {
        return;
      }
      this.navigateWithQueryParams(next);
      return;
    }

    this.openTickerInFullscreen(next);
  }

  hideSearchResults(): void {
    // Clear search state
    this.showSearchResults = false;
    this.searchResults = [];
    this.searchQuery = '';
    this.isSearching = false;

    this.changeDetectorRef.detectChanges();
    setTimeout(() => this.refreshLucideIcons(), 100);
  }





  // Method to toggle recent tickers pane
  toggleRecentTickersPane(): void {
    // Close all other menus before opening sidebar
    this.closeAllMenus();

    // In embedded mode, ensure we're on watchlists tab when opening
    if (this.isEmbeddedView) {
      this.activeSidebarTab = 'watchlists';
    }

    this.isRecentTickersPaneOpen = !this.isRecentTickersPaneOpen;
    this.changeDetectorRef.markForCheck();

    // Resize chart after sidebar animation completes
    setTimeout(() => {
      if (this.chartContainer?.nativeElement) {
        const containerElement = this.chartContainer.nativeElement;
        this.chartService.resizeChart(containerElement.clientWidth, containerElement.clientHeight);
      }
    }, 300); // Match the CSS transition duration
  }

  selectRecentTicker(ticker: RecentTicker): void {
    const symbol = (ticker?.symbol || '').toString().trim().toUpperCase();
    if (!symbol) {
      return;
    }
    this.openTickerInFullscreen(symbol, undefined, true, 'recent');
  }

  clearRecentTickers(): void {
    // In API mode, don't clear locally.
    if (this.isRecentTickersApiMode) {
      return;
    }
    this.recentTickers = [];
    localStorage.removeItem(this.RECENT_TICKERS_STORAGE_KEY);
  }

  // Watchlist methods
  private initializeWatchlists(): void {
    // Subscribe to watchlists changes
    this.watchlistService.watchlists$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(watchlists => {
      this.watchlists = watchlists;
      this.changeDetectorRef.markForCheck();
    });

    // Subscribe to selected watchlist changes
    this.watchlistService.selectedWatchlist$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(selectedId => {
      this.selectedWatchlistId = selectedId;
      this.changeDetectorRef.markForCheck();
    });

    // Fetch watchlists from API only if token is available
    // In embedded mode, token will be loaded from query params and API watchlists will be loaded then
    const token = this.storageService.get('token');
    if (token) {
      this.loadApiWatchlists();
    } else if (!this.isEmbeddedView) {
      // In non-embedded mode, try to load anyway (token might be in localStorage from previous session)
      this.loadApiWatchlists();
    }
  }

  // Load watchlists from API
  loadApiWatchlists(): void {
    // Check if token is available before making API call
    const token = this.storageService.get('token');
    if (!token) {
      console.warn('[ChartComponent] No auth token available, skipping API watchlist load');
      this.isLoadingApiWatchlists = false;
      this.apiWatchlists = [];
      this.apiWatchlistSymbols.clear();
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.isLoadingApiWatchlists = true;
    this.watchlistService.getAllWatchList('1', 'true').subscribe({
      next: (response: any) => {
        this.isLoadingApiWatchlists = false;
        // Handle API response - adjust based on your API response structure
        if (response && response.data) {
          this.apiWatchlists = Array.isArray(response.data) ? response.data : [];
        } else if (Array.isArray(response)) {
          this.apiWatchlists = response;
        } else {
          this.apiWatchlists = [];
        }

        // Build set of all symbols in API watchlists for quick lookup
        this.apiWatchlistSymbols.clear();
        this.apiWatchlists.forEach((watchlist: any) => {
          if (watchlist.symbols && Array.isArray(watchlist.symbols)) {
            watchlist.symbols.forEach((symbol: any) => {
              // Handle both string symbols and symbol objects
              const symbolStr = typeof symbol === 'string' ? symbol : (symbol.symbol || symbol.ticker || symbol);
              if (symbolStr) {
                this.apiWatchlistSymbols.add(symbolStr.toUpperCase());
              }
            });
          }
          // Also check for items array (alternative structure)
          if (watchlist.items && Array.isArray(watchlist.items)) {
            watchlist.items.forEach((item: any) => {
              const symbolStr = item.symbol || item.ticker || item;
              if (symbolStr) {
                this.apiWatchlistSymbols.add(symbolStr.toUpperCase());
              }
            });
          }
        });

        // Map API watchlists to standard Watchlist array so they display in the sidebar properly
        if (this.apiWatchlists && this.apiWatchlists.length > 0) {
          const mappedWatchlists: Watchlist[] = this.apiWatchlists.map(w => {
            const arr = w.symbols || w.items || [];
            return {
              id: w._id || w.id,
              name: w.name || w.title || 'Watchlist',
              description: w.description || '',
              items: Array.isArray(arr) ? arr.map((s: any) => {
                const symbolStr = typeof s === 'string' ? s : (s.symbol || s.ticker || s);
                return {
                  symbol: symbolStr,
                  addedDate: new Date(),
                  displayName: typeof s === 'object' && s.name ? s.name : symbolStr
                };
              }) : [],
              createdDate: w.createdAt ? new Date(w.createdAt) : new Date(),
              lastModified: w.updatedAt ? new Date(w.updatedAt) : new Date(),
              isDefault: w.isDefault || false
            };
          });

          this.watchlists = mappedWatchlists;
          if (!this.selectedWatchlistId || !this.watchlists.find(w => w.id === this.selectedWatchlistId)) {
            this.selectedWatchlistId = this.watchlists[0]?.id || null;
          }
        }

        this.changeDetectorRef.markForCheck();
        // Load symbols (with price/change/%) for the selected watchlist from watchlist API only; no MBOUM
        if (this.selectedWatchlistId) {
          this.loadWatchlistSymbolsFromApi(this.selectedWatchlistId);
        }

        // Refresh icons after API watchlists are loaded
        setTimeout(() => {
          if (typeof (window as any).lucide !== 'undefined') {
            (window as any).lucide.createIcons();
          }
        }, 100);
      },
      error: (error) => {
        this.isLoadingApiWatchlists = false;
        console.error('Error loading API watchlists:', error);
        this.apiWatchlists = [];
        this.apiWatchlistSymbols.clear();
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  switchSidebarTab(tab: SidebarTab): void {
    // Don't allow switching to recent tab in embedded mode
    if (this.isEmbeddedView && tab === 'recent') {
      return;
    }
    this.activeSidebarTab = tab;
    this.changeDetectorRef.markForCheck();
    // Refresh icons after tab change
    setTimeout(() => this.refreshLucideIcons(), 50);
  }

  selectWatchlist(watchlistId: string): void {
    if (!watchlistId) return;
    // Keep service in sync (used for local watchlists mode)
    this.watchlistService.setSelectedWatchlist(watchlistId);
    // Update local state so dropdown shows selection as active
    this.selectedWatchlistId = watchlistId;
    this.changeDetectorRef.markForCheck();

    const token = this.storageService.get('token');
    if (token) {
      // Fetch symbols for this watchlist from API and then hydrate quotes
      this.loadWatchlistSymbolsFromApi(watchlistId);
    } else {
      this.hydrateSelectedWatchlistQuotes();
      // Refresh Lucide icons so delete icons appear after switching watchlist
      setTimeout(() => this.refreshLucideIcons(), 150);
    }
  }

  private loadWatchlistSymbolsFromApi(watchlistId: string): void {
    this.isLoadingWatchlistSymbols = true;
    this.changeDetectorRef.markForCheck();

    this.watchlistService.getAllSymbolList(watchlistId).subscribe({
      next: (response: any) => {
        const items = this.parseWatchlistSymbolsResponse(response);
        this.watchlists = (this.watchlists || []).map(w => {
          if (w.id !== watchlistId) return w;
          return { ...w, items };
        });
        this.isLoadingWatchlistSymbols = false;
        this.changeDetectorRef.markForCheck();
        // Refresh Lucide icons so delete (trash) icons appear in the new list
        setTimeout(() => this.refreshLucideIcons(), 100);
      },
      error: (err) => {
        console.warn('[ChartComponent] Error loading watchlist symbols, using existing items', err);
        this.isLoadingWatchlistSymbols = false;
        this.changeDetectorRef.markForCheck();
        setTimeout(() => this.refreshLucideIcons(), 100);
      }
    });
  }

  private parseWatchlistSymbolsResponse(response: any): WatchlistItem[] {
    // Prefer stockInfo[] (full quote data: symbol, regularMarketPrice, regularMarketChange, regularMarketChangePercent, displayName)
    const stockInfo = response?.stockInfo ?? response?.data?.stockInfo ?? response?.body?.stockInfo;
    if (Array.isArray(stockInfo) && stockInfo.length > 0) {
      const result: WatchlistItem[] = [];
      for (const item of stockInfo) {
        const symbol = (item?.symbol ?? item?.ticker ?? item?.code ?? '').toString().trim().toUpperCase();
        if (!symbol) continue;
        const lastPrice = this.parseOptionalNumber(item?.regularMarketPrice ?? item?.lastPrice ?? item?.price ?? item?.close);
        const change = this.parseOptionalNumber(item?.regularMarketChange ?? item?.change);
        const changePercent = this.parseOptionalNumber(item?.regularMarketChangePercent ?? item?.changePercent);
        result.push({
          symbol,
          displayName: (item?.displayName ?? item?.shortName ?? item?.longName ?? item?.name ?? symbol).toString(),
          addedDate: new Date(),
          lastPrice,
          change,
          changePercent,
          lastUpdated: (lastPrice !== undefined || change !== undefined) ? new Date() : undefined
        });
      }
      return result;
    }

    // Fallback: data.symbols (string[]) or data array
    const raw =
      response?.data?.symbols ??
      response?.data?.symbolList ??
      response?.data ??
      response?.body?.symbols ??
      response?.body?.symbolList ??
      response?.body ??
      response?.symbols ??
      response?.symbolList ??
      response;
    const list = Array.isArray(raw) ? raw : [];
    const fallbackResult: WatchlistItem[] = [];
    for (const item of list) {
      const symbol = (item?.symbol ?? item?.ticker ?? item?.code ?? (typeof item === 'string' ? item : '')).toString().trim().toUpperCase();
      if (!symbol) continue;
      const lastPrice = this.parseOptionalNumber(item?.regularMarketPrice ?? item?.lastPrice ?? item?.price ?? item?.close);
      const change = this.parseOptionalNumber(item?.regularMarketChange ?? item?.change);
      const changePercent = this.parseOptionalNumber(item?.regularMarketChangePercent ?? item?.changePercent);
      fallbackResult.push({
        symbol,
        displayName: (item?.displayName ?? item?.name ?? item?.shortName ?? symbol).toString(),
        addedDate: item?.addedDate ? new Date(item.addedDate) : new Date(),
        notes: item?.notes,
        lastPrice,
        change,
        changePercent,
        lastUpdated: lastPrice !== undefined || change !== undefined ? new Date() : undefined
      });
    }
    return fallbackResult;
  }

  private async hydrateSelectedWatchlistQuotes(): Promise<void> {
    try {
      const selected = this.getSelectedWatchlist();
      if (!selected?.items?.length) return;

      const symbols = selected.items
        .map(i => (i?.symbol || '').toString().trim().toUpperCase())
        .filter(Boolean);
      if (symbols.length === 0) return;

      const quotes = await this.marketDataService.fetchMultipleQuotes(symbols);
      if (!quotes || typeof quotes !== 'object') return;

      // Update watchlists immutably so OnPush templates refresh reliably
      this.watchlists = (this.watchlists || []).map(w => {
        if (!w || w.id !== selected.id) return w;
        const nextItems = (w.items || []).map(item => {
          const sym = (item?.symbol || '').toString().trim().toUpperCase();
          const q = quotes[sym];
          if (!q) return item;
          return {
            ...item,
            symbol: sym,
            lastPrice: q.lastPrice,
            change: q.change,
            changePercent: q.changePercent,
            lastUpdated: new Date()
          } as WatchlistItem;
        });
        return { ...w, items: nextItems };
      });

      this.changeDetectorRef.markForCheck();
      setTimeout(() => this.refreshLucideIcons(), 100);
    } catch {
      // Silent: watchlist should still render symbols without quotes
    }
  }

  getSelectedWatchlist(): Watchlist | null {
    if (this.storageService.get('token') && this.watchlists.length > 0) {
      if (!this.selectedWatchlistId) return this.watchlists[0] || null;
      return this.watchlists.find(w => w.id === this.selectedWatchlistId) || this.watchlists[0] || null;
    }
    return this.watchlistService.getSelectedWatchlist();
  }

  selectWatchlistTicker(item: WatchlistItem): void {
    const symbol = (item?.symbol || '').toString().trim().toUpperCase();
    if (!symbol) {
      return;
    }
    this.openTickerInFullscreen(symbol, undefined, true, 'watchlists');
  }

  addCurrentSymbolToWatchlist(): void {
    if (!this.currentSymbol || typeof this.currentSymbol !== 'string' || this.currentSymbol.trim().length === 0) {
      console.warn('[WARNING] Invalid current symbol for adding to watchlist');
      return;
    }

    const symbol = this.currentSymbol.trim();
    const displayName = this.tickerDetails?.companyName || symbol;
    const selectedWatchlist = this.getSelectedWatchlist();
    if (!selectedWatchlist) return;

    if (this.storageService.get('token')) {
      const watchlistData = {
        name: selectedWatchlist.name,
        symbol: symbol,
        description: selectedWatchlist.description || '',
        watchListIds: [selectedWatchlist.id],
        watchListCount: 1
      };

      this.watchlistService.createWatchList(watchlistData).subscribe({
        next: () => {
          this.loadApiWatchlists();
        },
        error: (err) => {
          console.error('Error adding to API watchlist:', err);
        }
      });
    } else {
      this.watchlistService.addToSelectedWatchlist(symbol, displayName);
    }
  }

  openAddWatchlistSymbolModal(): void {
    const selectedWatchlist = this.getSelectedWatchlist();
    if (!selectedWatchlist) {
      console.warn('[WARNING] No watchlist selected for adding symbol');
      return;
    }

    this.watchlistSymbolSearchQuery = this.currentSymbol || '';
    this.watchlistSymbolSearchResults = [];
    this.watchlistSymbolsSelected = [];
    if (this.watchlistSymbolSearchQuery.trim().length >= 2) {
      this.watchlistSearchSubject.next(this.watchlistSymbolSearchQuery.trim());
    }
    this.showAddWatchlistSymbolModal = true;
    this.changeDetectorRef.markForCheck();
  }

  closeAddWatchlistSymbolModal(): void {
    this.showAddWatchlistSymbolModal = false;
    this.watchlistSymbolSearchQuery = '';
    this.watchlistSymbolSearchResults = [];
    this.watchlistSymbolsSelected = [];
    this.addCurrentTickerToWatchlist = false;
    this.changeDetectorRef.markForCheck();
  }

  onWatchlistSearchInput(): void {
    const q = (this.watchlistSymbolSearchQuery || '').trim();
    if (q.length < 2) {
      this.watchlistSymbolSearchResults = [];
      this.changeDetectorRef.markForCheck();
      return;
    }
    this.watchlistSearchSubject.next(q);
  }

  isWatchlistSymbolSelected(symbol: string): boolean {
    return this.watchlistSymbolsSelected.some(s => s.symbol.toUpperCase() === symbol.toUpperCase());
  }

  toggleWatchlistSymbolSelection(item: { symbol: string; name?: string }): void {
    const sym = (item.symbol || '').trim().toUpperCase();
    if (!sym) return;
    const idx = this.watchlistSymbolsSelected.findIndex(s => s.symbol.toUpperCase() === sym);
    if (idx >= 0) {
      this.watchlistSymbolsSelected = this.watchlistSymbolsSelected.filter((_, i) => i !== idx);
    } else {
      this.watchlistSymbolsSelected = [...this.watchlistSymbolsSelected, { symbol: sym, name: item.name }];
    }
    this.changeDetectorRef.markForCheck();
  }

  removeWatchlistSelectedSymbol(symbol: string): void {
    const sym = (symbol || '').trim().toUpperCase();
    this.watchlistSymbolsSelected = this.watchlistSymbolsSelected.filter(s => s.symbol.toUpperCase() !== sym);
    this.changeDetectorRef.markForCheck();
  }

  getAddWatchlistSymbolCount(): number {
    let count = this.watchlistSymbolsSelected.length;
    if (this.addCurrentTickerToWatchlist && this.currentSymbol?.trim()) {
      const cur = this.currentSymbol.trim().toUpperCase();
      if (!this.watchlistSymbolsSelected.some(s => s.symbol.toUpperCase() === cur)) count += 1;
    }
    return count;
  }

  // Add all selected symbols (and optionally current chart ticker) to the currently selected watchlist
  confirmAddWatchlistSymbol(): void {
    const selectedWatchlist = this.getSelectedWatchlist();
    if (!selectedWatchlist) return;

    const symbolsToAdd: { symbol: string; name?: string }[] = [...this.watchlistSymbolsSelected];
    if (this.addCurrentTickerToWatchlist && this.currentSymbol?.trim()) {
      const cur = this.currentSymbol.trim().toUpperCase();
      const displayName = this.tickerDetails?.companyName || cur;
      if (!symbolsToAdd.some(s => s.symbol.toUpperCase() === cur)) {
        symbolsToAdd.unshift({ symbol: cur, name: displayName });
      }
    }

    if (symbolsToAdd.length === 0) return;

    const hasToken = !!this.storageService.get('token');

    if (hasToken) {
      const addCalls = symbolsToAdd.map(item =>
        this.watchlistService.addSymbolToWatchList(selectedWatchlist.id, item.symbol).pipe(
          map(() => true),
          catchError(() => of(false))
        )
      );
      forkJoin(addCalls).subscribe({
        next: () => {
          this.loadApiWatchlists();
          this.closeAddWatchlistSymbolModal();
        },
        error: (err) => {
          console.error('Error adding symbols to watchlist:', err);
          this.loadApiWatchlists();
          this.closeAddWatchlistSymbolModal();
        }
      });
    } else {
      symbolsToAdd.forEach(item =>
        this.watchlistService.addToSelectedWatchlist(item.symbol, item.name || item.symbol)
      );
      this.closeAddWatchlistSymbolModal();
    }
  }

  removeFromWatchlist(item: WatchlistItem): void {
    const selectedWatchlist = this.getSelectedWatchlist();
    if (!selectedWatchlist) return;

    if (this.storageService.get('token')) {
      this.watchlistService.removeSymbolFromWatchList(selectedWatchlist.id, item.symbol).subscribe({
        next: () => {
          this.loadApiWatchlists();
        },
        error: (err) => {
          console.error('Error removing from API watchlist:', err);
        }
      });
    } else {
      this.watchlistService.removeFromWatchlist(selectedWatchlist.id, item.symbol);
    }
  }

  isCurrentSymbolInWatchlist(): boolean {
    // Check both local watchlists and API watchlists
    const inLocalWatchlist = this.watchlistService.isInWatchlist(this.currentSymbol);
    const inApiWatchlist = this.currentSymbol ? this.apiWatchlistSymbols.has(this.currentSymbol.toUpperCase()) : false;
    return inLocalWatchlist || inApiWatchlist;
  }

  createNewWatchlist(name: string): void {
    this.watchlistService.createWatchlist(name);
  }

  // Quick add watchlist methods
  watchlistContainsSymbol(watchlist: Watchlist, symbol: string): boolean {
    // Check local watchlist
    const inLocalWatchlist = watchlist.items.some(item => item.symbol === symbol);
    if (inLocalWatchlist) return true;

    // Check API watchlists
    const symbolUpper = symbol.toUpperCase();
    return this.apiWatchlistSymbols.has(symbolUpper);
  }

  // Check if symbol is in any API watchlist
  isSymbolInApiWatchlist(symbol: string): boolean {
    if (!symbol) return false;
    return this.apiWatchlistSymbols.has(symbol.toUpperCase());
  }

  // Check if symbol is in a specific API watchlist
  isSymbolInSpecificApiWatchlist(watchlist: any, symbol: string): boolean {
    if (!symbol || !watchlist) return false;
    const symbolUpper = symbol.toUpperCase();

    // Check symbols array
    if (watchlist.symbols && Array.isArray(watchlist.symbols)) {
      return watchlist.symbols.some((s: any) => {
        const symbolStr = typeof s === 'string' ? s : (s.symbol || s.ticker || s);
        return symbolStr && symbolStr.toUpperCase() === symbolUpper;
      });
    }

    // Check items array
    if (watchlist.items && Array.isArray(watchlist.items)) {
      return watchlist.items.some((item: any) => {
        const symbolStr = item.symbol || item.ticker || item;
        return symbolStr && symbolStr.toUpperCase() === symbolUpper;
      });
    }

    return false;
  }

  toggleSymbolInWatchlist(watchlist: Watchlist, symbol: string): void {
    if (this.watchlistContainsSymbol(watchlist, symbol)) {
      this.watchlistService.removeFromWatchlist(watchlist.id, symbol);
    } else {
      this.watchlistService.addToWatchlist(watchlist.id, {
        symbol,
        displayName: this.tickerDetails?.companyName || symbol
      });
    }

    // Close dropdown after action
    this.showWatchlistSelector = false;
  }

  // Toggle symbol in API watchlist (add/remove via API)
  toggleSymbolInApiWatchlist(watchlist: any, symbol: string): void {
    if (!symbol || !watchlist) return;

    const isInWatchlist = this.isSymbolInSpecificApiWatchlist(watchlist, symbol);
    const watchlistId = watchlist._id || watchlist.id;

    if (isInWatchlist) {
      // Remove from watchlist - using removeSymbolFromWatchList API with watchlist ID
      this.watchlistService.removeSymbolFromWatchList(watchlistId, symbol).subscribe({
        next: (response) => {
          console.log(`Removed ${symbol} from watchlist`, response);
          // Update local state immediately for better UX
          const symbolUpper = symbol.toUpperCase();
          this.apiWatchlistSymbols.delete(symbolUpper);
          // Reload API watchlists to reflect changes
          this.loadApiWatchlists();
          this.changeDetectorRef.markForCheck();
        },
        error: (error) => {
          console.error(`Error removing ${symbol} from watchlist:`, error);
          // Refresh icons on error
          setTimeout(() => {
            if (typeof (window as any).lucide !== 'undefined') {
              (window as any).lucide.createIcons();
            }
          }, 100);
        }
      });
    } else {
      // Add to watchlist - pass full object structure
      const watchlistData = {
        name: watchlist.name || watchlist.title || '',
        symbol: symbol,
        description: watchlist.description || '',
        watchListIds: [watchlistId], // Array containing the watchlist ID
        watchListCount: 1
      };

      this.watchlistService.createWatchList(watchlistData).subscribe({
        next: (response) => {
          console.log(`Added ${symbol} to watchlist ${watchlistId}`, response);
          // Update local state immediately for better UX
          const symbolUpper = symbol.toUpperCase();
          this.apiWatchlistSymbols.add(symbolUpper);
          // Reload API watchlists to reflect changes
          this.loadApiWatchlists();
          this.changeDetectorRef.markForCheck();
        },
        error: (error) => {
          console.error(`Error adding ${symbol} to watchlist ${watchlistId}:`, error);
          console.error('Error details:', {
            status: error?.status,
            statusText: error?.statusText,
            message: error?.message,
            url: error?.url,
            watchlistId: watchlistId,
            symbol: symbol,
            watchlistData: watchlistData
          });
          // Refresh icons on error
          setTimeout(() => {
            if (typeof (window as any).lucide !== 'undefined') {
              (window as any).lucide.createIcons();
            }
          }, 100);
        }
      });
    }

    // Close dropdown after action
    this.showWatchlistSelector = false;
  }

  // Create watchlist modal methods
  createWatchlistAndAdd(): void {
    if (!this.newWatchlistName?.trim()) return;

    const watchlistData: any = {
      name: this.newWatchlistName.trim(),
      description: this.newWatchlistDescription?.trim() || ''
    };

    // Call API to create watchlist
    this.watchlistService.createWatchList(watchlistData).subscribe({
      next: (response: any) => {
        console.log('Watchlist created successfully:', response);
        // Reload API watchlists to get the new one
        this.loadApiWatchlists();
        // Switch to watchlists tab
        this.activeSidebarTab = 'watchlists';
        // Close modal and reset form
        this.cancelCreateWatchlist();
        this.changeDetectorRef.markForCheck();
      },
      error: (error) => {
        console.error('Error creating watchlist:', error);
        // Close modal and reset form even on error
        this.cancelCreateWatchlist();
      }
    });
  }

  cancelCreateWatchlist(): void {
    this.showCreateWatchlist = false;
    this.showWatchlistSelector = false;
    this.newWatchlistName = '';
    this.newWatchlistDescription = '';
    this.addCurrentSymbolToNew = true;
  }

  // Watchlist management methods
  openEditWatchlistModal(): void {
    const currentWatchlist = this.getSelectedWatchlist();
    if (!currentWatchlist) return;

    this.editWatchlistId = currentWatchlist.id;
    this.editWatchlistName = currentWatchlist.name || '';
    this.editWatchlistDescription = (currentWatchlist as any).description ?? '';
    this.showEditWatchlistModal = true;
    this.showWatchlistMenu = false;
    this.changeDetectorRef.markForCheck();
  }

  closeEditWatchlistModal(): void {
    this.showEditWatchlistModal = false;
    this.editWatchlistId = null;
    this.editWatchlistName = '';
    this.editWatchlistDescription = '';
    this.changeDetectorRef.markForCheck();
  }

  saveEditWatchlist(): void {
    const name = this.editWatchlistName?.trim();
    if (!name) return;
    if (!this.editWatchlistId) return;

    const description = this.editWatchlistDescription?.trim() ?? '';

    if (this.storageService.get('token')) {
      this.watchlistService.updateWatchList({
        watchListId: this.editWatchlistId,
        name,
        description
      }).subscribe({
        next: () => {
          this.loadApiWatchlists();
          this.closeEditWatchlistModal();
        },
        error: (err) => console.error('Error updating watchlist:', err)
      });
    } else {
      this.watchlistService.updateWatchlist(this.editWatchlistId, {
        name,
        description
      });
      this.closeEditWatchlistModal();
    }
  }

  deleteCurrentWatchlist(): void {
    const currentWatchlist = this.getSelectedWatchlist();
    if (!currentWatchlist || currentWatchlist.isDefault) return;

    const confirmed = confirm(`Are you sure you want to delete "${currentWatchlist.name}"? This cannot be undone.`);
    if (confirmed) {
      if (this.storageService.get('token')) {
        this.watchlistService.removeFromWatchList(currentWatchlist.id).subscribe({
          next: () => {
            this.selectedWatchlistId = null;
            this.loadApiWatchlists();
          },
          error: (err) => console.error('Error deleting watchlist:', err)
        });
      } else {
        this.watchlistService.deleteWatchlist(currentWatchlist.id);
      }
    }
    this.showWatchlistMenu = false;
  }

  clearCurrentWatchlist(): void {
    const currentWatchlist = this.getSelectedWatchlist();
    if (!currentWatchlist) return;

    const confirmed = confirm(`Are you sure you want to remove all tickers from "${currentWatchlist.name}"?`);
    if (confirmed) {
      if (this.storageService.get('token')) {
        // Clearing is complex via API as we need to remove each symbol
        currentWatchlist.items.forEach(item => {
          this.watchlistService.removeSymbolFromWatchList(currentWatchlist.id, item.symbol).subscribe();
        });
        setTimeout(() => this.loadApiWatchlists(), 500);
      } else {
        this.watchlistService.updateWatchlist(currentWatchlist.id, {
          items: []
        });
      }
    }
    this.showWatchlistMenu = false;
  }

  getRelativeTime(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return new Date(date).toLocaleDateString();
  }


  // Get dataset information for display
  getDatasetInfo(): string {
    const info = this.chartService.getDatasetInfo();
    if (info.dataPoints === 0) {
      return 'No data loaded';
    }

    const startDate = info.startDate?.toLocaleDateString();
    const endDate = info.endDate?.toLocaleDateString();

    return `${info.dataPoints} data points (${startDate} - ${endDate})`;
  }

  // Helper method to check if any tools are active
  hasActiveTools(): boolean {
    return this.isDrawingMode || this.isTextMode || this.isPenMode || this.isArrowMode ||
      (this.showLineToolbar && this.selectedLine !== null) ||
      (this.showTextToolbar && this.selectedText !== null) ||
      (this.showPenToolbar && this.selectedPenDrawing !== null) ||
      (this.showArrowToolbar && this.selectedArrowDrawing !== null);
  }

  // Method to deselect current line
  deselectLine(): void {
    this.showLineToolbar = false;
    this.selectedLine = null;
    this.activeToolbarOption = null;
    this.chartService.deselectLine();
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }

  // Smart positioning to keep line toolbar on screen (responsive, container-relative)
  private positionToolbarSafely(x: number, y: number): void {
    const toolbarWidth = 220;
    const toolbarHeight = 52;
    const padding = 12;
    const edgePadding = 8;

    const container = this.chartContainer?.nativeElement;
    const containerWidth = container?.clientWidth ?? 400;
    const containerHeight = container?.clientHeight ?? 300;

    let finalX = x + padding;
    let finalY = y - toolbarHeight - padding;

    if (finalX + toolbarWidth > containerWidth - edgePadding) {
      finalX = x - toolbarWidth - padding;
    }
    if (finalX < edgePadding) {
      finalX = edgePadding;
    }
    if (finalY < edgePadding) {
      finalY = y + padding;
    }
    if (finalY + toolbarHeight > containerHeight - edgePadding) {
      finalY = containerHeight - toolbarHeight - edgePadding;
    }

    this.toolbarX = Math.max(0, Math.min(finalX, containerWidth - toolbarWidth - edgePadding));
    this.toolbarY = Math.max(0, Math.min(finalY, containerHeight - toolbarHeight - edgePadding));
  }

  // Smart positioning for text toolbar
  private positionTextToolbarSafely(x: number, y: number): void {
    const toolbarWidth = 250; // Text toolbar is a bit wider
    const toolbarHeight = 50;
    const padding = 10;

    // Get chart container dimensions
    const container = this.chartContainer?.nativeElement;
    if (!container) {
      this.textToolbarX = x + padding;
      this.textToolbarY = y - padding;
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    // Calculate optimal position
    let finalX = x + padding;
    let finalY = y - toolbarHeight - padding;

    // Adjust X position if toolbar would go off the right edge
    if (finalX + toolbarWidth > containerWidth) {
      finalX = x - toolbarWidth - padding;
    }

    // Adjust X position if toolbar would go off the left edge
    if (finalX < 0) {
      finalX = padding;
    }

    // Adjust Y position if toolbar would go off the top edge
    if (finalY < 0) {
      finalY = y + padding;
    }

    // Adjust Y position if toolbar would go off the bottom edge
    if (finalY + toolbarHeight > containerHeight) {
      finalY = containerHeight - toolbarHeight - padding;
    }

    this.textToolbarX = Math.max(0, finalX);
    this.textToolbarY = Math.max(0, finalY);
  }

  // Pen drawing management methods
  deselectPenDrawing(): void {
    this.showPenToolbar = false;
    this.selectedPenDrawing = null;
    this.activePenToolbarOption = null;
    this.chartService.deselectPenDrawing();
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }

  togglePenToolbarOption(option: string): void {
    // Close all other menus before opening pen toolbar option
    this.closeAllMenus();

    this.activePenToolbarOption = this.activePenToolbarOption === option ? null : option;
    this.changeDetectorRef.markForCheck();
    // Refresh icons after dropdown state changes
    this.refreshLucideIcons();
  }

  updatePenDrawingColor(color: string): void {
    if (!this.selectedPenDrawing) return;
    this.chartService.updatePenDrawing(this.selectedPenDrawing.id, { color: color });
    this.deselectPenDrawing();
  }

  updatePenDrawingLineWidth(width: number): void {
    if (!this.selectedPenDrawing) return;
    this.chartService.updatePenDrawing(this.selectedPenDrawing.id, { lineWidth: width });
    this.deselectPenDrawing();
  }

  deletePenDrawingFromContext(): void {
    if (!this.selectedPenDrawing) return;
    this.chartService.removePenDrawing(this.selectedPenDrawing.id);
    this.showPenToolbar = false;
    this.selectedPenDrawing = null;
    this.changeDetectorRef.markForCheck();
  }

  // Smart positioning for pen toolbar
  private positionPenToolbarSafely(x: number, y: number): void {
    const toolbarWidth = 200;
    const toolbarHeight = 40;
    const padding = 10;

    // Get chart container dimensions
    const container = this.chartContainer?.nativeElement;
    if (!container) {
      this.penToolbarX = x + padding;
      this.penToolbarY = y - padding;
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    // Calculate optimal position
    let finalX = x + padding;
    let finalY = y - toolbarHeight - padding;

    // Adjust X position if toolbar would go off the right edge
    if (finalX + toolbarWidth > containerWidth) {
      finalX = x - toolbarWidth - padding;
    }

    // Ensure toolbar doesn't go off the left edge
    if (finalX < 0) {
      finalX = padding;
    }

    // Adjust Y position if toolbar would go off the top edge
    if (finalY < 0) {
      finalY = y + padding;
    }

    // Adjust Y position if toolbar would go off the bottom edge
    if (finalY + toolbarHeight > containerHeight) {
      finalY = containerHeight - toolbarHeight - padding;
    }

    this.penToolbarX = Math.max(0, finalX);
    this.penToolbarY = Math.max(0, finalY);
  }

  // Smart positioning for arrow toolbar
  private positionArrowToolbarSafely(x: number, y: number): void {
    const toolbarWidth = 200;
    const toolbarHeight = 40;
    const padding = 10;

    const container = this.chartContainer?.nativeElement;
    if (!container) {
      this.arrowToolbarX = x + padding;
      this.arrowToolbarY = y - padding;
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    let finalX = x + padding;
    let finalY = y - toolbarHeight - padding;

    if (finalX + toolbarWidth > containerWidth) {
      finalX = x - toolbarWidth - padding;
    }
    if (finalX < 0) {
      finalX = padding;
    }
    if (finalY < 0) {
      finalY = y + padding;
    }
    if (finalY + toolbarHeight > containerHeight) {
      finalY = containerHeight - toolbarHeight - padding;
    }

    this.arrowToolbarX = Math.max(0, finalX);
    this.arrowToolbarY = Math.max(0, finalY);
  }

  private positionATRTooltipSafely(x: number, y: number): { x: number; y: number } {
    // Position tooltip relative to chart container with smart boundary detection
    const padding = 10;
    const tooltipWidth = 200;
    const tooltipHeight = 80;

    // Use approximate chart dimensions (tooltip is now inside chart container)
    const chartWidth = 1000; // Approximate chart width
    const chartHeight = 600; // Approximate chart height

    let safeX = x + padding;
    let safeY = y - tooltipHeight - padding;

    // Keep within chart container bounds
    if (safeX + tooltipWidth > chartWidth) {
      safeX = x - tooltipWidth - padding; // Position to the left
    }
    if (safeX < padding) {
      safeX = padding;
    }

    if (safeY < padding) {
      safeY = y + padding + 20; // Position below cursor
    }
    if (safeY + tooltipHeight > chartHeight) {
      safeY = chartHeight - tooltipHeight - padding;
    }

    return { x: safeX, y: safeY };
  }



  // Format price for display
  getPreMarketTimeDisplay(): string {
    const raw = (this.tickerDetails?.preMarketTime ?? this.tickerDetails?.lastUpdated) as unknown;
    const date = this.parseDateLikeValue(raw);
    if (!date) {
      return '--';
    }
    return date.toLocaleTimeString('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  }

  getAtCloseDisplay(): string {
    const raw = (this.tickerDetails?.regularMarketTime ?? this.tickerDetails?.lastUpdated) as unknown;
    const date = this.parseDateLikeValue(raw);
    if (!date) {
      return '--';
    }
    return this.formatDateWithGmtOffset(date);
  }

  getPreMarketPriceDisplay(): string {
    const value = this.tickerDetails?.preMarketPrice ?? this.tickerDetails?.close;
    return value === undefined || value === null
      ? '--'
      : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  }

  getPreMarketChangeDisplay(): string {
    const value = this.tickerDetails?.preMarketChange ?? this.tickerDetails?.change;
    return value === undefined || value === null ? '--' : this.formatChange(value);
  }

  getPreMarketChangePercentDisplay(): string {
    const value = this.tickerDetails?.preMarketChangePercent ?? this.tickerDetails?.changePercent;
    return value === undefined || value === null ? '--' : `(${this.formatChangePercent(value)})`;
  }

  isPreMarketChangePositive(): boolean {
    const value = this.tickerDetails?.preMarketChange ?? this.tickerDetails?.change;
    return value !== undefined && value !== null && value >= 0;
  }

  isPreMarketChangeNegative(): boolean {
    const value = this.tickerDetails?.preMarketChange ?? this.tickerDetails?.change;
    return value !== undefined && value !== null && value < 0;
  }

  isPreMarketChangePercentPositive(): boolean {
    const value = this.tickerDetails?.preMarketChangePercent ?? this.tickerDetails?.changePercent;
    return value !== undefined && value !== null && value >= 0;
  }

  isPreMarketChangePercentNegative(): boolean {
    const value = this.tickerDetails?.preMarketChangePercent ?? this.tickerDetails?.changePercent;
    return value !== undefined && value !== null && value < 0;
  }

  private parseDateLikeValue(raw: unknown): Date | null {
    if (raw === undefined || raw === null) {
      return null;
    }
    let date: Date;
    if (raw instanceof Date) {
      date = raw;
    } else if (typeof raw === 'number') {
      const ms = raw > 1e12 ? raw : raw * 1000;
      date = new Date(ms);
    } else {
      date = new Date(String(raw));
    }
    return isNaN(date.getTime()) ? null : date;
  }

  private formatDateWithGmtOffset(date: Date): string {
    const targetTimezone = environment.defaultTimezone || 'UTC';
    const formatted = date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: targetTimezone
    });
    const [monthDay = '--', time = '--'] = formatted.split(', ');
    return `${monthDay} at ${time}`;
  }

  formatPrice(price: number | undefined): string {
    if (price === undefined || price === null) return '--';
    return price.toFixed(2);
  }

  // Format change for display
  formatChange(change: number | undefined): string {
    if (change === undefined || change === null) return '--';
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(2)}`;
  }

  // Format change percent for display
  formatChangePercent(changePercent: number | undefined): string {
    if (changePercent === undefined || changePercent === null) return '--';
    const sign = changePercent >= 0 ? '+' : '';
    return `${sign}${changePercent.toFixed(2)}%`;
  }

  // Get CSS class for change styling
  getChangeClass(change: number | undefined): string {
    if (change === undefined || change === null) return '';
    return change >= 0 ? 'positive' : 'negative';
  }


} 